import {
   addEmptyProductToProductList,
   findObjectByKeyValue,
   IsForeignCurrencyQuote,
   isAlwaysUSD,
   getAccountSnowflakeQtyItem,
   hasAccountSnowflakeQtyItem
} from './helpers.mjs'
import { calculateMonthlyTotalForActivation } from './calculateMonthlyTotalForActivation.mjs'
import { calculateAnnualTotal, calculatePayrollAnnualTotal } from './calculateAnnualTotal.mjs'
import { isProrated, getProductProratedPrice, applyProrationToMonthlyPrice } from './prorationFunctions.mjs'
import { calculateDynamicProducts } from './calculateDynamicProducts.mjs'
import { calculateOngoingMonthlyCost } from './calculateOngoingMonthlyCost.mjs'

// This file contains the calculation engine for the quote application.
// It is used both by the front end, and the backend /api/get-calculated-values API.
// Calculation Engine Structure:
//  - Classes
//     These classes are used to define MonthlyPrice and OneTimePrice objects. All monthly and one time charges are defined by these.
//     For combined/summarized onetime or monthly charges (total monthly, or total implementation, for example), no productCode is specified.
//    -- MonthlyPrice()
//    -- OneTimePrice()
//  - Main Calculation Function (calculateQuote)
//    -- Shared Variables/Definitions
//        This sections defines common variables, definitions, or overrides that are required for other calculations to be performed.
//    -- Discount States
//        This section contains a mapping of discount form the pricebook to the state of the discount in the discount being passed into the function.
//        For a discount to be added to the engine, it must be added to both the pricebook, and the inputs of calculateQuote() first.
//    -- Monthly Products
//        Each product in this section is a MonthlyPrice() object, calculated from prices in the pricebook. Core monthly charges,
//        Payroll monthly charges, HR Addons (ben admin, time tracking, etc), and all other monthly single product charges exist here.
//    -- One Time Products
//        All onetime charges( Implementation, one time expansion charges, etc) are calculated in this section.
//        All single product one time charges shoud go here.
//    -- Summarized Monthly Charges
//        When monthly charges need combined into a single MonthlyPrice(), use this section.
//    -- Summarized One Time Charges
//        When one time charges need combined into a single OneTimePrice(), use this section.
//    -- Other Calculated Values
//        Any other values that need calculated, but do not fit in the above section are found here.
//        For example, estimated annual charges, a list of product codes, etc.
//    -- Final Values and Functions Applied to All Values
//        This takes all calculated values in the previous sections, compiles them to a single object, and applies any final apply all functions to them. Setting USD values, etc.
//  - Calculation Helper Functions
//     These functions are used to help calculate individual product prices. Examples include a monthly price calculationf for monthly products,
//     formulas for volume discounts, Implementation pricing formulas, functions to determine banded or (base + per EE) pricing, etc.

/**
 * Monthly Price Class
 *
 * Represents the calculated monthly cost for a product including all applicable
 * discounts and provides both list prices and final totals.
 *
 * @class MonthlyPrice
 * @example
 * // Example MonthlyPrice object for Core Product with Non Profit discount
 * {
 *   "productCode": "core1",
 *   "productEECount": 10,
 *   "listPrice": 250,
 *   "listPEPMCost": 25,
 *   "volumeDiscountedPEPMPercent": 0,
 *   "bundleDiscountedPEPMPercent": 0,
 *   "discountedPEPMCost": 21.25,
 *   "totalPEPMDiscount": 3.75,
 *   "totalDiscountPercent": 15,
 *   "totalDiscount": 37.5,
 *   "totalPrice": 212.5,
 *   "MRRTotal": 240
 * }
 */
export class MonthlyPrice {
   constructor(
      productCode = null,
      productEECount = 0,
      listPrice = 0,
      listPEPMCost = 0,
      volumeDiscountedPEPMPercent = 0,
      bundleDiscountedPEPMPercent = 0,
      discountedPEPMCost = 0,
      totalPEPMDiscount = 0,
      totalDiscountPercent = 0,
      totalDiscount = 0,
      totalPrice = 0,
      listMRRTotal = 0,
      pdfDisplayTotal = 0,         // Used for PDF display represents the total price
      pdfDisplayDifferenceTotal = 0, // Used for PDF display represents the difference price
      pdfDisplayDiscountedPEPMCost = 0, // Used for PDF display represents the PEPM discounted cost
      pdfDisplayTotalDiscountPercent = 0, // Used for PDF display represents the total discount percent
      pdfDisplayTotalDiscount = 0, // Used for PDF display represents the total discount
      MRRTotal = 0,
      previousMRRTotal = 0,
      previousTotalPrice = 0,
      previousTotalPriceUnconverted = 0,
      totalPriceDifference = 0,
      appliedDiscounts = [],
      proratedTotal = 0,
      listPriceUSD = 0,
      totalPriceUSD = 0,
      totalDiscountUSD = 0,
      MRRTotalUSD = 0,
      proratedPrice = 0,
      SFProduct2Id = null,
      SFPricebookEntryID = null,
   ) {
      this.productCode = productCode // Product code, if any. Only single product prices should have product codes, not summarized prices of multiple products.
      this.productEECount = productEECount // Count of employees with this monthly product
      this.listPrice = listPrice // Total monthly list price. Either the monthly minimum charge if there is one, or the number of employees * the the base cost per employee
      this.listPEPMCost = listPEPMCost // List Per Employee Per Month cost. This is the base charge per employee
      this.volumeDiscountedPEPMPercent = volumeDiscountedPEPMPercent // The percentage discount off PEPM if a volume discount is present
      this.bundleDiscountedPEPMPercent = bundleDiscountedPEPMPercent // The percentage discount off PEPM if a bundle discount is present and applicable
      this.discountedPEPMCost = discountedPEPMCost // The total cost PEPM after all discounts
      this.totalPEPMDiscount = totalPEPMDiscount // Difference between list PEPM and discounted PEPM in dollars
      this.totalDiscountPercent = totalDiscountPercent // Difference between list and total price of entire monthly charge in percent form
      this.totalDiscount = totalDiscount // Difference between list and total price of entire monthly charge in dollars
      this.totalPrice = totalPrice // Total price in dollars
      this.appliedDiscounts = appliedDiscounts // An array of objects contianing discounts applied.
      this.listMRRTotal = listMRRTotal // Internal MRR used by Rev Ops for calculation commisionable MRR for reps for the quote. Certain discounts are excluded. Unlike MRRTotal, when differenceQuote is true, this number is not altered.
      this.pdfDisplayTotal = pdfDisplayTotal // Total price that will be displayed in the PDF. This is the total price after all discounts that have the flag 'ApplyToPDFDisplayedValue' set to true.
      this.pdfDisplayDifferenceTotal = pdfDisplayDifferenceTotal // Total price that will be displayed in the PDF. This is the total price after all discounts that have the flag 'ApplyToPDFDisplayedValue' set to true.
      this.pdfDisplayDiscountedPEPMCost = pdfDisplayDiscountedPEPMCost // Discounted PEPM cost that will be displayed in the PDF. This is the discounted PEPM cost after all discounts that have the flag 'ApplyToPDFDisplayedValue' set to true.
      this.pdfDisplayTotalDiscountPercent = pdfDisplayTotalDiscountPercent // Total discount percent that will be displayed in the PDF. This is the total discount percent after all discounts that have the flag 'ApplyToPDFDisplayedValue' set to true.
      this.pdfDisplayTotalDiscount = pdfDisplayTotalDiscount // Total discount that will be displayed in the PDF. This is the total discount after all discounts that have the flag 'ApplyToPDFDisplayedValue' set to true.
      this.MRRTotal = MRRTotal // Internal MRR used by Rev Ops for calculation commisionable MRR for reps for the quote. Certain discounts are excluded. When differenceQuote is true, this number become the 'upsell' difference between the new commissionable MRR and the existing commissionable MRR for the account.
      this.previousMRRTotal = previousMRRTotal // Internal MRR used by Rev Ops for calculation commisionable MRR for reps, but based on actual numbers from existing products a customer pays for. This number is like totalPrice, but with certain discounts excluded.
      this.previousTotalPrice = previousTotalPrice // The total price a customer currently pays for the product
      this.previousTotalPriceUnconverted = previousTotalPriceUnconverted // The total price a customer currently pays for the product, but not converted to the quote currency
      this.totalPriceDifference = totalPriceDifference // The difference between totalPrice and previousTotalPrice
      this.proratedTotal = proratedTotal // ProratedTotal for the quote app (Due at activation)
      this.proratedPrice = proratedPrice // ProratedPrice for the quote app (Due at activation)

      // The fields below will ALWAYS be in USD, regardless of the currency of the quote. These numbers are only used for sending values to Salesforce.
      // Fields will be set right before the calculation engine returns to ensure that all discounts and other math is performed correctly.
      // These amounts will be based on the current conversion rate back from foreign currency to USD provided by finance, not the pricebook converison rate used for agent commissions (MRR).
      this.listPriceUSD = listPriceUSD
      this.totalPriceUSD = totalPriceUSD
      this.totalDiscountUSD = totalDiscountUSD
      // This amount will always use the pricebook converison rate used for agent commissions (MRR).
      this.MRRTotalUSD = MRRTotalUSD
      this.SFProduct2Id = SFProduct2Id
      this.SFPricebookEntryID = SFPricebookEntryID

      // Example of the MonthlyPrice object for the Core Product, with a Non Profit discount.
      //   {
      //     "productEECount": 10,
      //     "listPrice": 250,
      //     "listPEPMCost": 10,
      //     "volumeDiscountedPEPMPercent": 0,
      //     "bundleDiscountedPEPMPercent": 0,
      //     "discountedPEPMCost": 21.25,
      //     "totalPEPMDiscount": 3.75,
      //     "totalDiscountPercent": 15,
      //     "totalDiscount": 37.5,
      //     "totalPrice": 212.5,
      //     "appliedDiscounts": [
      //         {
      //             "Id": 14,
      //             "BookId": 200,
      //             "DiscId": 100,
      //             "ApplyTo": "core1,core2,core3,benAdmin,performanceMan,timeTracking",
      //             "Priority": 10,
      //             "CreatedAt": "2023-10-17 19:36:13+00:00",
      //             "UpdatedAt": "2023-11-14 16:43:44+00:00",
      //             "BillingFreq": "monthly",
      //             "DiscountCode": "nonProfit",
      //             "DiscountName": "Non Profit",
      //             "DiscountType": "percent",
      //             "RoleRequired": null,
      //             "ProductFamily": null,
      //             "DiscountAmount": 15,
      //             "actualEffect": {
      //                 "previousTotalPrice": 250,
      //                 "newTotalPrice": 212.5,
      //                 "previousDiscountedPEPM": 25,
      //                 "newDiscountedPEPM": 21.25,
      //                 "changeInDollars": 37.5,
      //                 "changeInPercent": 0.15
      //             }
      //         }
      //     ]
      //    "MRRTotal": 240
      // }
   }
   deepCopy() {
      return new MonthlyPrice(
         this.productCode,
         this.productEECount,
         this.listPrice,
         this.listPEPMCost,
         this.volumeDiscountedPEPMPercent,
         this.bundleDiscountedPEPMPercent,
         this.discountedPEPMCost,
         this.totalPEPMDiscount,
         this.totalDiscountPercent,
         this.totalDiscount,
         this.totalPrice,
         this.pdfDisplayTotal,
         this.pdfDisplayDifferenceTotal,
         this.pdfDisplayDiscountedPEPMCost,
         this.pdfDisplayTotalDiscountPercent,
         this.pdfDisplayTotalDiscount,
         this.MRRTotal,
         this.previousMRRTotal,
         this.previousTotalPrice,
         this.previousTotalPriceUnconverted,
         this.totalPriceDifference,
         JSON.parse(JSON.stringify(this.appliedDiscounts)),
         this.proratedTotal,
         this.listPriceUSD,
         this.totalPriceUSD,
         this.totalDiscountUSD,
         this.MRRTotalUSD,
         this.proratedPrice,
         this.SFProduct2Id,
         this.SFPricebookEntryID,
      )
   }
}

/**
 * One Time Price Class
 *
 * Represents one-time pricing for implementation costs, setup fees, and other non-recurring charges.
 * Contains pricing breakdown including list prices, discounts, and final totals for one-time costs.
 *
 * @class OneTimePrice
 * @example
 * // Example OneTimePrice object for implementation costs
 * {
 *   "productCode": "implementation",
 *   "productEECount": 50,
 *   "listPrice": 2500.00,
 *   "totalDiscount": 250.00,
 *   "totalDiscountPercent": 10,
 *   "totalPrice": 2250.00,
 *   "appliedDiscounts": []
 * }
 */
export class OneTimePrice {
   constructor(
      productCode = null,
      productEECount = 0,
      listPrice = 0,
      totalDiscount = 0,
      totalDiscountPercent = 0,
      totalPrice = 0,
      pdfDisplayTotal = 0,
      listPriceUSD = 0,
      totalPriceUSD = 0,
      totalDiscountUSD = 0,
      appliedDiscounts = [],
      SFProduct2Id = null,
      SFPricebookEntryID = null,
      extraCountryCost = 0,
      totalPriceWithoutExtraCountryCost = 0,
   ) {
      this.productCode = productCode // Product code, if any. Only single product prices should have product codes, not summarized prices of multiple products.
      this.productEECount = productEECount // Total EE count, if applicable
      this.listPrice = listPrice // Total one time list price
      this.totalDiscount = totalDiscount // Difference between list and total price of entire one time charge in dollars
      this.totalDiscountPercent = totalDiscountPercent // Difference between list and total price of entire one time charge in percent form
      this.totalPrice = totalPrice // Total price in dollars
      this.pdfDisplayTotal = pdfDisplayTotal // Total price that will be displayed in the PDF. This is the total price after all discounts that have the flag 'ApplyToPDFDisplayedValue' set to true.
      this.appliedDiscounts = appliedDiscounts // An array of objects contianing discounts applied.

      // The fields below will ALWAYS be in USD, regardless of the currency of the quote. These numbers are only used for sending values to Salesforce.
      // Fields will be set right before the calculation engine returns to ensure that all discounts and other math is performed correctly.
      // These amounts will be based on the current conversion rate back from foreign currency to USD provided by finance, not the pricebook converison rate used for agent commissions (MRR).
      this.listPriceUSD = listPriceUSD
      this.totalPriceUSD = totalPriceUSD
      this.totalDiscountUSD = totalDiscountUSD
      this.SFProduct2Id = SFProduct2Id
      this.SFPricebookEntryID = SFPricebookEntryID
      this.extraCountryCost = extraCountryCost
      this.totalPriceWithoutExtraCountryCost = totalPriceWithoutExtraCountryCost
   }
   deepCopy() {
      return new OneTimePrice(
         this.productCode,
         this.productEECount,
         this.listPrice,
         this.totalDiscount,
         this.totalDiscountPercent,
         this.totalPrice,
         this.pdfDisplayTotal,
         this.listPriceUSD,
         this.totalPriceUSD,
         this.totalDiscountUSD,
         this.SFProduct2Id,
         this.SFPricebookEntryID,
         this.extraCountryCost,
         this.totalPriceWithoutExtraCountryCost,
      )
   }
}

/**
 * Calculates the quote based on products, discounts, and multiple configuration parameters.
 *
 * @param {Record<string, Product>} product_list - The list of products from the pricebook.
 * @param {Record<string, Discount>} discount_list - The available discounts keyed by discount code.
 * @param {number | string} book_id - The book identifier.
 * @param {string} quoteType - The type of quote (e.g., 'monthly' or 'onetime').
 * @param {string} coreProductSelected - The selected core product key.
 * @param {number} productCoreEECount - Count of core employees.
 * @param {number} productPerformanceEECount - Count of performance employees.
 * @param {number} productTimeTrackingEECount - Count of time tracking employees.
 * @param {boolean} hasTimeClocks - Flag indicating if time clocks are present.
 * @param {any[]} productTimeClockDetails - Array of time clock details.
 * @param {number} productBenefitsAdministrationEECount - Count of benefits administration employees.
 * @param {number} productPayrollEINCount - Count of payroll EINs.
 * @param {number} productPayrollEECount - Total payroll employee count.
 * @param {number[]} productPayrollEECounts - Array of payroll employee counts per EIN.
 * @param {number} productPayrollTotalStateCount - Total payroll state count.
 * @param {number[]} productPayrollStateCounts - Array of payroll state counts per EIN.
 * @param {any[]} professionalServiceDetails - Details for professional services.
 * @param {any[]} consultingServiceDetails - Details for consulting services.
 * @param {string} impType - HR implementation type.
 * @param {string} benAdminImpType - Benefits administration implementation type.
 * @param {string} payrollImpType - Payroll implementation type.
 * @param {number} discountBundle - Bundle discount amount.
 * @param {number} discountNonProfit - Non-profit discount amount.
 * @param {number} prepayLength - Prepayment length in months (1 = month-to-month, 12 = 1 year, etc.).
 * @param {number} discountMultiEntity - Multi-entity discount amount.
 * @param {number} discountHRMonthlyPromotion - HR monthly promotion discount amount.
 * @param {number} discountHRDealDeskMonthly - HR deal desk monthly discount amount.
 * @param {number} discountReseller - Reseller discount amount.
 * @param {number} discountQuickstartTraining - Quickstart training discount amount.
 * @param {number} discountImpPromotion - Implementation promotion discount amount.
 * @param {number} discountDealDeskHRImp - Deal desk HR implementation discount amount.
 * @param {number} discountDealDeskPayrollImp - Deal desk payroll implementation discount amount.
 * @param {number} discountSecondEIN - Second EIN discount amount.
 * @param {number} discountGroupImp - Group implementation discount amount.
 * @param {number} discountDealDeskHRImpRate - Deal desk HR implementation rate discount.
 * @param {number} discountDealDeskQuickstartImpRate - Deal desk quickstart implementation rate discount.
 * @param {number} discountDealDeskPayrollImpRate - Deal desk payroll implementation rate discount.
 * @param {number} productAdditionalStorageCount - Number of additional storage units.
 * @param {number} productAdditionalJobsCount - Number of additional job postings.
 * @param {boolean} productHistoricalDataImport - Flag for historical data import selection.
 * @param {boolean} productMarketplaceListing - Flag for marketplace listing selection.
 * @param {boolean} productSponsorship - Flag for sponsorship selection.
 * @param {number} productPaidServiceAmount - Amount for paid services.
 * @param {boolean} productTestAccount - Flag indicating if a test account is selected.
 * @param {string} activationDate - Activation date (ISO string).
 * @param {boolean} differenceQuote - Flag indicating if this is a difference quote.
 * @param {Record<string, any>} accountSnowflake - Object containing account snowflake details.
 * @param {number} coreCountryCount - Count of core countries.
 * @param {Record<string, any>} promotionalDiscounts - Mapping of promotional discount codes to values.
 * @param {string} nextInvoiceDate - Next invoice date (ISO string).
 * @param {Record<string, any>} currencyObject - Currency conversion details.
 * @param {number} accountBalance - The account balance amount.
 * @param {number} monthlyTax - The monthly tax amount.
 * @param {EINIntegrationSelection[][]} productEINIntegrations - Array of EIN integration details.
 * @param {any} allPermissions - Object representing all permissions.
 * @param {string} user_role - The user role.
 * @param {string} account_status - The account status.
 * @param {Record<string, any>} pricebookDetails - Details from the pricebook.
 * @param {Record<string, any>} previousCurrencyObject - Previous currency conversion details.
 * @param {boolean} prepaymentActivationUseDifference - Flag for prepayment activation difference usage.
 * @param {Map<string, number | boolean>} dynamicProducts - Products to calculate not part of the hardcoded set.
 * @param {string} [selectedTimeProduct='timeTracking'] - The selected time product ('timeTracking' or 'timeAttendance').
 * @param {boolean} [isATSOnly=false] - Flag indicating the quote is an ATS-only quote. Included in the cache key and returned in calculatedValues.
 * @param {boolean} [isRecalculation=false] - Flag indicating if this is a recalculation; when true the result is not cached.
 * @returns {import("./types/calculatedValues").CalculatedValues} An object containing all calculated quote values.
 * @deprecated discountPrepaymentCode parameter is deprecated. Use prepayLength instead.
 */
const quoteCache = new Map()

export default function calculateQuote(
   // Required inputs to perform a calculation.
   product_list,
   discount_list,
   book_id,
   quoteType,
   coreProductSelected,
   productCoreEECount,
   productPerformanceEECount,
   productTimeTrackingEECount,
   hasTimeClocks,
   productTimeClockDetails,
   productBenefitsAdministrationEECount,
   productPayrollEINCount,
   productPayrollEECount,
   productPayrollEECounts,
   productPayrollTotalStateCount,
   productPayrollStateCounts,
   professionalServiceDetails,
   consultingServiceDetails,
   impType,
   benAdminImpType,
   payrollImpType,
   discountBundle,
   discountNonProfit,
   prepayLength, // Prepayment length in months (1 = month-to-month). Replaces deprecated discountPrepaymentCode.
   discountMultiEntity,
   discountHRMonthlyPromotion,
   discountHRDealDeskMonthly,
   discountReseller,
   discountQuickstartTraining,
   discountImpPromotion,
   discountDealDeskHRImp,
   discountDealDeskPayrollImp,
   discountSecondEIN,
   discountGroupImp,
   discountDealDeskHRImpRate,
   discountDealDeskQuickstartImpRate,
   discountDealDeskPayrollImpRate,
   productAdditionalStorageCount,
   productAdditionalJobsCount,
   productHistoricalDataImport,
   productMarketplaceListing,
   productSponsorship,
   productPaidServiceAmount,
   productTestAccount,
   activationDate, // if not supplied, defaults to 1st day of month
   differenceQuote,
   accountSnowflake,
   coreCountryCount,
   promotionalDiscounts,
   nextInvoiceDate,
   currencyObject,
   accountBalance,
   monthlyTax,
   productEINIntegrations,
   allPermissions,
   user_role,
   account_status,
   pricebookDetails,
   previousCurrencyObject,
   productPayrollEINImpTypes,
   prepaymentActivationUseDifference,
   dynamicProducts,
   selectedTimeProduct = 'timeTracking',
   isATSOnly = false,
   isRecalculation = false,
) {
   // Normalize dynamicProducts to a Map. It may arrive as an array of [key, value]
   // tuples (e.g. cached/serialized state) or a plain object, in which case
   // dynamicProducts.entries / .get are undefined and downstream calls would throw.
   if (!(dynamicProducts instanceof Map)) {
      if (Array.isArray(dynamicProducts)) {
         dynamicProducts = new Map(dynamicProducts)
      } else if (dynamicProducts && typeof dynamicProducts === 'object') {
         dynamicProducts = new Map(Object.entries(dynamicProducts))
      } else {
         dynamicProducts = new Map()
      }
   }

   // Create cache key based on all parameters except isRecalculation
   const cacheKey = JSON.stringify({
      product_list,
      discount_list,
      book_id,
      quoteType,
      coreProductSelected,
      productCoreEECount,
      productPerformanceEECount,
      productTimeTrackingEECount,
      hasTimeClocks,
      productTimeClockDetails,
      productBenefitsAdministrationEECount,
      productPayrollEINCount,
      productPayrollEECount,
      productPayrollEECounts,
      productPayrollTotalStateCount,
      productPayrollStateCounts,
      professionalServiceDetails,
      consultingServiceDetails,
      impType,
      benAdminImpType,
      payrollImpType,
      discountBundle,
      discountNonProfit,
      prepayLength,
      discountMultiEntity,
      discountHRMonthlyPromotion,
      discountHRDealDeskMonthly,
      discountReseller,
      discountQuickstartTraining,
      discountImpPromotion,
      discountDealDeskHRImp,
      discountDealDeskPayrollImp,
      discountSecondEIN,
      discountGroupImp,
      discountDealDeskHRImpRate,
      discountDealDeskQuickstartImpRate,
      discountDealDeskPayrollImpRate,
      productAdditionalStorageCount,
      productAdditionalJobsCount,
      productHistoricalDataImport,
      productMarketplaceListing,
      productSponsorship,
      productPaidServiceAmount,
      productTestAccount,
      activationDate,
      differenceQuote,
      accountSnowflake,
      coreCountryCount,
      promotionalDiscounts,
      nextInvoiceDate,
      currencyObject,
      accountBalance,
      monthlyTax,
      productEINIntegrations,
      allPermissions,
      user_role,
      account_status,
      pricebookDetails,
      previousCurrencyObject,
      productPayrollEINImpTypes,
      prepaymentActivationUseDifference,
      dynamicProducts: Array.from(dynamicProducts?.entries() ?? []), // Convert Map to array for caching
      selectedTimeProduct,
      isATSOnly,
      isRecalculation,
   })

   // Check cache first
   if (quoteCache.has(cacheKey)) {
      return quoteCache.get(cacheKey)
   }
   if (
      arguments.length < calculateQuote.length ||
      arguments.length > calculateQuote.length + 3
   ) {
      console.error(
         'Invalid number of arguments for calcuating values! Expected:',
         calculateQuote.length,
         'Recieved:',
         arguments.length,
      )
      return `Invalid number of arguments for calcuating values! Expected:${calculateQuote.length} Recieved:${arguments.length}`
   }
   // Determine if volume discount should be capped at 50% based on feature permission
   const limitVolumeDiscount = allPermissions?.['LimitVolumeDiscount']?.includes(user_role) ?? false

   /********************************/
   // Shared Variables/Definitions //
   /********************************/
   // This sections defines common variables, definitions, or overrides that are required for other calculations to be performed.

   // sums all payroll ee and state counts if using multiple EINs

   /*******************/
   // Discount States //
   /*******************/
   // This section is used to keeping track of all discounts
   // Every discount should have a an option here keyed by 'discountCode':'reactDiscountStateVariable'
   // 'reactDiscountStateVariable' is a boolean marked true or false if the discount is enabled.
   // Discount overrides are also listed

   // Keyed discounts used to check if the discount is true or false, and should be applied later to individual products.
   // Note: Prepayment discounts are now handled as promotional discounts with DiscountFamily = 'Prepayment Discount'
   // and are filtered by PrepayLengthMin/Max constraints based on the prepayLength parameter.
   let discountStates = {
      bundleDiscount: discountBundle,
      nonProfit: discountNonProfit,
      relatedEntity: discountMultiEntity,
      hrMonthlyPromotion: discountHRMonthlyPromotion,
      hrDealdeskMonthly: discountHRDealDeskMonthly,
      reseller: discountReseller && discountReseller > 0,
      quickstartDiscount: discountQuickstartTraining,
      impPromotion: discountImpPromotion,
      hrDealdesk: discountDealDeskHRImp,
      payDealdesk: discountDealDeskPayrollImp,
      secondEINDiscount: discountSecondEIN,
      groupChildDiscount: discountGroupImp,
   }

   // Map Promotional discounts to discountStates field
   for (let discountKey in promotionalDiscounts) {
      discountStates[discountKey] = promotionalDiscounts[discountKey]
   }

   // Sets new discount rates if dealdesk values have been set. These dealdesk values are set in the DB if approved in SF.
   // The new discount value is a hard override on the discount value being pulled from the discount DB.
   if (discountDealDeskHRImpRate != null && discountDealDeskHRImpRate != 0) {
      if ('hrDealdesk' in discount_list)
         discount_list['hrDealdesk']['DiscountAmount'] =
            discountDealDeskHRImpRate
      else
         console.log(
            'error: DealDesk Imp Discount Amount provided, but hrDealdesk not in discount list',
         )
   }
   if (
      discountDealDeskQuickstartImpRate != null &&
      discountDealDeskQuickstartImpRate != 0
   ) {
      if ('quickstartDiscount' in discount_list) {
         discount_list['quickstartDiscount']['DiscountType'] = 'percent'
         discount_list['quickstartDiscount']['DiscountAmount'] =
            discountDealDeskQuickstartImpRate
      } else
         console.log(
            'error: DealDesk Quickstart Discount Amount provided, but quickstartDiscount not in discount list',
         )
   }
   if (
      discountDealDeskPayrollImpRate != null &&
      discountDealDeskPayrollImpRate != 0
   ) {
      if ('payDealdesk' in discount_list)
         discount_list['payDealdesk']['DiscountAmount'] =
            discountDealDeskPayrollImpRate
      else
         console.log(
            'error: DealDesk Payroll Imp Discount Amount provided, but payDealdesk not in discount list',
         )
   }
   // Sets new discount rate for resellers
   if (discountReseller != null) {
      if ('reseller' in discount_list)
         discount_list['reseller']['DiscountAmount'] = discountReseller
      else
         console.log(
            'error: Reseller Discount Amount provided, but reseller not in discount list',
         )
   }

   /********************/
   // Monthly Products //
   /********************/
   // Each product is a monthly recurring product. All products should be instantiated by calling 'new MonthlyPrice(productCodeName)';
   // All monthly products should be given a 'productCode' property.

   // Most monthly products follow this pattern to calculate their price:
   // First calculates the monthly product total with just the volume discount.
   // Second, applies all applicable discounts that are enabled.
   // Third, rounds all float properties in the object to a given precision.

   //////////
   // Core //

   //Check if core products are in the pricebook
   addEmptyProductToProductList(product_list, 'core0')
   addEmptyProductToProductList(product_list, 'core1')
   addEmptyProductToProductList(product_list, 'core2')
   addEmptyProductToProductList(product_list, 'core3')

   // Core Base PEPM Price
   let productCoreBasePEPMPrice = 0
   if (
      product_list &&
      coreProductSelected &&
      product_list.hasOwnProperty(coreProductSelected) &&
      product_list[coreProductSelected].hasOwnProperty('EmpListPrice') &&
      product_list[coreProductSelected]['EmpListPrice']
   )
      productCoreBasePEPMPrice =
         product_list[coreProductSelected]['EmpListPrice']
   else if (!product_list) console.error('ERROR: product_list is null')
   else if (!coreProductSelected)
      console.error('ERROR: coreProductSelected is null')
   else if (coreProductSelected !== 'core0') {
      if (!product_list.hasOwnProperty(coreProductSelected))
         console.error('ERROR: Core Product not found in product_list')
      else if (
         !product_list[coreProductSelected].hasOwnProperty('EmpListPrice')
      )
         console.error(
            'ERROR: EmpListPrice not found in product_list[coreProductSelected]',
         )
      else if (!product_list[coreProductSelected]['EmpListPrice'])
         console.error(
            'ERROR: EmpListPrice is null in product_list[coreProductSelected]',
         )
      else console.error('ERROR: Calculate.mjs Unknown error')
   }
   // Core Minimum Charge
   let productCoreMinimumCharge = 0
   let productCoreMinimumEECount = 0
   // Core Monthly Costs
   let productCoreMonthlyCosts = new MonthlyPrice()
   if (
      product_list &&
      coreProductSelected &&
      product_list.hasOwnProperty(coreProductSelected) &&
      product_list[coreProductSelected].hasOwnProperty('MinimumListPrice') &&
      product_list[coreProductSelected]['MinimumListPrice']
   ) {
      // Minimum Charge
      productCoreMinimumCharge =
         product_list[coreProductSelected]['MinimumListPrice']
      // Minimum Employee Count
      productCoreMinimumEECount =
            product_list[coreProductSelected]['MinimumEmployees']
      if (
         coreProductSelected &&
         coreProductSelected !== 'core0' &&
         quoteType !== 'onetime'
      ) {
         productCoreMonthlyCosts = calculateMonthlyProductCosts(
            productCoreEECount,
            productCoreBasePEPMPrice,
            volumeDiscountedPEPM(
               product_list[coreProductSelected],
               productCoreEECount,
               productCoreBasePEPMPrice,
               productCoreMinimumCharge,
               productCoreMinimumEECount,
               limitVolumeDiscount,
            ),
            productCoreMinimumCharge,
         )
         // Applies all selected and applicable discounts
         applyMonthlyDiscounts(
            discount_list,
            discountStates,
            product_list[coreProductSelected]['ProductCode'],
            productCoreMonthlyCosts,
            productCoreEECount,
            productBenefitsAdministrationEECount,
            productPayrollEECount,
            discount_list['bundleDiscount']['DiscountAmount'],
            promotionalDiscounts,
            currencyObject,
            user_role,
            allPermissions,
         )
         // Gets loyalty discount if applied and calculates MRR Total with it
         const loyaltyDiscount = productCoreMonthlyCosts.appliedDiscounts.find(
            (discount) =>
               discount.DiscountCode === '2024LoyaltyLead' ||
               discount.DiscountCode === '2024LoyaltyAlternate',
         )
         productCoreMonthlyCosts.MRRTotal = calculateMRRTotal(
            productCoreMonthlyCosts,
            allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
            accountSnowflake,
            coreProductSelected,
            differenceQuote
         )
      }

      // Calculate MRR differences if differenceQuote is true
      calculateDifference(
         productCoreMonthlyCosts,
         coreProductSelected,
         product_list,
         accountSnowflake,
         differenceQuote,
         currencyObject,
         previousCurrencyObject,
      )
   } else if (!product_list) console.error('ERROR: product_list is null')
   else if (!coreProductSelected)
      console.error('ERROR: coreProductSelected is null')
   else if (coreProductSelected !== 'core0') {
      if (!product_list.hasOwnProperty(coreProductSelected))
         console.error('ERROR: Core Product not found in product_list')
      else if (
         !product_list[coreProductSelected].hasOwnProperty('MinimumListPrice')
      )
         console.error(
            'ERROR: MinimumListPrice not found in product_list[coreProductSelected]',
         )
      else if (!product_list[coreProductSelected]['MinimumListPrice'])
         console.error(
            'ERROR: MinimumListPrice is null in product_list[coreProductSelected]',
         )
      else console.error('ERROR: Calculate.mjs Unknown error')
   }
   if (
      product_list &&
      coreProductSelected &&
      product_list.hasOwnProperty(coreProductSelected) &&
      product_list[coreProductSelected].hasOwnProperty('SFPricebookEntryID') &&
      product_list[coreProductSelected]['SFPricebookEntryID']
   )
      productCoreMonthlyCosts.SFPricebookEntryID =
         product_list[coreProductSelected]?.SFPricebookEntryID
   else if (!product_list) console.error('ERROR: product_list is null')
   else if (!coreProductSelected)
      console.error('ERROR: coreProductSelected is null')
   else if (coreProductSelected !== 'core0') {
      if (!product_list.hasOwnProperty(coreProductSelected))
         console.error('ERROR: Core Product not found in product_list')
      else if (
         !product_list[coreProductSelected].hasOwnProperty('SFPricebookEntryID')
      )
         console.error(
            'ERROR: SFPricebookEntryID not found in product_list[coreProductSelected]',
         )
      else if (!product_list[coreProductSelected]['SFPricebookEntryID'])
         console.error(
            'ERROR: SFPricebookEntryID is null in product_list[coreProductSelected]',
         )
      else console.error('ERROR: Calculate.mjs Unknown error')
   }
   if (
      product_list &&
      coreProductSelected &&
      product_list.hasOwnProperty(coreProductSelected) &&
      product_list[coreProductSelected].hasOwnProperty('SFProduct2Id') &&
      product_list[coreProductSelected]['SFProduct2Id']
   )
      productCoreMonthlyCosts.SFProduct2Id =
         product_list[coreProductSelected]?.SFProduct2Id
   else if (!product_list) console.error('ERROR: product_list is null')
   else if (!coreProductSelected)
      console.error('ERROR: coreProductSelected is null')
   else if (coreProductSelected !== 'core0') {
      if (!product_list.hasOwnProperty(coreProductSelected))
         console.error('ERROR: Core Product not found in product_list')
      else if (
         !product_list[coreProductSelected].hasOwnProperty('SFProduct2Id')
      )
         console.error(
            'ERROR: SFProduct2Id not found in product_list[coreProductSelected]',
         )
      else if (!product_list[coreProductSelected]['SFProduct2Id'])
         console.error(
            'ERROR: SFProduct2Id is null in product_list[coreProductSelected]',
         )
      else console.error('ERROR: Calculate.mjs Unknown error')
   }
   productCoreMonthlyCosts.productCode = coreProductSelected
   // Rounds all core product numbers
   roundObjectPropertiesToPrecision(productCoreMonthlyCosts, 2)

   ///////////////////
   // Time Tracking //

   // Check if time tracking is in the pricebook
   addEmptyProductToProductList(product_list, 'timeTracking')

   // Time Tracking Base PEPM Price
   let productTimeTrackingBasePEPMPrice =
      product_list['timeTracking']['EmpListPrice']
   let productTimeTrackingMinimumCharge =
      product_list['timeTracking']['MinimumListPrice']
   let productTimeTrackingMinimumEECount =
      product_list['timeTracking']['MinimumEECount']
   // Time Tracking Monthly Costs
   let productTimeTrackingMonthlyCosts = new MonthlyPrice()
   if (quoteType !== 'onetime' && selectedTimeProduct === 'timeTracking') {
      productTimeTrackingMonthlyCosts = calculateMonthlyProductCosts(
         productTimeTrackingEECount,
         productTimeTrackingBasePEPMPrice,
         volumeDiscountedPEPM(
            product_list['timeTracking'],
            productTimeTrackingEECount,
            productTimeTrackingBasePEPMPrice,
            null,
            productTimeTrackingMinimumEECount,
            limitVolumeDiscount,
         ),
         productTimeTrackingMinimumCharge,
      )
      // Applies all selected and applicable discounts
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'timeTracking',
         productTimeTrackingMonthlyCosts,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
      productTimeTrackingMonthlyCosts.MRRTotal = calculateMRRTotal(
         productTimeTrackingMonthlyCosts,
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake,
         'timeTracking',
         differenceQuote
      )
      productTimeTrackingMonthlyCosts.productCode = 'timeTracking'
      // Rounds all time tracking numbers
      roundObjectPropertiesToPrecision(productTimeTrackingMonthlyCosts, 2)
   }

   /////////////////////
   // Time Attendance //

   // Check if time attendance is in the pricebook
   addEmptyProductToProductList(product_list, 'timeAttendance')

   // Time attendance count comes exclusively from dynamicProducts. Never fall
   // back to productTimeTrackingEECount — the two products are mutually exclusive
   // and leaking the tracking count into attendance inflates totals.
   const productTimeAttendanceEECount =
      selectedTimeProduct === 'timeAttendance'
         ? Number(dynamicProducts?.get('timeAttendance')) || 0
         : 0

   let productTimeAttendanceBasePEPMPrice =
      product_list['timeAttendance']['EmpListPrice']
   let productTimeAttendanceMinimumCharge =
      product_list['timeAttendance']['MinimumListPrice']
   let productTimeAttendanceMinimumEECount =
      product_list['timeAttendance']['MinimumEECount']
   // Time Attendance Monthly Costs
   let productTimeAttendanceMonthlyCosts = new MonthlyPrice()
   if (quoteType !== 'onetime' && selectedTimeProduct === 'timeAttendance') {
      productTimeAttendanceMonthlyCosts = calculateMonthlyProductCosts(
         productTimeAttendanceEECount,
         productTimeAttendanceBasePEPMPrice,
         volumeDiscountedPEPM(
            product_list['timeAttendance'],
            productTimeAttendanceEECount,
            productTimeAttendanceBasePEPMPrice,
            null,
            productTimeAttendanceMinimumEECount,
            limitVolumeDiscount,
         ),
         productTimeAttendanceMinimumCharge,
      )
      // Applies all selected and applicable discounts
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'timeAttendance',
         productTimeAttendanceMonthlyCosts,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
      productTimeAttendanceMonthlyCosts.MRRTotal = calculateMRRTotal(
         productTimeAttendanceMonthlyCosts,
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake,
         'timeAttendance',
         differenceQuote
      )
      productTimeAttendanceMonthlyCosts.productCode = 'timeAttendance'
   }
   calculateDifference(
      productTimeAttendanceMonthlyCosts,
      'timeAttendance',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   if (quoteType !== 'onetime' && selectedTimeProduct === 'timeAttendance') {
      roundObjectPropertiesToPrecision(productTimeAttendanceMonthlyCosts, 2)
   }

   productTimeAttendanceMonthlyCosts.SFPricebookEntryID =
      product_list?.timeAttendance?.SFPricebookEntryID
   productTimeAttendanceMonthlyCosts.SFProduct2Id =
      product_list?.timeAttendance?.SFProduct2Id
   productTimeAttendanceMonthlyCosts.productCode = 'timeAttendance'

   // Time Clocks Monthly Costs
   let TimeClockMonthlyCost = new MonthlyPrice()
   if (
      hasTimeClocks &&
      product_list['clockMonthlySupport'] &&
      quoteType !== 'onetime'
   ) {
      // Excludes Shipping items from the Monthly Support count
      let clockCount = productTimeClockDetails
         ? productTimeClockDetails.reduce((acc, item) => {
              return (
                 acc +
                 (isNaN(item.count) ? 0 : item.timeClockCode ? item.count : 0)
              )
           }, 0)
         : 0
      TimeClockMonthlyCost.productEECount = clockCount
      TimeClockMonthlyCost.listPrice =
         product_list['clockMonthlySupport']['BasePrice'] * clockCount
      TimeClockMonthlyCost.listPEPMCost =
         product_list['clockMonthlySupport']['BasePrice']
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'clockMonthlySupport',
         TimeClockMonthlyCost,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
      TimeClockMonthlyCost.MRRTotal = calculateMRRTotal(
         TimeClockMonthlyCost,
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake,
         'clockMonthlySupport',
         differenceQuote
      )
      TimeClockMonthlyCost.totalPrice = TimeClockMonthlyCost.listPrice
      TimeClockMonthlyCost.pdfDisplayTotal = TimeClockMonthlyCost.totalPrice
   }
   calculateDifference(
      TimeClockMonthlyCost,
      'clockMonthlySupport',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   TimeClockMonthlyCost.productCode = 'clockMonthlySupport'
   TimeClockMonthlyCost.SFPricebookEntryID =
      product_list?.clockMonthlySupport?.SFPricebookEntryID
   TimeClockMonthlyCost.SFProduct2Id =
      product_list?.clockMonthlySupport?.SFProduct2Id

   // Time Clocks One Time Costs
   // If productTimeClockDetails is null or undefned, set to empty array
   productTimeClockDetails = productTimeClockDetails
      ? productTimeClockDetails
      : []

   let TimeClockCosts = Array.from(
      { length: productTimeClockDetails.length },
      () => new OneTimePrice(),
   )
   if (
      hasTimeClocks &&
      productTimeClockDetails.length > 0 &&
      quoteType !== 'monthly'
   ) {
      // Sorts the timeClockDetails by timeClockCode and shippingClockCode
      productTimeClockDetails
         .sort((a, b) => {
            // Check if 'timeClockCode' exists in both objects
            const aHasTimeClockCode = !!a.timeClockCode
            const bHasTimeClockCode = !!b.timeClockCode

            // Sort items with 'timeClockCode' first
            if (aHasTimeClockCode && !bHasTimeClockCode) return -1 // a comes before b
            if (!aHasTimeClockCode && bHasTimeClockCode) return 1 // b comes before a
            return 0 // No change in order if both have or don't have 'timeClockCode'
         })
         .forEach((clock, index) => {
            const clockItem =
               product_list[clock.timeClockCode] ??
               product_list[clock.shippingClockCode]
            const totalClockCount = isNaN(clock.count) ? 0 : clock.count
            TimeClockCosts[index].productCode =
               clock.timeClockCode ?? clock.shippingClockCode
            TimeClockCosts[index].productEECount = totalClockCount
            TimeClockCosts[index].listPrice =
               (clockItem ? clockItem['BasePrice'] : 0) * totalClockCount
            TimeClockCosts[index].totalPrice =
               (clockItem ? clockItem['BasePrice'] : 0) * totalClockCount
            TimeClockCosts[index].pdfDisplayTotal = TimeClockCosts[index].totalPrice
            TimeClockCosts[index].SFPricebookEntryID =
               clockItem?.SFPricebookEntryID
            TimeClockCosts[index].SFProduct2Id = clockItem?.SFProduct2Id
            applyOneTimeDiscounts(
               discount_list,
               discountStates,
               clock.timeClockCode,
               TimeClockCosts[index],
               productPayrollEINCount > 1,
               promotionalDiscounts,
               currencyObject,
            )
         })
   }
   // Calculate MRR differences if differenceQuote is true
   calculateDifference(
      productTimeTrackingMonthlyCosts,
      'timeTracking',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   productTimeTrackingMonthlyCosts.SFPricebookEntryID =
      product_list?.timeTracking?.SFPricebookEntryID
   productTimeTrackingMonthlyCosts.SFProduct2Id =
      product_list?.timeTracking?.SFProduct2Id
   productTimeTrackingMonthlyCosts.productCode = 'timeTracking'

   ///////////////
   // Ben Admin //

   // Check if ben admin is in the pricebook
   addEmptyProductToProductList(product_list, 'benAdmin')

   // Ben Admin Base PEPM Price
   let productBenefitsAdministrationBasePEPMPrice =
      product_list['benAdmin']['EmpListPrice']
   // Ben Admin Minimum Charge
   let BenefitsAdministrationMinimumCharge =
      product_list['benAdmin']['MinimumListPrice']
   // Benefits Administration costs
   let productBenefitsAdministrationMonthlyCosts = new MonthlyPrice()
   if (quoteType !== 'onetime') {
      productBenefitsAdministrationMonthlyCosts = calculateMonthlyProductCosts(
         productBenefitsAdministrationEECount,
         productBenefitsAdministrationBasePEPMPrice,
         volumeDiscountedPEPM(
            product_list['benAdmin'],
            productBenefitsAdministrationEECount,
            productBenefitsAdministrationBasePEPMPrice,
            0,
            1,
            limitVolumeDiscount,
         ),
         BenefitsAdministrationMinimumCharge,
      )
      // Applies all selected and applicable discounts
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'benAdmin',
         productBenefitsAdministrationMonthlyCosts,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
      productBenefitsAdministrationMonthlyCosts.MRRTotal = calculateMRRTotal(
         productBenefitsAdministrationMonthlyCosts,
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake,
         'benAdmin',
         differenceQuote
      )
      productBenefitsAdministrationMonthlyCosts.productCode = 'benAdmin'
      // Rounds all ben admin numbers
      roundObjectPropertiesToPrecision(
         productBenefitsAdministrationMonthlyCosts,
         2,
      )
   }
   // Calculate MRR differences if differenceQuote is true
   calculateDifference(
      productBenefitsAdministrationMonthlyCosts,
      'benAdmin',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   productBenefitsAdministrationMonthlyCosts.SFPricebookEntryID =
      product_list?.benAdmin?.SFPricebookEntryID
   productBenefitsAdministrationMonthlyCosts.SFProduct2Id =
      product_list?.benAdmin?.SFProduct2Id
   // Set Ben Admin productCode

   /////////////////
   // Performance //

   // Check if performance is in the pricebook
   addEmptyProductToProductList(product_list, 'performanceMan')

   // Peformance Base PEPM Price
   let productPerformanceBasePEPMPrice = 0
   // Checks if performance is in the pricebook
   if (product_list.hasOwnProperty('performanceMan') == true) {
      productPerformanceBasePEPMPrice =
         product_list['performanceMan']['EmpListPrice']
   }
   // Performance Monthly Costs
   let productPerformanceMonthlyCosts = new MonthlyPrice()
   // Checks if performance is in the pricebook
   if (
      product_list.hasOwnProperty('performanceMan') == true &&
      quoteType !== 'onetime'
   ) {
      productPerformanceMonthlyCosts = calculateMonthlyProductCosts(
         productPerformanceEECount,
         productPerformanceBasePEPMPrice,
         volumeDiscountedPEPM(
            product_list['performanceMan'],
            productPerformanceEECount,
            productPerformanceBasePEPMPrice,
            0,
            1,
            limitVolumeDiscount,
         ),
      )
      // Applies all selected and applicable discounts
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'performanceMan',
         productPerformanceMonthlyCosts,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
      productPerformanceMonthlyCosts.MRRTotal = calculateMRRTotal(
         productPerformanceMonthlyCosts,
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake,
         'performanceMan',
         differenceQuote
      )
      // Rounds all performance numbers
      roundObjectPropertiesToPrecision(productPerformanceMonthlyCosts, 2)
      // Calculate MRR differences if differenceQuote is true
      calculateDifference(
         productPerformanceMonthlyCosts,
         'performanceMan',
         product_list,
         accountSnowflake,
         differenceQuote,
         currencyObject,
         previousCurrencyObject,
      )
      productPerformanceMonthlyCosts.SFPricebookEntryID =
         product_list?.performanceMan?.SFPricebookEntryID
      productPerformanceMonthlyCosts.SFProduct2Id =
         product_list?.performanceMan?.SFProduct2Id
      productPerformanceMonthlyCosts.productCode = 'performanceMan'
   }

   //////////////////////////////
   // Payroll & Payroll States //
   // The next section is more complex than the previous. There are two costs, a MonthlyPrice(), for payroll costs,
   // and a price for payroll states. If quote has one payroll EIN, the calculation is similar to other normal products.
   // When using multiple EINs, the calculations become more complex and rely upon each other.

   // Payroll Base PEPM Price
   let productPayrollBasePEPMPrice = product_list['payroll']['EmpListPrice']
   // Payroll Minimum Charge
   let productPayrollMinimumCharge = product_list['payroll']['MinimumListPrice']

   // Payroll States per State Price
   let productPayrollStatePrice = product_list['states']['EmpListPrice']

   // Payroll and Payroll States costs
   let productPayrollMonthlyTotalCosts = new MonthlyPrice()
   let productPayrollMonthlyStateTotalCosts = new MonthlyPrice()
   productPayrollMonthlyStateTotalCosts.productCode = 'statesTotal'
   // Multi EIN Payroll Costs
   let productPayrollMonthlyCosts = Array.from(
      { length: productPayrollEINCount },
      () => new MonthlyPrice(),
   )
   let productPayrollMonthlyStateCosts = Array.from(
      { length: productPayrollEINCount },
      () => new MonthlyPrice(),
   )

   const existingEINCount = differenceQuote
      ? accountSnowflake?.EINs__c?.[0]?.length || 0
      : 0

   // If not MultiEIN, calculates payroll and states like normally monthly products
   for (let i = 0; i < productPayrollEINCount; i++) {
      // Payroll Monthly Costs
      productPayrollMonthlyCosts[i] = calculateMonthlyProductCosts(
         productPayrollEECounts[i],
         productPayrollBasePEPMPrice,
         volumeDiscountedPEPM(
            product_list['payroll'],
            productPayrollEECounts[i],
            productPayrollBasePEPMPrice,
            productPayrollMinimumCharge,
            1,
            limitVolumeDiscount,
         ),
         productPayrollMinimumCharge,
      )
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'payroll',
         productPayrollMonthlyCosts[i],
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )

      // Set previous values for difference display when this is a diff quote with snowflake data
      if (
         differenceQuote &&
         accountSnowflake &&
         accountSnowflake.EINs__c &&
         Array.isArray(accountSnowflake.EINs__c) &&
         accountSnowflake.EINs__c.length > 3
      ) {
         // Use EINs__c[2][index] for previous expected MRR (totalPrice)
         const previousExpectedMRR = accountSnowflake.EINs__c?.[2]?.[i] || 0
         // Use EINs__c[3][index] for previous commissionable MRR (MRRTotal)
         const previousCommissionableMRR =
            accountSnowflake.EINs__c?.[3]?.[i] || 0

         // Set previous price values
         productPayrollMonthlyCosts[i].previousTotalPrice = previousExpectedMRR
         productPayrollMonthlyCosts[i].previousTotalPriceUnconverted =
            previousExpectedMRR
         // Set previous MRR value specifically from EINs__c[3]
         productPayrollMonthlyCosts[i].previousMRRTotal =
            previousCommissionableMRR

         // Apply currency conversion if needed
         if (currencyObject && currencyObject.Rate) {
            productPayrollMonthlyCosts[i].previousTotalPrice *=
               currencyObject.Rate
            productPayrollMonthlyCosts[i].previousMRRTotal *=
               currencyObject.Rate
         }

         // Calculate price difference
         productPayrollMonthlyCosts[i].totalPriceDifference =
            productPayrollMonthlyCosts[i].totalPrice -
            productPayrollMonthlyCosts[i].previousTotalPrice

         productPayrollMonthlyCosts[i].pdfDisplayDifferenceTotal =
            productPayrollMonthlyCosts[i].pdfDisplayTotal -
            productPayrollMonthlyCosts[i].previousTotalPrice
      }

      // PAYROLL COMMISSION LOGIC:
      // When differenceQuote is true, existing EINs (by index) get $0 MRR — no
      // commission is earned on headcount increases for existing payroll customers.
      // New EINs always calculate full MRR. When differenceQuote is false, all EINs
      // calculate full MRR.
      //
      // FUTURE ENHANCEMENT: When implementing pricebook migration detection,
      // check if customer is moving from old to new pricing (e.g., 2024 -> 2026 Payroll).
      // In migration scenarios, DO calculate commission on both:
      //   1. Package price increases due to new pricing
      //   2. Headcount changes
      // Add pricebook version comparison logic here when ready.
      if (i < existingEINCount) {
         productPayrollMonthlyCosts[i].MRRTotal = 0
         productPayrollMonthlyCosts[i].previousMRRTotal = 0
         productPayrollMonthlyCosts[i].listMRRTotal = calculateMRRTotal(
            productPayrollMonthlyCosts[i],
            allPermissions['NoMRRCalculation']?.includes(user_role) || false,
         )
      } else {
         // New EINs have no prior billing data in EINs__c[3], so previousMRRTotal is always 0
         const currentMRR = calculateMRRTotal(
            productPayrollMonthlyCosts[i],
            allPermissions['NoMRRCalculation']?.includes(user_role) || false,
         )
         productPayrollMonthlyCosts[i].MRRTotal = currentMRR
         productPayrollMonthlyCosts[i].listMRRTotal = currentMRR
      }

      productPayrollMonthlyCosts[i].productCode = `payroll${i}`
      roundObjectPropertiesToPrecision(productPayrollMonthlyCosts[i], 2)
   }

   for (let i = 0; i < productPayrollEINCount; i++) {
      // Calculate state charge - first state is free, charge for additional states
      const stateCount = parseInt(productPayrollStateCounts[i]) || 0
      const currentStateCharge =
         Math.max(stateCount - 1, 0) * productPayrollStatePrice

      // Create a MonthlyPrice object for state costs
      productPayrollMonthlyStateCosts[i].productCode = `states${i}`
      productPayrollMonthlyStateCosts[i].productEECount = stateCount
      productPayrollMonthlyStateCosts[i].listPrice = currentStateCharge
      productPayrollMonthlyStateCosts[i].listPEPMCost = productPayrollStatePrice
      productPayrollMonthlyStateCosts[i].totalPrice = currentStateCharge

      // Calculate MRR for state costs
      productPayrollMonthlyStateCosts[i].MRRTotal = calculateMRRTotal(
         productPayrollMonthlyStateCosts[i],
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake,
      )
      productPayrollMonthlyStateCosts[i].listMRRTotal =
         productPayrollMonthlyStateCosts[i].MRRTotal

      // If this is a difference quote, calculate the previous values and differences
      if (differenceQuote) {
         const previousStateCharge = accountSnowflake?.EINs__c?.[5]?.[i] || 0
         const previousStateCount = accountSnowflake?.EINs__c?.[6]?.[i] || 0

         // Set previous values
         productPayrollMonthlyStateCosts[i].previousTotalPrice =
            previousStateCharge
         productPayrollMonthlyStateCosts[i].previousTotalPriceUnconverted =
            previousStateCharge
         // Set previously commissionable MRR for statez
         productPayrollMonthlyStateCosts[i].previousMRRTotal =
            (accountSnowflake?.EINs__c?.[5]?.[i] || 1) *
            (previousStateCharge / (accountSnowflake?.EINs__c?.[5]?.[i] || 1)) // Proportion of state costs

         // Apply currency conversion if needed
         if (currencyObject && currencyObject.Rate) {
            productPayrollMonthlyStateCosts[i].previousTotalPrice *=
               currencyObject.Rate
            productPayrollMonthlyStateCosts[i].previousMRRTotal *=
               currencyObject.Rate
         }

         // Calculate price difference
         productPayrollMonthlyStateCosts[i].totalPriceDifference =
            productPayrollMonthlyStateCosts[i].totalPrice -
            productPayrollMonthlyStateCosts[i].previousTotalPrice


         productPayrollMonthlyStateCosts[i].pdfDisplayDifferenceTotal =
            productPayrollMonthlyStateCosts[i].pdfDisplayTotal -
            productPayrollMonthlyStateCosts[i].previousTotalPrice

         // PAYROLL STATES DIFFERENCE QUOTE COMMISSION LOGIC:
         // Currently, payroll states DO earn commission on state count increases in difference quotes.
         // MRRTotal is the difference between current and previous, may be negative for individual EINs, but never for the total
         //
         // FUTURE ENHANCEMENT: Similar to payroll EINs above, we may want to disable commission
         // on state count increases for existing customers. When implementing this:
         //   - Check if previousStateCharge > 0 (existing states)
         //   - Set MRRTotal to 0 for existing state increases
         //   - Still allow commission for pricebook migrations (when that logic is added)
         if (i < existingEINCount) {
            productPayrollMonthlyStateCosts[i].MRRTotal = 0
            productPayrollMonthlyStateCosts[i].previousMRRTotal = 0
         } else {
            productPayrollMonthlyStateCosts[i].MRRTotal =
               productPayrollMonthlyStateCosts[i].listMRRTotal -
               productPayrollMonthlyStateCosts[i].previousMRRTotal
         }
      }

      // Round all numbers for consistency
      roundObjectPropertiesToPrecision(productPayrollMonthlyStateCosts[i], 2)
   }
   // calculate values for each payroll EIN
   if (quoteType !== 'onetime') {
      // Summarized Payroll Monthly Costs Among All EINs
      productPayrollMonthlyTotalCosts = new MonthlyPrice()
      ;((productPayrollMonthlyTotalCosts.productEECount =
         productPayrollEECount),
         applyMonthlyDiscounts(
            discount_list,
            discountStates,
            'payroll',
            productPayrollMonthlyTotalCosts,
            productCoreEECount,
            productBenefitsAdministrationEECount,
            productPayrollEECount,
            discount_list['bundleDiscount']['DiscountAmount'],
            promotionalDiscounts,
            currencyObject,
            user_role,
            allPermissions,
         ))
      productPayrollMonthlyTotalCosts.listPrice = isNaN(
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.listPrice,
            0,
         ),
      )
         ? 0
         : productPayrollMonthlyCosts.reduce(
              (acc, val) => acc + val.listPrice,
              0,
           )
      productPayrollMonthlyTotalCosts.listPEPMCost =
         productPayrollEECount > 0 ? productPayrollBasePEPMPrice : 0
      productPayrollMonthlyTotalCosts.totalPrice =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + (isNaN(val.totalPrice) ? 0 : val.totalPrice),
            0,
         )
      productPayrollMonthlyTotalCosts.pdfDisplayTotal =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + (isNaN(val.pdfDisplayTotal) ? 0 : val.pdfDisplayTotal),
            0,
         )
      productPayrollMonthlyTotalCosts.discountedPEPMCost =
         productPayrollEINCount >= 1
            ? productPayrollEECount > 0
               ? productPayrollMonthlyTotalCosts.totalPrice /
                 productPayrollEECount
               : 0
            : Math.max(
                 productPayrollEECount > 0
                    ? productPayrollMinimumCharge / productPayrollEECount
                    : 0,
                 productPayrollEECount > 0
                    ? productPayrollMonthlyCosts.reduce(
                         (acc, val) =>
                            acc +
                            (isNaN(val.discountedPEPMCost)
                               ? 0
                               : val.discountedPEPMCost),
                         0,
                      ) / productPayrollEECount
                    : 0,
              )
      productPayrollMonthlyTotalCosts.pdfDisplayDiscountedPEPMCost =
         productPayrollEINCount >= 1
            ? productPayrollEECount > 0
               ? productPayrollMonthlyTotalCosts.pdfDisplayTotal /
                 productPayrollEECount
               : 0
            : Math.max(
                 productPayrollEECount > 0
                    ? productPayrollMinimumCharge / productPayrollEECount
                    : 0,
                 productPayrollEECount > 0
                    ? productPayrollMonthlyCosts.reduce(
                         (acc, val) =>
                            acc +
                            (isNaN(val.pdfDisplayDiscountedPEPMCost)
                               ? 0
                               : val.pdfDisplayDiscountedPEPMCost),
                         0,
                      ) / productPayrollEECount
                    : 0,
              )
      ;((productPayrollMonthlyTotalCosts.volumeDiscountedPEPMPercent =
         (Math.max(
            productPayrollEECount * productPayrollBasePEPMPrice -
               productPayrollEECount *
                  productPayrollMonthlyTotalCosts.discountedPEPMCost,
            0,
         ) /
            Math.max(
               productPayrollMinimumCharge,
               productPayrollEECount * productPayrollBasePEPMPrice,
            )) *
         100),
         (productPayrollMonthlyTotalCosts.totalPEPMDiscount = Math.max(
            productPayrollBasePEPMPrice -
               Math.max(
                  productPayrollEECount > 0
                     ? productPayrollMinimumCharge / productPayrollEECount
                     : productPayrollBasePEPMPrice,
                  productPayrollMonthlyTotalCosts.discountedPEPMCost,
               ),
            0,
         )))
      productPayrollMonthlyTotalCosts.totalDiscount =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.totalDiscount,
            0,
         )
      productPayrollMonthlyTotalCosts.pdfDisplayTotalDiscount =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.pdfDisplayTotalDiscount,
            0,
         )
      productPayrollMonthlyTotalCosts.totalDiscountPercent =
         (Math.max(
            productPayrollEECount * productPayrollBasePEPMPrice -
               productPayrollEECount *
                  productPayrollMonthlyTotalCosts.discountedPEPMCost,
            0,
         ) /
            Math.max(
               productPayrollMinimumCharge,
               productPayrollEECount * productPayrollBasePEPMPrice,
            )) *
         100
      productPayrollMonthlyTotalCosts.pdfDisplayTotalDiscountPercent =
         (Math.max(
            productPayrollEECount * productPayrollBasePEPMPrice -
               productPayrollEECount *
                  productPayrollMonthlyTotalCosts.pdfDisplayDiscountedPEPMCost,
            0,
         ) /
            Math.max(
               productPayrollMinimumCharge,
               productPayrollEECount * productPayrollBasePEPMPrice,
            )) *
         100
      productPayrollMonthlyTotalCosts.MRRTotal = Math.max(
         productPayrollMonthlyCosts.reduce((acc, val) => acc + val.MRRTotal, 0),
         0,
      )
      productPayrollMonthlyTotalCosts.listMRRTotal =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.listMRRTotal,
            0,
         )
      productPayrollMonthlyTotalCosts.previousMRRTotal =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.previousMRRTotal,
            0,
         )
      productPayrollMonthlyTotalCosts.appliedDiscounts =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => [...acc, ...(val.appliedDiscounts || [])],
            [],
         )

      // Summarized Payroll States Monthly Costs
      productPayrollMonthlyStateTotalCosts.productCode = 'statesTotal'
      productPayrollMonthlyStateTotalCosts.listPrice =
         productPayrollMonthlyStateCosts.reduce(
            (acc, val) =>
               acc +
               (typeof val === 'object' && !isNaN(val.listPrice)
                  ? val.listPrice
                  : 0),
            0,
         )

      const calculatedStateTotal = productPayrollMonthlyStateCosts.reduce(
         (acc, val) =>
            acc +
            (typeof val === 'object'
               ? val.totalPrice
               : typeof val === 'number'
                 ? val
                 : 0),
         0,
      )

      if (isNaN(calculatedStateTotal)) {
         console.warn(
            'State total calculation resulted in NaN, using 0 instead',
         )
         productPayrollMonthlyStateTotalCosts.totalPrice = 0
      } else {
         productPayrollMonthlyStateTotalCosts.totalPrice = calculatedStateTotal
         productPayrollMonthlyStateTotalCosts.pdfDisplayTotal = calculatedStateTotal
      }

      if (!productPayrollMonthlyStateTotalCosts.totalPrice) {
         productPayrollMonthlyStateTotalCosts.totalPrice = calculatedStateTotal || 0
         productPayrollMonthlyStateTotalCosts.pdfDisplayTotal = calculatedStateTotal || 0
      }

      if (!productPayrollMonthlyStateTotalCosts.MRRTotal) {
         productPayrollMonthlyStateTotalCosts.MRRTotal = Math.max(
            productPayrollMonthlyStateCosts.reduce(
               (acc, val) =>
                  acc +
                  (typeof val === 'object' && val !== null
                     ? val.MRRTotal || 0
                     : 0),
               0,
            ),
            0,
         )
      }
   }

   if (differenceQuote) {
      // Add previous price and difference calculations for the payroll monthly costs ONLY (no states)
      productPayrollMonthlyTotalCosts.previousTotalPrice =
         productPayrollMonthlyCosts.reduce(
            (total, ein) => total + (ein.previousTotalPrice || 0),
            0,
         )

      productPayrollMonthlyTotalCosts.previousTotalPriceUnconverted =
         productPayrollMonthlyCosts.reduce(
            (total, ein) => total + (ein.previousTotalPriceUnconverted || 0),
            0,
         )

      productPayrollMonthlyTotalCosts.totalPriceDifference =
         productPayrollMonthlyTotalCosts.totalPrice -
         productPayrollMonthlyTotalCosts.previousTotalPrice

      productPayrollMonthlyTotalCosts.pdfDisplayDifferenceTotal =
         productPayrollMonthlyTotalCosts.pdfDisplayTotal -
         productPayrollMonthlyTotalCosts.previousTotalPrice

      productPayrollMonthlyTotalCosts.listMRRTotal =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.listMRRTotal,
            0,
         )
      productPayrollMonthlyTotalCosts.previousMRRTotal =
         productPayrollMonthlyCosts.reduce(
            (acc, val) => acc + val.previousMRRTotal,
            0,
         )

      // Calculate total previous state costs and total differences
      productPayrollMonthlyStateTotalCosts.previousTotalPrice =
         productPayrollMonthlyStateCosts.reduce(
            (total, state) =>
               total +
               (typeof state === 'object' ? state.previousTotalPrice : 0),
            0,
         )

      productPayrollMonthlyStateTotalCosts.totalPriceDifference =
         productPayrollMonthlyStateCosts.reduce(
            (total, state) =>
               total +
               (typeof state === 'object' ? state.totalPriceDifference : 0),
            0,
         )
      productPayrollMonthlyStateTotalCosts.pdfDisplayDifferenceTotal = productPayrollMonthlyStateTotalCosts.totalPriceDifference

      // Add calculation for previousMRRTotal
      productPayrollMonthlyStateTotalCosts.previousMRRTotal =
         productPayrollMonthlyStateCosts.reduce(
            (total, state) =>
               total +
               (typeof state === 'object' ? state.previousMRRTotal || 0 : 0),
            0,
         )

      productPayrollMonthlyStateTotalCosts.listMRRTotal =
         productPayrollMonthlyStateCosts.reduce(
            (total, state) =>
               total +
               (typeof state === 'object' ? state.listMRRTotal || 0 : 0),
            0,
         )
   } else {
      productPayrollMonthlyTotalCosts.previousTotalPrice = 0
      productPayrollMonthlyTotalCosts.previousTotalPriceUnconverted = 0
      productPayrollMonthlyTotalCosts.totalPriceDifference =
         productPayrollMonthlyTotalCosts.totalPrice

      productPayrollMonthlyTotalCosts.pdfDisplayDifferenceTotal = productPayrollMonthlyTotalCosts.totalPriceDifference

      productPayrollMonthlyStateTotalCosts.previousTotalPrice = 0
      productPayrollMonthlyStateTotalCosts.previousTotalPriceUnconverted = 0
      productPayrollMonthlyStateTotalCosts.totalPriceDifference =
         productPayrollMonthlyStateTotalCosts.totalPrice

      productPayrollMonthlyStateTotalCosts.pdfDisplayDifferenceTotal = productPayrollMonthlyStateTotalCosts.totalPriceDifference
   }

   productPayrollMonthlyTotalCosts.productCode = 'payroll'
   // Rounds all payroll numbers
   roundObjectPropertiesToPrecision(productPayrollMonthlyTotalCosts, 2)

   /////////////////////////
   //   EIN Integrations  //
   /////////////////////////

   let productEINIntegrationsTotalCosts = new MonthlyPrice()
   let productEINIntegrationsCosts = Array.from(
      { length: productPayrollEINCount },
      () => new MonthlyPrice(),
   )

   if (
      Array.isArray(productEINIntegrations) &&
      (productEINIntegrations?.length ?? 0) > 0
   ) {
      // Calculate EIN Integrations Monthly Costs For all EINs
      let integrationCount =
         productEINIntegrations?.reduce((acc, ein) => {
            return acc + (isNaN(ein?.length ?? NaN) ? 0 : (ein?.length ?? 0))
         }, 0) ?? 0
      productEINIntegrationsTotalCosts.productCode = 'EINIntegrationsTotal'
      productEINIntegrationsTotalCosts.productEECount = integrationCount
      productEINIntegrationsTotalCosts.listPrice =
         productEINIntegrations?.reduce((acc, ein) => {
            return (
               acc +
                  ein?.reduce(
                     (intAcc, integration) =>
                        intAcc + integration?.solutionCost ?? 0,
                     0,
                  ) ?? 0
            )
         }, 0) ?? 0
      productEINIntegrationsTotalCosts.listPEPMCost = 0
      productEINIntegrationsTotalCosts.MRRTotal = calculateMRRTotal(
         productEINIntegrationsTotalCosts,
         allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
         accountSnowflake
      )
      productEINIntegrationsTotalCosts.listMRRTotal =
         productEINIntegrationsTotalCosts.MRRTotal
      // Difference quote not yet enabled per EIN Integration
      productEINIntegrationsTotalCosts.previousTotalPrice = 0
      productEINIntegrationsTotalCosts.totalPriceDifference =
         productEINIntegrationsTotalCosts.listPrice

      productEINIntegrationsTotalCosts.pdfDisplayDifferenceTotal = productEINIntegrationsTotalCosts.totalPriceDifference
      // ^^^
      productEINIntegrationsTotalCosts.totalPrice =
         productEINIntegrationsTotalCosts.listPrice
      productEINIntegrationsTotalCosts.pdfDisplayTotal = productEINIntegrationsTotalCosts.totalPrice

      // Calculate EIN Integrations Monthly Costs For each EIN
      for (let i = 0; i < productPayrollEINCount; i++) {
         const integrationsArray = productEINIntegrations[i] || []
         productEINIntegrationsCosts[i].productEECount =
            integrationsArray.length
         productEINIntegrationsCosts[i].listPrice = integrationsArray.reduce(
            (acc, integration) => acc + integration.solutionCost,
            0,
         )
         productEINIntegrationsCosts[i].listPEPMCost = 0
         productEINIntegrationsCosts[i].MRRTotal = calculateMRRTotal(
            productEINIntegrationsCosts[i],
            allPermissions['NoMRRCalculation']?.includes(user_role) || false, // No calculation MRR returns 0
            accountSnowflake
         )
         productEINIntegrationsCosts[i].totalPrice =
            productEINIntegrationsCosts[i].listPrice
         productEINIntegrationsCosts[i].pdfDisplayTotal = productEINIntegrationsCosts[i].totalPrice
      }
   }

   ////////////////////////
   // Additional Storage //

   // Check if add storage is in the pricebook
   addEmptyProductToProductList(product_list, 'addStorage')

   // Additional Storage PEPM Cost
   let productAdditionalStoragePerEmp =
      product_list['addStorage']['EmpListPrice']
   // Additional Storage Monthly Costs
   let AdditionalStorageCosts = new MonthlyPrice()
   if (productAdditionalStorageCount > 0 && quoteType !== 'onetime') {
      AdditionalStorageCosts.listPrice =
         productAdditionalStoragePerEmp * productAdditionalStorageCount
      AdditionalStorageCosts.listPEPMCost = productAdditionalStoragePerEmp
      AdditionalStorageCosts.totalPrice =
         productAdditionalStoragePerEmp * productAdditionalStorageCount
      AdditionalStorageCosts.MRRTotal = AdditionalStorageCosts.listPrice
      AdditionalStorageCosts.pdfDisplayTotal = AdditionalStorageCosts.totalPrice
   }
   // Calculate MRR differences if differenceQuote is true
   calculateDifference(
      AdditionalStorageCosts,
      'addStorage',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   AdditionalStorageCosts.SFPricebookEntryID =
      product_list?.addStorage?.SFPricebookEntryID
   AdditionalStorageCosts.SFProduct2Id = product_list?.addStorage?.SFProduct2Id
   AdditionalStorageCosts.productCode = 'addStorage'

   /////////////////////
   // Additional Jobs //

   // Check if add job posting is in the pricebook
   addEmptyProductToProductList(product_list, 'addJobPosting')

   // Additional Jobs PEPM Cost
   let productAdditionalJobsPerEmp =
      product_list['addJobPosting']['EmpListPrice']
   // Additional Jobs Monthly Costs
   let AdditionalJobsCosts = new MonthlyPrice()
   if (productAdditionalJobsCount > 0 && quoteType !== 'onetime') {
      AdditionalJobsCosts.productEECount = productAdditionalJobsCount
      AdditionalJobsCosts.listPrice =
         productAdditionalJobsPerEmp * productAdditionalJobsCount
      AdditionalJobsCosts.listPEPMCost = productAdditionalJobsPerEmp
      AdditionalJobsCosts.totalPrice =
         productAdditionalJobsPerEmp * productAdditionalJobsCount
      AdditionalJobsCosts.pdfDisplayTotal = AdditionalJobsCosts.totalPrice
      AdditionalJobsCosts.MRRTotal = AdditionalJobsCosts.listPrice
      AdditionalJobsCosts.listMRRTotal = AdditionalJobsCosts.listPrice
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'addJobPosting',
         AdditionalJobsCosts,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
   }
   // Calculate MRR differences if differenceQuote is true
   calculateDifference(
      AdditionalJobsCosts,
      'addJobPosting',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   AdditionalJobsCosts.SFPricebookEntryID =
      product_list?.addJobPosting?.SFPricebookEntryID
   AdditionalJobsCosts.SFProduct2Id = product_list?.addJobPosting?.SFProduct2Id
   AdditionalJobsCosts.productCode = 'addJobPosting'

   //////////////////
   // Test Account //

   // Check if test account is in the pricebook
   addEmptyProductToProductList(product_list, 'testAccount')

   // Test Account Base Cost
   let productTestAccountBase =
      'testAccount' in product_list
         ? product_list['testAccount']['BasePrice']
         : 0
   // Test Account One Time Cost
   let TestAccountCosts = new MonthlyPrice()
   if (productTestAccount && quoteType !== 'onetime') {
      TestAccountCosts.productEECount = 1
      TestAccountCosts.listPrice = productTestAccountBase
      TestAccountCosts.totalPrice = productTestAccountBase
      TestAccountCosts.pdfDisplayTotal = TestAccountCosts.totalPrice
      TestAccountCosts.MRRTotal = TestAccountCosts.listPrice
   }
   // Calculate MRR differences if differenceQuote is true
   calculateDifference(
      TestAccountCosts,
      'testAccount',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   TestAccountCosts.SFPricebookEntryID =
      product_list?.testAccount?.SFPricebookEntryID
   TestAccountCosts.SFProduct2Id = product_list?.testAccount?.SFProduct2Id
   TestAccountCosts.productCode = 'testAccount'

   /////////////////
   // Monthly Tax //

   // Check if monthly tax is in the pricebook
   addEmptyProductToProductList(product_list, 'monthlyTax')
   // If monthly tax is not in the pricebook, set to 0 since it is not getting the base price from the JSON object.
   if (product_list['monthlyTax']?.NotInPricebook) monthlyTax = 0

   let MonthlyTaxCosts = new MonthlyPrice()
   if (monthlyTax > 0 && quoteType !== 'onetime') {
      MonthlyTaxCosts.listPrice = parseFloat(monthlyTax)
      MonthlyTaxCosts.totalPrice = parseFloat(monthlyTax)
      MonthlyTaxCosts.pdfDisplayTotal = MonthlyTaxCosts.totalPrice
   }
   // Calculate MRR differences if differenceQuote is true
   calculateDifference(
      MonthlyTaxCosts,
      'monthlyTax',
      product_list,
      accountSnowflake,
      differenceQuote,
      currencyObject,
      previousCurrencyObject,
   )
   MonthlyTaxCosts.productCode = 'monthlyTax'

   /*********************/
   // One Time Products //
   /*********************/

   ///////////////////////
   // HR Implementation //
   let hrImplementationCosts = new OneTimePrice()
   let productQuickstartImplementation = null
   let productNormalImplementation = null
   let productPremierImplementation = null

   if (coreProductSelected !== 'core0') {
      // Check if HR Implementation products are in the pricebook
      addEmptyProductToProductList(
         product_list,
         `${coreProductSelected}Quickstart`,
      )
      addEmptyProductToProductList(product_list, `${coreProductSelected}Imp`)
      addEmptyProductToProductList(
         product_list,
         `${coreProductSelected}PremierImp`,
      )

      // HR Implementation Product Codes
      productQuickstartImplementation =
         product_list[`${coreProductSelected}Quickstart`]
      productNormalImplementation = product_list[`${coreProductSelected}Imp`]
      productPremierImplementation =
         product_list[`${coreProductSelected}PremierImp`]

      if (quoteType !== 'monthly') {
         // HR Implementation One Time Costs
         hrImplementationCosts = calculateHRImplementationCost(
            product_list,
            productCoreEECount,
            determineHRImplementationType(
               impType,
               productCoreEECount,
               productQuickstartImplementation,
               productNormalImplementation,
               productPremierImplementation,
            ),
            impType,
            coreCountryCount,
         )
         // Applies all selected and applicable discounts
         applyOneTimeDiscounts(
            discount_list,
            discountStates,
            determineHRImplementationType(
               impType,
               productCoreEECount,
               productQuickstartImplementation,
               productNormalImplementation,
               productPremierImplementation,
            ),
            hrImplementationCosts,
            productPayrollEINCount > 1,
            promotionalDiscounts,
            currencyObject,
         )
         // Rounds all hr imp costs
         roundObjectPropertiesToPrecision(hrImplementationCosts, 2)
         // Set HRIS Imp Product Code
         hrImplementationCosts.productCode = determineHRImplementationType(
            impType,
            productCoreEECount,
            productQuickstartImplementation,
            productNormalImplementation,
            productPremierImplementation,
         )
         hrImplementationCosts.productEECount = 1

         hrImplementationCosts.SFPricebookEntryID =
            product_list[
               determineHRImplementationType(
                  impType,
                  productCoreEECount,
                  productQuickstartImplementation,
                  productNormalImplementation,
                  productPremierImplementation,
               )
            ]?.SFPricebookEntryID
         hrImplementationCosts.SFProduct2Id =
            product_list[
               determineHRImplementationType(
                  impType,
                  productCoreEECount,
                  productQuickstartImplementation,
                  productNormalImplementation,
                  productPremierImplementation,
               )
            ]?.SFProduct2Id
      }
   } else {
      hrImplementationCosts.productCode = 'selfSetup'
   }

   ////////////////////////////
   // Payroll Implementation //

   // Check if Payroll Implementation options products are in the pricebook
   addEmptyProductToProductList(product_list, 'payrollImp')
   addEmptyProductToProductList(product_list, 'payrollImpFoundations')
   addEmptyProductToProductList(product_list, 'payrollImpAdditionalEIN')
   addEmptyProductToProductList(
      product_list,
      'payrollImpFoundationsAdditionalEIN',
   )

   // Checks if ImpType is Foundations or not, and sets the product code accordingly
   let payrollImpProductCode =
      payrollImpType === 'foundations' && product_list['payrollImpFoundations']
         ? 'payrollImpFoundations'
         : 'payrollImp'
   // Payroll Implementation PEPM Cost
   let productPayrollImplementationPerEmp =
      product_list[payrollImpProductCode]['EmpListPrice']

   // Calculated individual cost per EIN in the multiple EIN table
   let payrollImplementationTotalCosts = new OneTimePrice()
   payrollImplementationTotalCosts.SFPricebookEntryID =
      product_list[payrollImpProductCode]?.SFPricebookEntryID
   payrollImplementationTotalCosts.SFProduct2Id =
      product_list[payrollImpProductCode]?.SFProduct2Id
   let payrollImplementationCosts = Array.from(
      { length: productPayrollEINCount },
      () => new OneTimePrice(),
   )

   payrollImplementationTotalCosts.productCode = payrollImpProductCode
   if (quoteType !== 'monthly') {
      // Calculate imp cost and apply discounts for each EIN
      for (let i = 0; i < productPayrollEINCount; i++) {
         const PayrollEECount = !isNaN(productPayrollEECounts[i])
            ? productPayrollEECounts[i]
            : 0
         // Get the correct product code for this specific EIN's imp type
         let payrollEINImpProductCode =
            productPayrollEINImpTypes[i] === 'foundations' &&
            product_list['payrollImpFoundations']
               ? 'payrollImpFoundations'
               : 'payrollImp'
         // Get the correct PEPM price and base price for this specific EIN's imp type
         let einPayrollImplementationPerEmp =
            product_list[payrollEINImpProductCode]['EmpListPrice']
         let einBasePrice = getBasePrice(
            product_list,
            payrollEINImpProductCode,
            productPayrollEECounts[i],
         )

         payrollImplementationCosts[i] = calculatePayrollImplementationCost(
            product_list,
            productPayrollEINImpTypes[i],
            PayrollEECount,
            einPayrollImplementationPerEmp,
            einBasePrice,
            productPayrollEINCount > 1,
            accountSnowflake,
            productPayrollEECounts,
            i,
         )
         applyOneTimeDiscounts(
            discount_list,
            discountStates,
            payrollEINImpProductCode,
            payrollImplementationCosts[i],
            productPayrollEINCount > 1,
            promotionalDiscounts,
            currencyObject,
         )
      }

      // Summarizes all payroll imp costs from payrollImplementationCosts array to payrollImplementationTotalCosts OneTimePrice object
      payrollImplementationTotalCosts.listPrice =
         payrollImplementationCosts.reduce((acc, val) => acc + val.listPrice, 0)
      payrollImplementationTotalCosts.totalDiscount =
         payrollImplementationCosts.reduce(
            (acc, val) => acc + val.totalDiscount,
            0,
         )
      payrollImplementationTotalCosts.totalDiscountPercent =
         payrollImplementationTotalCosts.listPrice === 0
            ? 0
            : (payrollImplementationTotalCosts.totalDiscount /
                 payrollImplementationTotalCosts.listPrice) *
              100
      payrollImplementationTotalCosts.totalPrice =
         payrollImplementationCosts.reduce(
            (acc, val) => acc + val.totalPrice,
            0,
         )
      payrollImplementationTotalCosts.pdfDisplayTotal =
         payrollImplementationCosts.reduce(
            (acc, val) => acc + val.pdfDisplayTotal,
            0,
         )
      payrollImplementationTotalCosts.appliedDiscounts =
         payrollImplementationCosts.reduce(
            (acc, val) => [...acc, ...(val.appliedDiscounts || [])],
            [],
         )

      // Rounds all payroll imp numbers
      roundObjectPropertiesToPrecision(payrollImplementationTotalCosts, 2)
   }

   //////////////////////////////
   // Ben Admin Implementation //

   //Check if ben admin imp is in the pricebook
   addEmptyProductToProductList(product_list, 'benAdminImp')

   // Benefits Administration Implementation Base Cost
   let productBenefitsAdministrationImplementationBase
   try {
      productBenefitsAdministrationImplementationBase = getBasePrice(
         product_list,
         'benAdminImp',
         productBenefitsAdministrationEECount,
      )
   } catch {
      productBenefitsAdministrationImplementationBase = 0
   }

   // Benefits Administration Implementation PEPM Cost
   let productBenefitsAdministrationImplementationPerEmp
   try {
      productBenefitsAdministrationImplementationPerEmp =
         product_list['benAdminImp']['EmpListPrice']
   } catch {
      productBenefitsAdministrationImplementationPerEmp = 0
   }

   // Benefits Administration Implementation Cost
   let benefitsAdministrationImplementationCosts = new OneTimePrice()
   benefitsAdministrationImplementationCosts.productCode = 'benAdminImp'
   if (quoteType !== 'monthly') {
      benefitsAdministrationImplementationCosts =
         calculateBenefitsAdministrationImplementationCost(
            product_list,
            benAdminImpType,
            productBenefitsAdministrationEECount,
            productBenefitsAdministrationImplementationPerEmp,
            productBenefitsAdministrationImplementationBase,
         )
      // Applies all selected and applicable discounts
      applyOneTimeDiscounts(
         discount_list,
         discountStates,
         'benAdminImp',
         benefitsAdministrationImplementationCosts,
         productPayrollEINCount > 1,
         promotionalDiscounts,
         currencyObject,
      )
      // Rounds all ben admin imp costs
      roundObjectPropertiesToPrecision(
         benefitsAdministrationImplementationCosts,
         2,
      )
      benefitsAdministrationImplementationCosts.productEECount = 1

      benefitsAdministrationImplementationCosts.SFPricebookEntryID =
         product_list['benAdminImp']['SFPricebookEntryID']
      benefitsAdministrationImplementationCosts.SFProduct2Id =
         product_list['benAdminImp']['SFProduct2Id']
   }

   ////////////////////////////
   // Historical Data Import //

   // Check if historical data import is in the pricebook
   addEmptyProductToProductList(product_list, 'historicalDataImport')

   // Historical Data Import Base Cost
   let productHistoricalDataImportBase =
      product_list['historicalDataImport']['BasePrice']
   // Historical Data Import One Time Cost
   let HistoricalDataImportCosts = new OneTimePrice()
   HistoricalDataImportCosts.productCode = 'historicalDataImport'
   if (productHistoricalDataImport && quoteType !== 'monthly') {
      HistoricalDataImportCosts.listPrice = productHistoricalDataImportBase
      HistoricalDataImportCosts.totalPrice = productHistoricalDataImportBase
      HistoricalDataImportCosts.pdfDisplayTotal = HistoricalDataImportCosts.totalPrice
   }

   /////////////////////////
   // Marketplace Listing //

   // Check if marketplace listing is in the pricebook
   addEmptyProductToProductList(product_list, 'marketplaceListing')

   // Marketplace Listing Base Cost
   let productMarketplaceListingBase =
      product_list['marketplaceListing']['BasePrice']
   // Marketplace Listing One Time Cost
   let MarketplaceListingCosts = new OneTimePrice()
   MarketplaceListingCosts.productCode = 'marketplaceListing'
   if (productMarketplaceListing && quoteType !== 'monthly') {
      MarketplaceListingCosts.listPrice = productMarketplaceListingBase
      MarketplaceListingCosts.totalPrice = productMarketplaceListingBase
      MarketplaceListingCosts.pdfDisplayTotal = MarketplaceListingCosts.totalPrice
   }

   /////////////////
   // Sponsorship //

   // Check if sponsorship is in the pricebook
   addEmptyProductToProductList(product_list, 'sponsorship')

   // Sponsorship Base Cost
   let productSponsorshipBase = product_list['sponsorship']['BasePrice']
   // Sponsorship One Time Cost
   let SponsorshipCosts = new OneTimePrice()
   SponsorshipCosts.productCode = 'sponsorship'
   if (productSponsorship && quoteType !== 'monthly') {
      SponsorshipCosts.listPrice = productSponsorshipBase
      SponsorshipCosts.totalPrice = productSponsorshipBase
      SponsorshipCosts.pdfDisplayTotal = SponsorshipCosts.totalPrice
   }

   /////////////////////
   // Account Balance //

   // Check if account balance is in the pricebook
   addEmptyProductToProductList(product_list, 'accountBalance')
   if (product_list['accountBalance']?.NotInPricebook) accountBalance = 0

   // Account Balance One Time Cost
   // This price is determined by a number entered directly into the GUI, not from the pricebook
   let AccountBalanceCosts = new OneTimePrice()
   AccountBalanceCosts.productCode = 'accountBalance'
   if (accountBalance != 0 && quoteType !== 'monthly') {
      AccountBalanceCosts.listPrice = parseFloat(accountBalance)
      AccountBalanceCosts.totalPrice = parseFloat(accountBalance)
      AccountBalanceCosts.pdfDisplayTotal = AccountBalanceCosts.totalPrice
   }

   //////////////////////////
   // Professinal Services //

   // Check if Array is null or undefined
   let ProfessionalServicesDetails = professionalServiceDetails ?? []

   let ProfessionalServicesCosts = Array.from(
      { length: ProfessionalServicesDetails.length },
      () => new OneTimePrice(),
   )
   if (ProfessionalServicesDetails.length > 0 && quoteType !== 'monthly') {
      ProfessionalServicesDetails.forEach((service, index) => {
         const totalServiceCount = isNaN(service.count) ? 0 : service.count
         ProfessionalServicesCosts[index].productCode = service.serviceCode
         ProfessionalServicesCosts[index].productEECount = totalServiceCount
         ProfessionalServicesCosts[index].listPrice =
            (product_list[service.serviceCode]
               ? product_list[service.serviceCode]['BasePrice']
               : 0) * totalServiceCount
         ProfessionalServicesCosts[index].totalPrice =
            (product_list[service.serviceCode]
               ? product_list[service.serviceCode]['BasePrice']
               : 0) * totalServiceCount
         ProfessionalServicesCosts[index].pdfDisplayTotal = ProfessionalServicesCosts[index].totalPrice
         ProfessionalServicesCosts[index].SFPricebookEntryID =
            product_list[service.serviceCode]?.SFPricebookEntryID
         ProfessionalServicesCosts[index].SFProduct2Id =
            product_list[service.serviceCode]?.SFProduct2Id
         applyOneTimeDiscounts(
            discount_list,
            discountStates,
            service.serviceCode,
            ProfessionalServicesCosts[index],
            productPayrollEINCount > 1,
            promotionalDiscounts,
            currencyObject,
         )
      })
   }
   /////////////////////////
   // Consulting Services //

   let ConsultingServicesDetails = consultingServiceDetails ?? []

   let ConsultingServicesCosts = Array.from(
      { length: ConsultingServicesDetails.length },
      () => new OneTimePrice(),
   )
   if (ConsultingServicesDetails.length > 0 && quoteType !== 'monthly') {
      ConsultingServicesDetails.forEach((service, index) => {
         const totalServiceCount = isNaN(service.count) ? 0 : service.count
         ConsultingServicesCosts[index].productCode = service.serviceCode
         ConsultingServicesCosts[index].productEECount = totalServiceCount
         ConsultingServicesCosts[index].listPrice =
            (product_list[service.serviceCode]
               ? product_list[service.serviceCode]['BasePrice']
               : 0) * totalServiceCount
         ConsultingServicesCosts[index].totalPrice =
            (product_list[service.serviceCode]
               ? product_list[service.serviceCode]['BasePrice']
               : 0) * totalServiceCount
         ConsultingServicesCosts[index].pdfDisplayTotal = ConsultingServicesCosts[index].totalPrice
         ConsultingServicesCosts[index].SFPricebookEntryID =
            product_list[service.serviceCode]?.SFPricebookEntryID
         ConsultingServicesCosts[index].SFProduct2Id =
            product_list[service.serviceCode]?.SFProduct2Id
         applyOneTimeDiscounts(
            discount_list,
            discountStates,
            service.serviceCode,
            ConsultingServicesCosts[index],
            productPayrollEINCount > 1,
            promotionalDiscounts,
            currencyObject,
         )
      })
   }

   //////////////////
   // Paid Service //

   // Check if paid service is in the pricebook
   addEmptyProductToProductList(product_list, 'paidService')
   if (product_list['paidService']?.NotInPricebook) productPaidServiceAmount = 0

   // Paid Service One Time Cost
   // This price is determined by a number entered directly into the GUI, not from the pricebook
   let PaidServiceCosts = new OneTimePrice()
   // Intentionally omit SF IDs so no generic Professional Consulting product is pushed to SF
   PaidServiceCosts.productCode = 'paidService'
   if (
      quoteType !== 'monthly' &&
      (productPaidServiceAmount > 0 ||
         ConsultingServicesCosts.length > 0 ||
         ProfessionalServicesCosts.length > 0)
   ) {
      PaidServiceCosts.listPrice =
         parseFloat(productPaidServiceAmount) +
         ConsultingServicesCosts.reduce((acc, cur) => acc + cur.listPrice, 0) +
         ProfessionalServicesCosts.reduce((acc, cur) => acc + cur.listPrice, 0)
      PaidServiceCosts.totalPrice =
         parseFloat(productPaidServiceAmount) +
         ConsultingServicesCosts.reduce((acc, cur) => acc + cur.totalPrice, 0) +
         ProfessionalServicesCosts.reduce((acc, cur) => acc + cur.totalPrice, 0)
      PaidServiceCosts.pdfDisplayTotal = PaidServiceCosts.totalPrice
   }
   //Dynamic Products
   const headcountMap = {
      core1: coreProductSelected === 'core1' ? productCoreEECount : 0,
      core2: coreProductSelected === 'core2' ? productCoreEECount : 0,
      core3: coreProductSelected === 'core3' ? productCoreEECount : 0,
      benAdmin: productBenefitsAdministrationEECount,
      timeTracking: productTimeTrackingEECount,
      timeAttendance: productTimeAttendanceEECount,
      payroll: productPayrollEECount,
      addJobPosting: productAdditionalJobsCount,
      payrollStates: productPayrollTotalStateCount,
   }
   const { dynamicProductCosts, dynamicMonthlyTotal, dynamicOneTimeTotal, alwaysUSDMonthlyTotal, alwaysUSDOneTimeTotal } =
      calculateDynamicProducts(
         product_list,
         discount_list,
         discountStates,
         promotionalDiscounts,
         currencyObject,
         pricebookDetails?.agentExchangeRate,
         dynamicProducts,
         quoteType,
         user_role,
         allPermissions,
         headcountMap,
      )

   // Populate proratedPrice/proratedTotal on each monthly-billed, prorate-eligible product
   // ahead of the summarized totals below, so the prorated first-month amounts roll up
   // into hrMonthlyTotal and monthlyTotal instead of only being reflected in
   // proratedActivationDetails.totalPrice.
   for (const monthlyCost of [
      productCoreMonthlyCosts,
      productPerformanceMonthlyCosts,
      productTimeTrackingMonthlyCosts,
      productTimeAttendanceMonthlyCosts,
      productBenefitsAdministrationMonthlyCosts,
      AdditionalJobsCosts,
      AdditionalStorageCosts,
      TestAccountCosts,
      MonthlyTaxCosts,
   ]) {
      applyProrationToMonthlyPrice(
         monthlyCost,
         product_list,
         activationDate,
         nextInvoiceDate,
         differenceQuote,
         account_status,
      )
   }

   /******************************/
   // Summarized Monthly Charges //
   /******************************/

   // Total HR Monthly Costs
   // This includes core and all addons (performance, time tracking OR time attendance, ben admin)
   // Time tracking and time attendance are mutually exclusive; whichever isn't selected is zeroed.
   let hrMonthlyTotal = new MonthlyPrice()
   hrMonthlyTotal.listPrice =
      productCoreMonthlyCosts.listPrice +
      productPerformanceMonthlyCosts.listPrice +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.listPrice : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.listPrice : 0) +
      productBenefitsAdministrationMonthlyCosts.listPrice
   hrMonthlyTotal.listPEPMCost =
      productCoreMonthlyCosts.listPEPMCost +
      productPerformanceMonthlyCosts.listPEPMCost +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.listPEPMCost : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.listPEPMCost : 0) +
      productBenefitsAdministrationMonthlyCosts.listPEPMCost
   hrMonthlyTotal.discountedPEPMCost =
      productCoreMonthlyCosts.discountedPEPMCost +
      productPerformanceMonthlyCosts.discountedPEPMCost +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.discountedPEPMCost : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.discountedPEPMCost : 0) +
      productBenefitsAdministrationMonthlyCosts.discountedPEPMCost
   hrMonthlyTotal.totalPEPMDiscount =
      productCoreMonthlyCosts.totalPEPMDiscount +
      productPerformanceMonthlyCosts.totalPEPMDiscount +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.totalPEPMDiscount : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.totalPEPMDiscount : 0) +
      productBenefitsAdministrationMonthlyCosts.totalPEPMDiscount
   hrMonthlyTotal.totalDiscountPercent =
      hrMonthlyTotal.listPrice !== 0
         ? ((productCoreMonthlyCosts.totalDiscount +
              productPerformanceMonthlyCosts.totalDiscount +
              (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.totalDiscount : 0) +
              (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.totalDiscount : 0) +
              productBenefitsAdministrationMonthlyCosts.totalDiscount) /
              hrMonthlyTotal.listPrice) *
           100
         : 0
   hrMonthlyTotal.pdfDisplayTotalDiscountPercent =
      hrMonthlyTotal.listPrice !== 0
         ? ((productCoreMonthlyCosts.pdfDisplayTotalDiscount +
              productPerformanceMonthlyCosts.pdfDisplayTotalDiscount +
              (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.pdfDisplayTotalDiscount : 0) +
              (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.pdfDisplayTotalDiscount : 0) +
              productBenefitsAdministrationMonthlyCosts.pdfDisplayTotalDiscount) /
              hrMonthlyTotal.listPrice) *
           100
         : 0
   hrMonthlyTotal.totalDiscount =
      productCoreMonthlyCosts.totalDiscount +
      productPerformanceMonthlyCosts.totalDiscount +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.totalDiscount : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.totalDiscount : 0) +
      productBenefitsAdministrationMonthlyCosts.totalDiscount
   hrMonthlyTotal.pdfDisplayTotalDiscount =
      productCoreMonthlyCosts.pdfDisplayTotalDiscount +
      productPerformanceMonthlyCosts.pdfDisplayTotalDiscount +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.pdfDisplayTotalDiscount : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.pdfDisplayTotalDiscount : 0) +
      productBenefitsAdministrationMonthlyCosts.pdfDisplayTotalDiscount
   hrMonthlyTotal.totalPrice =
      productCoreMonthlyCosts.totalPrice +
      productPerformanceMonthlyCosts.totalPrice +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.totalPrice : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.totalPrice : 0) +
      productBenefitsAdministrationMonthlyCosts.totalPrice

   hrMonthlyTotal.proratedPrice =
      productCoreMonthlyCosts.proratedPrice +
      productPerformanceMonthlyCosts.proratedPrice +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.proratedPrice : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.proratedPrice : 0) +
      productBenefitsAdministrationMonthlyCosts.proratedPrice
   hrMonthlyTotal.proratedTotal =
      productCoreMonthlyCosts.proratedTotal +
      productPerformanceMonthlyCosts.proratedTotal +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.proratedTotal : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.proratedTotal : 0) +
      productBenefitsAdministrationMonthlyCosts.proratedTotal

   hrMonthlyTotal.pdfDisplayTotal =
      productCoreMonthlyCosts.pdfDisplayTotal +
      productPerformanceMonthlyCosts.pdfDisplayTotal +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.pdfDisplayTotal : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.pdfDisplayTotal : 0) +
      productBenefitsAdministrationMonthlyCosts.pdfDisplayTotal

   hrMonthlyTotal.MRRTotal =
      productCoreMonthlyCosts.MRRTotal +
      productPerformanceMonthlyCosts.MRRTotal +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.MRRTotal : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.MRRTotal : 0) +
      productBenefitsAdministrationMonthlyCosts.MRRTotal
   hrMonthlyTotal.listMRRTotal =
      productCoreMonthlyCosts.listMRRTotal +
      productPerformanceMonthlyCosts.listMRRTotal +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.listMRRTotal : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.listMRRTotal : 0) +
      productBenefitsAdministrationMonthlyCosts.listMRRTotal

   hrMonthlyTotal.previousMRRTotal =
      productCoreMonthlyCosts.previousMRRTotal +
      productPerformanceMonthlyCosts.previousMRRTotal +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.previousMRRTotal : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.previousMRRTotal : 0) +
      productBenefitsAdministrationMonthlyCosts.previousMRRTotal
   hrMonthlyTotal.previousTotalPrice =
      productCoreMonthlyCosts.previousTotalPrice +
      productPerformanceMonthlyCosts.previousTotalPrice +
      (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.previousTotalPrice : 0) +
      (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.previousTotalPrice : 0) +
      productBenefitsAdministrationMonthlyCosts.previousTotalPrice
   let totalHRDifference = 0
   totalHRDifference += productCoreMonthlyCosts.totalPriceDifference
   totalHRDifference += productPerformanceMonthlyCosts.totalPriceDifference
   totalHRDifference += (selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.totalPriceDifference : 0)
   totalHRDifference += (selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.totalPriceDifference : 0)
   totalHRDifference +=
      productBenefitsAdministrationMonthlyCosts.totalPriceDifference
   hrMonthlyTotal.totalPriceDifference = totalHRDifference
   hrMonthlyTotal.appliedDiscounts = [
      ...productCoreMonthlyCosts.appliedDiscounts,
      ...productPerformanceMonthlyCosts.appliedDiscounts,
      ...(selectedTimeProduct === 'timeTracking' ? productTimeTrackingMonthlyCosts.appliedDiscounts : []),
      ...(selectedTimeProduct === 'timeAttendance' ? productTimeAttendanceMonthlyCosts.appliedDiscounts : []),
      ...productBenefitsAdministrationMonthlyCosts.appliedDiscounts,
   ]

   hrMonthlyTotal.pdfDisplayDifferenceTotal = hrMonthlyTotal.totalPriceDifference
   // Rounds all numbers
   roundObjectPropertiesToPrecision(hrMonthlyTotal, 2)

   // Total Payroll Monthly Costs
   // Included monthly cost for extra states as well as total cost of all multiple EINs, and integrations if applicable. All products billed through Payroll
   let payrollMonthlyTotal = new MonthlyPrice()
   payrollMonthlyTotal.productCode = 'payrollMonthlyTotal'
   payrollMonthlyTotal.SFPricebookEntryID =
      product_list?.payroll?.SFPricebookEntryID
   payrollMonthlyTotal.SFProduct2Id = product_list?.payroll?.SFProduct2Id
   payrollMonthlyTotal.listPrice =
      (productPayrollMonthlyTotalCosts.listPrice || 0) +
      (productPayrollMonthlyStateTotalCosts.listPrice || 0) +
      (productEINIntegrationsTotalCosts.listPrice || 0)
   payrollMonthlyTotal.productEECount = productPayrollEECount
   payrollMonthlyTotal.listPEPMCost =
      productPayrollMonthlyTotalCosts.listPEPMCost || 0
   payrollMonthlyTotal.discountedPEPMCost =
      productPayrollMonthlyTotalCosts.discountedPEPMCost || 0
   payrollMonthlyTotal.pdfDisplayDiscountedPEPMCost =
      productPayrollMonthlyTotalCosts.pdfDisplayDiscountedPEPMCost || 0
   payrollMonthlyTotal.totalPEPMDiscount =
      productPayrollMonthlyTotalCosts.totalPEPMDiscount || 0
   payrollMonthlyTotal.totalDiscountPercent =
      ((productPayrollMonthlyTotalCosts.totalDiscount || 0) /
         (productPayrollMonthlyTotalCosts.listPrice || 1)) *
      100

   // Payroll products difference calculation no longer using calcualteDifference function as it now uses a complex data field from Snowflake.
   payrollMonthlyTotal.totalPriceDifference =
      (productPayrollMonthlyTotalCosts.totalPriceDifference || 0) +
      (productPayrollMonthlyStateTotalCosts.totalPriceDifference || 0) +
      (productEINIntegrationsTotalCosts.totalPriceDifference || 0)
   payrollMonthlyTotal.pdfDisplayDifferenceTotal = payrollMonthlyTotal.totalPriceDifference
   let totalPayrollDifference = 0
   totalPayrollDifference += productPayrollMonthlyTotalCosts.previousTotalPrice
   totalPayrollDifference +=
      productPayrollMonthlyStateTotalCosts.previousTotalPrice
   totalPayrollDifference += productEINIntegrationsTotalCosts.previousTotalPrice
   payrollMonthlyTotal.previousTotalPrice = totalPayrollDifference
   payrollMonthlyTotal.previousTotalPriceUnconverted =
      payrollMonthlyTotal.previousTotalPrice
   payrollMonthlyTotal.MRRTotal =
      (productPayrollMonthlyTotalCosts.MRRTotal || 0) +
      (productPayrollMonthlyStateTotalCosts.MRRTotal || 0) +
      (productEINIntegrationsTotalCosts.MRRTotal || 0)
   payrollMonthlyTotal.listMRRTotal =
      (productPayrollMonthlyTotalCosts.listMRRTotal || 0) +
      (productPayrollMonthlyStateTotalCosts.listMRRTotal || 0) +
      (productEINIntegrationsTotalCosts.listMRRTotal || 0)
   payrollMonthlyTotal.previousMRRTotal =
      (productPayrollMonthlyTotalCosts.previousMRRTotal || 0) +
      (productPayrollMonthlyStateTotalCosts.previousMRRTotal || 0) +
      (productEINIntegrationsTotalCosts.previousMRRTotal || 0)

   payrollMonthlyTotal.totalDiscount =
      (productPayrollMonthlyTotalCosts.totalDiscount || 0)
   payrollMonthlyTotal.pdfDisplayTotalDiscount =
      (productPayrollMonthlyTotalCosts.pdfDisplayTotalDiscount || 0)
   payrollMonthlyTotal.totalPrice =
      (productPayrollMonthlyTotalCosts.totalPrice || 0) +
      (productPayrollMonthlyStateTotalCosts.totalPrice || 0) +
      (productEINIntegrationsTotalCosts.totalPrice || 0)
   payrollMonthlyTotal.pdfDisplayTotal =
      (productPayrollMonthlyTotalCosts.pdfDisplayTotal || 0) +
      (productPayrollMonthlyStateTotalCosts.pdfDisplayTotal || 0) +
      (productEINIntegrationsTotalCosts.pdfDisplayTotal || 0)

   // Rounds all numbers
   roundObjectPropertiesToPrecision(payrollMonthlyTotal, 2)
   payrollMonthlyTotal.appliedDiscounts = [
      ...productPayrollMonthlyTotalCosts.appliedDiscounts,
      ...productPayrollMonthlyStateTotalCosts.appliedDiscounts,
      ...productEINIntegrationsTotalCosts.appliedDiscounts,
   ]

   // Payroll Annual Total - annualized payroll costs for display in the totals section
   // Uses calculatePayrollAnnualTotal to account for time-limited discounts (e.g. 2 months at 50% off)
   let payrollAnnualTotal = new MonthlyPrice()
   payrollAnnualTotal.productCode = 'payrollAnnualTotal'
   payrollAnnualTotal.listPrice = payrollMonthlyTotal.listPrice * 12
   if (!isRecalculation) {
      payrollAnnualTotal.totalPrice = calculatePayrollAnnualTotal(
         payrollMonthlyTotal,
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate,
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         isRecalculation,
      )
   }
   roundObjectPropertiesToPrecision(payrollAnnualTotal, 2)

   // Combined Total HR and Payroll Monthly Costs
   // Includes core, its addons, and all payroll costs, payroll states, and multiple EINs if applicable
   let monthlyTotal = new MonthlyPrice()
   monthlyTotal.productCode = 'monthlyTotal'
   // Ensure each component is a valid number with '|| 0'
   monthlyTotal.listPrice =
      (hrMonthlyTotal.listPrice || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.listPrice || 0) + (alwaysUSDMonthlyTotal.listPrice || 0)
         : 0) +
      (AdditionalStorageCosts.listPrice || 0) +
      (AdditionalJobsCosts.listPrice || 0) +
      (TestAccountCosts.listPrice || 0) +
      (MonthlyTaxCosts.listPrice || 0) +
      (TimeClockMonthlyCost.listPrice || 0) +
      (dynamicMonthlyTotal.listPrice || 0)

   monthlyTotal.listPEPMCost =
      (hrMonthlyTotal.listPEPMCost || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.listPEPMCost || 0) + (alwaysUSDMonthlyTotal.listPEPMCost || 0)
         : 0) +
      (dynamicMonthlyTotal.listPEPMCost || 0)

   monthlyTotal.discountedPEPMCost =
      (hrMonthlyTotal.discountedPEPMCost || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.discountedPEPMCost || 0) + (alwaysUSDMonthlyTotal.discountedPEPMCost || 0)
         : 0) +
      (dynamicMonthlyTotal.discountedPEPMCost || 0)

   monthlyTotal.totalPEPMDiscount =
      (hrMonthlyTotal.totalPEPMDiscount || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.totalPEPMDiscount || 0) + (alwaysUSDMonthlyTotal.totalPEPMDiscount || 0)
         : 0) +
      (dynamicMonthlyTotal.totalPEPMDiscount || 0)

   // Make sure discounts are calculated correctly
   monthlyTotal.totalDiscountPercent =
      (((hrMonthlyTotal.totalDiscount || 0) +
         (!IsForeignCurrencyQuote(book_id)
            ? (payrollMonthlyTotal.totalDiscount || 0) + (alwaysUSDMonthlyTotal.totalDiscount || 0)
            : 0) +
         (AdditionalStorageCosts.totalDiscount || 0) +
         (AdditionalJobsCosts.totalDiscount || 0) +
         (TestAccountCosts.totalDiscount || 0) +
         (MonthlyTaxCosts.totalDiscount || 0) +
         (dynamicMonthlyTotal.totalDiscount || 0)) /
         (monthlyTotal.listPrice || 1)) *
      100

   monthlyTotal.pdfDisplayTotalDiscountPercent =
      (((hrMonthlyTotal.pdfDisplayTotalDiscount || 0) +
         (!IsForeignCurrencyQuote(book_id)
            ? (payrollMonthlyTotal.pdfDisplayTotalDiscount || 0) + (alwaysUSDMonthlyTotal.pdfDisplayTotalDiscount || 0)
            : 0) +
         (AdditionalStorageCosts.pdfDisplayTotalDiscount || 0) +
         (AdditionalJobsCosts.pdfDisplayTotalDiscount || 0) +
         (TestAccountCosts.pdfDisplayTotalDiscount || 0) +
         (MonthlyTaxCosts.pdfDisplayTotalDiscount || 0) +
         (dynamicMonthlyTotal.pdfDisplayTotalDiscount || 0)) /
         (monthlyTotal.listPrice || 1)) *
      100
   monthlyTotal.totalDiscount =
      (hrMonthlyTotal.totalDiscount || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.totalDiscount || 0) + (alwaysUSDMonthlyTotal.totalDiscount || 0)
         : 0) +
      (AdditionalStorageCosts.totalDiscount || 0) +
      (AdditionalJobsCosts.totalDiscount || 0) +
      (TestAccountCosts.totalDiscount || 0) +
      (MonthlyTaxCosts.totalDiscount || 0) +
      (dynamicMonthlyTotal.totalDiscount || 0)

   monthlyTotal.pdfDisplayTotalDiscount =
      (hrMonthlyTotal.pdfDisplayTotalDiscount || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.pdfDisplayTotalDiscount || 0) + (alwaysUSDMonthlyTotal.pdfDisplayTotalDiscount || 0)
         : 0) +
      (AdditionalStorageCosts.pdfDisplayTotalDiscount || 0) +
      (AdditionalJobsCosts.pdfDisplayTotalDiscount || 0) +
      (TestAccountCosts.pdfDisplayTotalDiscount || 0) +
      (MonthlyTaxCosts.pdfDisplayTotalDiscount || 0) +
      (dynamicMonthlyTotal.pdfDisplayTotalDiscount || 0)
   monthlyTotal.totalPrice =
      (hrMonthlyTotal.totalPrice || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.totalPrice || 0) + (alwaysUSDMonthlyTotal.totalPrice || 0)
         : 0) +
      (AdditionalStorageCosts.totalPrice || 0) +
      (AdditionalJobsCosts.totalPrice || 0) +
      (TestAccountCosts.totalPrice || 0) +
      (MonthlyTaxCosts.totalPrice || 0) +
      (TimeClockMonthlyCost.totalPrice || 0) +
      (dynamicMonthlyTotal.totalPrice || 0)

   monthlyTotal.proratedPrice =
      (hrMonthlyTotal.proratedPrice || 0) +
      (AdditionalStorageCosts.proratedPrice || 0) +
      (AdditionalJobsCosts.proratedPrice || 0) +
      (TestAccountCosts.proratedPrice || 0) +
      (MonthlyTaxCosts.proratedPrice || 0)
   monthlyTotal.proratedTotal =
      (hrMonthlyTotal.proratedTotal || 0) +
      (AdditionalStorageCosts.proratedTotal || 0) +
      (AdditionalJobsCosts.proratedTotal || 0) +
      (TestAccountCosts.proratedTotal || 0) +
      (MonthlyTaxCosts.proratedTotal || 0)

   monthlyTotal.pdfDisplayTotal =
      (hrMonthlyTotal.pdfDisplayTotal || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.pdfDisplayTotal || 0) + (alwaysUSDMonthlyTotal.pdfDisplayTotal || 0)
         : 0) +
      (AdditionalStorageCosts.pdfDisplayTotal || 0) +
      (AdditionalJobsCosts.pdfDisplayTotal || 0) +
      (TestAccountCosts.pdfDisplayTotal || 0) +
      (MonthlyTaxCosts.pdfDisplayTotal || 0) +
      (TimeClockMonthlyCost.pdfDisplayTotal || 0) +
      (dynamicMonthlyTotal.pdfDisplayTotal || 0)

   monthlyTotal.MRRTotal =
      (hrMonthlyTotal.MRRTotal || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.MRRTotal || 0) + (alwaysUSDMonthlyTotal.MRRTotal || 0)
         : 0) +
      (AdditionalStorageCosts.MRRTotal || 0) +
      (AdditionalJobsCosts.MRRTotal || 0) +
      (TestAccountCosts.MRRTotal || 0) +
      (TimeClockMonthlyCost.MRRTotal || 0) +
      (dynamicMonthlyTotal.MRRTotal || 0)

   monthlyTotal.listMRRTotal =
      (hrMonthlyTotal.listMRRTotal || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.listMRRTotal || 0) + (alwaysUSDMonthlyTotal.listMRRTotal || 0)
         : 0) +
      (AdditionalStorageCosts.listMRRTotal || 0) +
      (AdditionalJobsCosts.listMRRTotal || 0) +
      (TestAccountCosts.listMRRTotal || 0) +
      (TimeClockMonthlyCost.listMRRTotal || 0) +
      (dynamicMonthlyTotal.listMRRTotal || 0)

   monthlyTotal.previousMRRTotal =
      (hrMonthlyTotal.previousMRRTotal || 0) +
      (!IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.previousMRRTotal || 0) + (alwaysUSDMonthlyTotal.previousMRRTotal || 0)
         : 0) +
      (AdditionalStorageCosts.previousMRRTotal || 0) +
      (AdditionalJobsCosts.previousMRRTotal || 0) +
      (TestAccountCosts.previousMRRTotal || 0) +
      (TimeClockMonthlyCost.previousMRRTotal || 0) +
      (dynamicMonthlyTotal.previousMRRTotal || 0)

   // For difference quotes, handle the totalPriceDifference properly
   if (differenceQuote) {
      monthlyTotal.previousTotalPrice =
         (hrMonthlyTotal.previousTotalPrice || 0) +
         (!IsForeignCurrencyQuote(book_id)
            ? (payrollMonthlyTotal.previousTotalPrice || 0) + (alwaysUSDMonthlyTotal.previousTotalPrice || 0)
            : 0) +
         (AdditionalStorageCosts.previousTotalPrice || 0) +
         (AdditionalJobsCosts.previousTotalPrice || 0) +
         (TestAccountCosts.previousTotalPrice || 0) +
         (MonthlyTaxCosts.previousTotalPrice || 0) +
         (dynamicMonthlyTotal.previousTotalPrice || 0)

      let monthlyTotalDifference = 0
      monthlyTotalDifference += hrMonthlyTotal.totalPriceDifference || 0
      monthlyTotalDifference += !IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.totalPriceDifference || 0) + (alwaysUSDMonthlyTotal.totalPriceDifference || 0)
         : 0
      monthlyTotalDifference += AdditionalJobsCosts.totalPriceDifference || 0
      monthlyTotalDifference += AdditionalStorageCosts.totalPriceDifference || 0
      monthlyTotalDifference += TestAccountCosts.totalPriceDifference || 0
      monthlyTotalDifference += MonthlyTaxCosts.totalPriceDifference || 0
      monthlyTotalDifference += dynamicMonthlyTotal.totalPriceDifference || 0

      // For difference quotes, handle the pdfDisplayDifferenceTotal properly
      let pdfDisplayMonthlyTotalDifference = 0
      pdfDisplayMonthlyTotalDifference += hrMonthlyTotal.pdfDisplayDifferenceTotal || 0
      pdfDisplayMonthlyTotalDifference += !IsForeignCurrencyQuote(book_id)
         ? (payrollMonthlyTotal.pdfDisplayDifferenceTotal || 0) + (alwaysUSDMonthlyTotal.pdfDisplayDifferenceTotal || 0)
         : 0
      pdfDisplayMonthlyTotalDifference += AdditionalJobsCosts.pdfDisplayDifferenceTotal || 0
      pdfDisplayMonthlyTotalDifference += AdditionalStorageCosts.pdfDisplayDifferenceTotal || 0
      pdfDisplayMonthlyTotalDifference += TestAccountCosts.pdfDisplayDifferenceTotal || 0
      pdfDisplayMonthlyTotalDifference += MonthlyTaxCosts.pdfDisplayDifferenceTotal || 0
      pdfDisplayMonthlyTotalDifference += dynamicMonthlyTotal.pdfDisplayDifferenceTotal || 0

      if (
         TimeClockMonthlyCost.hasOwnProperty('totalPriceDifference') &&
         TimeClockMonthlyCost.totalPriceDifference !== null &&
         !isNaN(TimeClockMonthlyCost.totalPriceDifference)
      ) {
         monthlyTotalDifference += TimeClockMonthlyCost.totalPriceDifference
      }

      if (
         TimeClockMonthlyCost.hasOwnProperty('totalPriceDifference') &&
         TimeClockMonthlyCost.pdfDisplayDifferenceTotal !== null &&
         !isNaN(TimeClockMonthlyCost.pdfDisplayDifferenceTotal)
      ) {
         pdfDisplayMonthlyTotalDifference += TimeClockMonthlyCost.pdfDisplayDifferenceTotal
      }

      monthlyTotal.totalPriceDifference = monthlyTotalDifference
      monthlyTotal.pdfDisplayDifferenceTotal = pdfDisplayMonthlyTotalDifference
   }

   monthlyTotal.appliedDiscounts = [
      ...hrMonthlyTotal.appliedDiscounts,
      ...payrollMonthlyTotal.appliedDiscounts,
      ...(!IsForeignCurrencyQuote(book_id) ? alwaysUSDMonthlyTotal.appliedDiscounts : []),
      ...AdditionalStorageCosts.appliedDiscounts,
      ...AdditionalJobsCosts.appliedDiscounts,
      ...TestAccountCosts.appliedDiscounts,
      ...MonthlyTaxCosts.appliedDiscounts,
      ...dynamicMonthlyTotal.appliedDiscounts,
   ]
   // Applies discounts to the total monthly costs
   applyMonthlyDiscounts(
      discount_list,
      discountStates,
      'monthlyTotal',
      monthlyTotal,
      productCoreEECount,
      productBenefitsAdministrationEECount,
      productPayrollEECount,
      discount_list['bundleDiscount']['DiscountAmount'],
      promotionalDiscounts,
      currencyObject,
      user_role,
      allPermissions,
   )
   // Rounds all numbers
   roundObjectPropertiesToPrecision(monthlyTotal, 2)

   /*******************************/
   // Summarized One Time Charges //
   /*******************************/

   // Total implementation costs for all implementations, core, payroll, and ben admin
   let totalImplementationCosts = new OneTimePrice()
   totalImplementationCosts.listPrice =
      hrImplementationCosts.listPrice +
      payrollImplementationTotalCosts.listPrice +
      benefitsAdministrationImplementationCosts.listPrice
   totalImplementationCosts.totalDiscount =
      hrImplementationCosts.totalDiscount +
      payrollImplementationTotalCosts.totalDiscount +
      benefitsAdministrationImplementationCosts.totalDiscount
   totalImplementationCosts.totalDiscountPercent =
      totalImplementationCosts.listPrice !== 0
         ? ((hrImplementationCosts.totalDiscount +
              payrollImplementationTotalCosts.totalDiscount +
              benefitsAdministrationImplementationCosts.totalDiscount) /
              totalImplementationCosts.listPrice) *
           100
         : 0
   totalImplementationCosts.totalPrice =
      hrImplementationCosts.totalPrice +
      payrollImplementationTotalCosts.totalPrice +
      benefitsAdministrationImplementationCosts.totalPrice
   // Rounds all numbers
   roundObjectPropertiesToPrecision(totalImplementationCosts, 2)

   // One Time Total Charge
   // Inclusive of all implementation and other one time charges
   const servicesTotalPrice =
      ProfessionalServicesCosts.reduce((acc, val) => acc + val.totalPrice, 0) +
      ConsultingServicesCosts.reduce((acc, val) => acc + val.totalPrice, 0)
   const servicesListPrice =
      ProfessionalServicesCosts.reduce((acc, val) => acc + val.listPrice, 0) +
      ConsultingServicesCosts.reduce((acc, val) => acc + val.listPrice, 0)

   let oneTimeTotal = new OneTimePrice()
   oneTimeTotal.productCode = 'oneTimeTotal'
   oneTimeTotal.listPrice =
      hrImplementationCosts.listPrice +
      payrollImplementationTotalCosts.listPrice +
      benefitsAdministrationImplementationCosts.listPrice +
      HistoricalDataImportCosts.listPrice +
      MarketplaceListingCosts.listPrice +
      SponsorshipCosts.listPrice +
      AccountBalanceCosts.listPrice +
      TimeClockCosts.reduce((acc, val) => acc + val.listPrice, 0) +
      ProfessionalServicesCosts.reduce((acc, val) => acc + val.listPrice, 0) +
      ConsultingServicesCosts.reduce((acc, val) => acc + val.listPrice, 0) +
      dynamicOneTimeTotal.listPrice +
      (!IsForeignCurrencyQuote(book_id) ? alwaysUSDOneTimeTotal.listPrice || 0 : 0)
   oneTimeTotal.totalDiscount =
      hrImplementationCosts.totalDiscount +
      payrollImplementationTotalCosts.totalDiscount +
      benefitsAdministrationImplementationCosts.totalDiscount +
      HistoricalDataImportCosts.totalDiscount +
      MarketplaceListingCosts.totalDiscount +
      SponsorshipCosts.totalDiscount +
      PaidServiceCosts.totalDiscount +
      AccountBalanceCosts.totalDiscount +
      TimeClockCosts.reduce((acc, val) => acc + val.totalDiscount, 0) +
      ProfessionalServicesCosts.reduce(
         (acc, val) => acc + val.totalDiscount,
         0,
      ) +
      ConsultingServicesCosts.reduce((acc, val) => acc + val.totalDiscount, 0) +
      dynamicOneTimeTotal.totalDiscount +
      (!IsForeignCurrencyQuote(book_id) ? alwaysUSDOneTimeTotal.totalDiscount || 0 : 0)
   oneTimeTotal.totalDiscountPercent =
      oneTimeTotal.listPrice !== 0
         ? ((hrImplementationCosts.totalDiscount +
              payrollImplementationTotalCosts.totalDiscount +
              benefitsAdministrationImplementationCosts.totalDiscount +
              HistoricalDataImportCosts.totalDiscount +
              MarketplaceListingCosts.totalDiscount +
              SponsorshipCosts.totalDiscount +
              PaidServiceCosts.totalDiscount +
              AccountBalanceCosts.totalDiscount +
              TimeClockCosts.reduce((acc, val) => acc + val.totalDiscount, 0) +
              ProfessionalServicesCosts.reduce(
                 (acc, val) => acc + val.totalDiscount,
                 0,
              ) +
              ConsultingServicesCosts.reduce(
                 (acc, val) => acc + val.totalDiscount,
                 0,
              ) +
              dynamicOneTimeTotal.totalDiscount +
              (!IsForeignCurrencyQuote(book_id)
                 ? alwaysUSDOneTimeTotal.totalDiscount || 0
                 : 0)) /
              oneTimeTotal.listPrice) *
           100
         : 0
   oneTimeTotal.totalPrice =
      hrImplementationCosts.totalPrice +
      payrollImplementationTotalCosts.totalPrice +
      benefitsAdministrationImplementationCosts.totalPrice +
      HistoricalDataImportCosts.totalPrice +
      MarketplaceListingCosts.totalPrice +
      SponsorshipCosts.totalPrice +
      (PaidServiceCosts.totalPrice - servicesTotalPrice) +
      AccountBalanceCosts.totalPrice +
      TimeClockCosts.reduce((acc, val) => acc + val.totalPrice, 0) +
      ProfessionalServicesCosts.reduce((acc, val) => acc + val.totalPrice, 0) +
      ConsultingServicesCosts.reduce((acc, val) => acc + val.totalPrice, 0) +
      dynamicOneTimeTotal.totalPrice +
      (!IsForeignCurrencyQuote(book_id) ? alwaysUSDOneTimeTotal.totalPrice || 0 : 0)
   oneTimeTotal.appliedDiscounts = [
      ...hrImplementationCosts.appliedDiscounts,
      ...payrollImplementationTotalCosts.appliedDiscounts,
      ...benefitsAdministrationImplementationCosts.appliedDiscounts,
      ...HistoricalDataImportCosts.appliedDiscounts,
      ...MarketplaceListingCosts.appliedDiscounts,
      ...SponsorshipCosts.appliedDiscounts,
      ...AccountBalanceCosts.appliedDiscounts,
      // Add applied discounts for Arrays of Products
      ...(TimeClockCosts.length > 0
         ? TimeClockCosts.reduce((acc, val) => acc + val.appliedDiscounts, 0)
         : []),
      ...(ProfessionalServicesCosts.length > 0
         ? ProfessionalServicesCosts.reduce(
              (acc, val) => acc + val.appliedDiscounts,
              0,
           )
         : []),
      ...(ConsultingServicesCosts.length > 0
         ? ConsultingServicesCosts.reduce(
              (acc, val) => acc + val.appliedDiscounts,
              0,
           )
         : []),
      ...dynamicOneTimeTotal.appliedDiscounts,
      ...(!IsForeignCurrencyQuote(book_id) ? alwaysUSDOneTimeTotal.appliedDiscounts : []),
   ]
   // Applies discounts to the total one time costs
   applyOneTimeDiscounts(
      discount_list,
      discountStates,
      'oneTimeTotal',
      oneTimeTotal,
      productPayrollEINCount > 1,
      promotionalDiscounts,
      currencyObject,
   )
   // Rounds all numbers
   roundObjectPropertiesToPrecision(oneTimeTotal, 2)

   /***************************/
   // Other Calculated Values //
   /***************************/
   //Sets Implementation to null if core0 (None) is selected
   let hrImplementationType = 'selfSetup'
   if (coreProductSelected !== 'core0') {
      // Get the string productCode for the Imp type
      hrImplementationType = determineHRImplementationType(
         impType,
         productCoreEECount,
         productQuickstartImplementation,
         productNormalImplementation,
         productPremierImplementation,
      )
   }

   // Create a hashmap/dictionary of productcodes to costs
   const selectedProductCosts = {
      addJobPosting: AdditionalJobsCosts,
      addStorage: AdditionalStorageCosts,
      benAdmin: productBenefitsAdministrationMonthlyCosts,
      benAdminImp: benefitsAdministrationImplementationCosts,
      [coreProductSelected]: productCoreMonthlyCosts,
      [hrImplementationType]: hrImplementationCosts,
      historicalDataImport: HistoricalDataImportCosts,
      marketplaceListing: MarketplaceListingCosts,
      paidService: PaidServiceCosts,
      payroll: productPayrollMonthlyTotalCosts,
      payrollImp: payrollImplementationTotalCosts,
      performanceMan: productPerformanceMonthlyCosts,
      sponsorship: SponsorshipCosts,
      statesTotal: productPayrollMonthlyStateTotalCosts,
      timeTracking: productTimeTrackingMonthlyCosts,
      timeAttendance: productTimeAttendanceMonthlyCosts,
      testAccount: TestAccountCosts,
      // "whatsThis": whatsThis
   }
   for (const [productCode, productCost] of dynamicProductCosts.entries()) {
      selectedProductCosts[productCode] = productCost
   }

   // Total estimated annual price INCLUDING PAYROLL
   let annualTotal = new MonthlyPrice()
   annualTotal.productCode = 'annualTotal'
   let annualTotalListPrice = 0
   annualTotalListPrice += productCoreMonthlyCosts.listPrice * 12
   annualTotalListPrice += productPerformanceMonthlyCosts.listPrice * 12
   annualTotalListPrice += productTimeTrackingMonthlyCosts.listPrice * 12
   annualTotalListPrice += productTimeAttendanceMonthlyCosts.listPrice * 12
   annualTotalListPrice +=
      productBenefitsAdministrationMonthlyCosts.listPrice * 12
   annualTotalListPrice += AdditionalJobsCosts.listPrice * 12
   annualTotalListPrice += AdditionalStorageCosts.listPrice * 12
   annualTotalListPrice += TestAccountCosts.listPrice * 12
   annualTotalListPrice += MonthlyTaxCosts.listPrice * 12
   annualTotalListPrice += TimeClockMonthlyCost.listPrice * 12
   annualTotalListPrice += !IsForeignCurrencyQuote(book_id)
      ? (payrollMonthlyTotal.listPrice + (alwaysUSDMonthlyTotal.listPrice || 0)) * 12
      : 0
   annualTotalListPrice += dynamicMonthlyTotal.listPrice * 12
   annualTotal.listPrice = annualTotalListPrice

   let MonthlyDynamicCosts = []
   for (const [productCode, count] of dynamicProducts?.entries() ?? []) {
      const product = product_list[productCode] ?? null
      if (
         product &&
         productCode in selectedProductCosts &&
         product.BillingFreq === 'monthly' &&
         count > 0 &&
         // timeAttendance is a first-class time product passed explicitly to
         // calculateAnnualTotal as productTimeAttendanceMonthlyCosts. Including
         // it here would double-count it in the annual total.
         productCode !== 'timeAttendance' &&
         // Exclude AlwaysUSD products on foreign currency quotes - they are handled separately in the payroll section
         !(IsForeignCurrencyQuote(book_id) && isAlwaysUSD(productCode))
      ) {
         MonthlyDynamicCosts.push(selectedProductCosts[productCode])
      }
   }

   if (!isRecalculation) {
      annualTotal.totalPrice = calculateAnnualTotal(
         //MonthlyPrice objects for each monthly product which factors into the activation total
         productCoreMonthlyCosts,
         productPerformanceMonthlyCosts,
         productTimeTrackingMonthlyCosts,
         productTimeAttendanceMonthlyCosts,
         productBenefitsAdministrationMonthlyCosts,
         AdditionalJobsCosts,
         AdditionalStorageCosts,
         TestAccountCosts,
         MonthlyTaxCosts,
         TimeClockMonthlyCost,
         payrollMonthlyTotal,
         MonthlyDynamicCosts,
         // Required inputs to perform a calculation.
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate, // if not supplied, defaults to 1st day of month
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         isRecalculation,
      )
   } else {
      annualTotal.totalPrice = 0
   }
   annualTotal.totalDiscount = annualTotal.listPrice - annualTotal.totalPrice
   annualTotal.totalDiscountPercent =
      annualTotal.listPrice !== 0
         ? (annualTotal.totalDiscount / annualTotal.listPrice) * 100
         : 0
   // Rounds all numbers
   roundObjectPropertiesToPrecision(annualTotal, 2)

   //Create Summarized Discount List
   monthlyTotal.appliedDiscounts = summarizeDiscounts(
      monthlyTotal.appliedDiscounts,
      discount_list,
      selectedProductCosts,
      product_list,
   )
   oneTimeTotal.appliedDiscounts = summarizeDiscounts(
      oneTimeTotal.appliedDiscounts,
      discount_list,
      selectedProductCosts,
      product_list,
   )

   // Calculates total related entity discounts among all monthly products
   let totalRelatedEntityDiscountAmount = getDiscountTotal(
      selectedProductCosts,
      discount_list,
      'relatedEntity',
   ).toFixed(2)

   //Calculates total Nonprofit Discount amount among all applicable products
   let totalNonprofitDiscountAmount = getDiscountTotal(
      selectedProductCosts,
      discount_list,
      'nonProfit',
   ).toFixed(2)

   // Calculates total Prepayment Discount amount among all applicable products
   // Find prepayment discounts by looking for discounts with DiscountFamily = 'Prepayment Discount' that are enabled
   let totalPrepaymentDiscountAmount = 0
   for (const discountCode in discount_list) {
      if (
         discount_list[discountCode]['DiscountFamily'] === 'Prepayment Discount' &&
         discountStates[discountCode] === true
      ) {
         totalPrepaymentDiscountAmount += getDiscountTotal(
            selectedProductCosts,
            discount_list,
            discountCode,
         )
      }
   }
   totalPrepaymentDiscountAmount = totalPrepaymentDiscountAmount.toFixed(2)

   // Calculates total Nonprofit discounts among all monthly products

   let selectedProducts = []
   //Loop through all products in pricebook, and add them to the selectedProducts list if they have a cost, or are discounted to $0
   Object.keys(product_list).forEach((product_key) => {
      if (product_key === 'productBands') {
         return // Skip the productBands array, it's not a product object
      }
      let product = product_list[product_key]
      if (product.ProductCode in selectedProductCosts) {
         if (
            'totalPrice' in selectedProductCosts[product.ProductCode] &&
            'totalDiscount' in selectedProductCosts[product.ProductCode]
         ) {
            let totalPrice =
               selectedProductCosts[product.ProductCode].totalPrice
            let totalDiscount =
               selectedProductCosts[product.ProductCode].totalDiscount

            if (totalPrice > 0) selectedProducts.push(product.ProductCode)
            else if (totalPrice == 0 && totalDiscount > 0)
               selectedProducts.push(product.ProductCode)
         }
      }
   })
   //Add "multiEIN" if it is selected
   if (productPayrollEINCount > 1) selectedProducts.push('multiEIN')
   //Concatonate the list into a semicolon-separated string
   let selectedProductsString = selectedProducts.join(';')
   let oneMonthDiscount = 1
   let twoMonthsDiscount = 2

   // Details for the total due at activation when proration is involved
   let proratedActivationDetails = {}
   let monthlyActivationPrice = new MonthlyPrice()
   proratedActivationDetails = getProrationDetails(
      activationDate,
      nextInvoiceDate,
      differenceQuote,
      account_status,
   )
   // Use prepayLength directly for discount months calculation
   let discountMonths = prepayLength
   monthlyActivationPrice.productCode = 'monthlyActivationPrice'
   //TODO: calculate List price as well
   if (!isRecalculation) {
      monthlyActivationPrice.totalPrice = calculateMonthlyTotalForActivation(
         //MonthlyPrice objects for each monthly product which factors into the activation total
         productCoreMonthlyCosts,
         productPerformanceMonthlyCosts,
         productTimeTrackingMonthlyCosts,
         productTimeAttendanceMonthlyCosts,
         productBenefitsAdministrationMonthlyCosts,
         AdditionalJobsCosts,
         AdditionalStorageCosts,
         TestAccountCosts,
         MonthlyTaxCosts,
         TimeClockMonthlyCost,
         MonthlyDynamicCosts,
         // Required inputs to perform a calculation.
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate, // if not supplied, defaults to 1st day of month
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         isRecalculation,
      )

      // Calculates the total price for the prorated activation period just for the first month.
      proratedActivationDetails.totalPrice = calculateMonthlyTotalForActivation(
         //MonthlyPrice objects for each monthly product which factors into the activation total
         productCoreMonthlyCosts,
         productPerformanceMonthlyCosts,
         productTimeTrackingMonthlyCosts,
         productTimeAttendanceMonthlyCosts,
         productBenefitsAdministrationMonthlyCosts,
         AdditionalJobsCosts,
         AdditionalStorageCosts,
         TestAccountCosts,
         MonthlyTaxCosts,
         TimeClockMonthlyCost,
         MonthlyDynamicCosts,
         // Required inputs to perform a calculation.
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate, // if not supplied, defaults to 1st day of month
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         isRecalculation,
         1, // monthsToCalculate = 1 for prorated total (only first month)
      )

      // Applies all selected and applicable discounts
      applyMonthlyDiscounts(
         discount_list,
         discountStates,
         'monthlyTotal',
         monthlyActivationPrice,
         productCoreEECount,
         productBenefitsAdministrationEECount,
         productPayrollEECount,
         discount_list['bundleDiscount']['DiscountAmount'],
         promotionalDiscounts,
         currencyObject,
         user_role,
         allPermissions,
      )
      roundObjectPropertiesToPrecision(proratedActivationDetails, 2)
   } else {
      monthlyActivationPrice.totalPrice = 0
      proratedActivationDetails.totalPrice = 0
   }
   let totalDueAtActivation =
      oneTimeTotal.totalPrice + monthlyActivationPrice.totalPrice

   //Total due at activation list price is calculated by combining onetime total list price + first month of HR monthly cost list price DOES NOT INCLUDE PAYROLL
   let monthlyActivationListPriceAmount = calculateMonthlyListPrice(
      product_list,
      activationDate,
      nextInvoiceDate,
      discountMonths,
      productCoreMonthlyCosts,
      productPerformanceMonthlyCosts,
      productTimeTrackingMonthlyCosts,
      productTimeAttendanceMonthlyCosts,
      productBenefitsAdministrationMonthlyCosts,
      AdditionalJobsCosts,
      AdditionalStorageCosts,
      TestAccountCosts,
      MonthlyTaxCosts,
      TimeClockMonthlyCost,
      MonthlyDynamicCosts,
      differenceQuote,
      account_status,
   )
   let listPriceDueAtActivation =
      oneTimeTotal.listPrice + monthlyActivationListPriceAmount

   let DueAtActivation = new OneTimePrice('dueAtActivation')
   DueAtActivation.listPrice = listPriceDueAtActivation
   DueAtActivation.totalPrice = totalDueAtActivation
   DueAtActivation.totalDiscount =
      listPriceDueAtActivation - totalDueAtActivation
   DueAtActivation.totalDiscountPercent =
      listPriceDueAtActivation !== 0
         ? ((listPriceDueAtActivation - totalDueAtActivation) /
              listPriceDueAtActivation) *
           100
         : 0
   // Rounds all numbers
   roundObjectPropertiesToPrecision(DueAtActivation, 2)

   // Specific values for the reseller to share with clients. These values are values with the reseller discount removed, if present.
   let resellerCustomerQuoteValues
   resellerCustomerQuoteValues = calculateResellerQuoteValues(
      discountReseller,
      productCoreMonthlyCosts,
      productPerformanceMonthlyCosts,
      productBenefitsAdministrationMonthlyCosts,
      selectedTimeProduct === 'timeAttendance'
         ? productTimeAttendanceMonthlyCosts
         : productTimeTrackingMonthlyCosts,
      monthlyTotal,
      annualTotal,
      discountMonths,
   )
   let ongoingMonthlyTotalPrice = new MonthlyPrice()
   ongoingMonthlyTotalPrice.productCode = 'ongoingMonthlyTotal'
   const [OngoingMonthlyTotal, OngoingMonthlyTotalDiscount] =
      calculateOngoingMonthlyCost(
         productCoreMonthlyCosts,
         productPerformanceMonthlyCosts,
         productTimeTrackingMonthlyCosts,
         productTimeAttendanceMonthlyCosts,
         productBenefitsAdministrationMonthlyCosts,
         AdditionalJobsCosts,
         AdditionalStorageCosts,
         TestAccountCosts,
         MonthlyTaxCosts,
         TimeClockMonthlyCost,
         MonthlyDynamicCosts,
         productPayrollMonthlyTotalCosts,
         productPayrollMonthlyStateTotalCosts,
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate,
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         isRecalculation,
      )
   ongoingMonthlyTotalPrice.totalPrice = OngoingMonthlyTotal
   ongoingMonthlyTotalPrice.totalDiscount = OngoingMonthlyTotalDiscount
   // listPrice (pre-discount) is the correct denominator for a discount percent — dividing
   // by OngoingMonthlyTotal (post-discount) inflates the percent, and can exceed 100% once
   // a heavily-discounted product's list price forms a large share of the pre-discount total.
   ongoingMonthlyTotalPrice.listPrice =
      OngoingMonthlyTotal + OngoingMonthlyTotalDiscount
   ongoingMonthlyTotalPrice.totalDiscountPercent =
      ongoingMonthlyTotalPrice.listPrice !== 0
         ? (OngoingMonthlyTotalDiscount / ongoingMonthlyTotalPrice.listPrice) *
           100
         : 0
   if (differenceQuote) {
      ongoingMonthlyTotalPrice.previousTotalPrice =
         monthlyTotal.previousTotalPrice || 0
      ongoingMonthlyTotalPrice.totalPriceDifference =
         ongoingMonthlyTotalPrice.totalPrice - ongoingMonthlyTotalPrice.previousTotalPrice
   }
   roundObjectPropertiesToPrecision(ongoingMonthlyTotalPrice, 2)
   selectedProductCosts['ongoingMonthlyTotal'] = ongoingMonthlyTotalPrice

   // Total estimated ongoing annual price INCLUDING PAYROLL
   let annualOngoingTotal = new MonthlyPrice()
   annualOngoingTotal.productCode = 'annualOngoingTotal'
   let annualOngoingTotalListPrice = 0
   annualOngoingTotalListPrice += productCoreMonthlyCosts.listPrice * 12
   annualOngoingTotalListPrice += productPerformanceMonthlyCosts.listPrice * 12
   annualOngoingTotalListPrice += productTimeTrackingMonthlyCosts.listPrice * 12
   annualOngoingTotalListPrice += productTimeAttendanceMonthlyCosts.listPrice * 12
   annualOngoingTotalListPrice +=
      productBenefitsAdministrationMonthlyCosts.listPrice * 12
   annualOngoingTotalListPrice += AdditionalJobsCosts.listPrice * 12
   annualOngoingTotalListPrice += AdditionalStorageCosts.listPrice * 12
   annualOngoingTotalListPrice += TestAccountCosts.listPrice * 12
   annualOngoingTotalListPrice += MonthlyTaxCosts.listPrice * 12
   annualOngoingTotalListPrice += TimeClockMonthlyCost.listPrice * 12
   annualOngoingTotalListPrice += !IsForeignCurrencyQuote(book_id)
      ? (payrollMonthlyTotal.listPrice + (alwaysUSDMonthlyTotal.listPrice || 0)) * 12
      : 0
   annualOngoingTotalListPrice += dynamicMonthlyTotal.listPrice * 12
   annualOngoingTotal.listPrice = annualOngoingTotalListPrice

   //If the customer is not a current payroll customer or not all 3 products are on the quote, we exclude the bundle discount from the calculation

   const isCurrentPayrollCustomer =
      accountSnowflake.hasOwnProperty('Billed_Payroll__c') &&
      accountSnowflake.Billed_Payroll__c &&
      accountSnowflake.Billed_Payroll__c > 0
         ? true
         : false

   const allThreeBundleProductsOnQuote =
      productCoreEECount > 0 &&
      productPayrollEECount > 0 &&
      productBenefitsAdministrationEECount > 0

   const hasBundleDiscountNoRequirements =
      allPermissions['BundleDiscountNoRequirements']?.includes(user_role) ??
      false

   const includeBundleDiscount =
      isCurrentPayrollCustomer || allThreeBundleProductsOnQuote || hasBundleDiscountNoRequirements

   if (!isRecalculation) {
      annualOngoingTotal.totalPrice = calculateAnnualTotal(
         //MonthlyPrice objects for each monthly product which factors into the activation total
         productCoreMonthlyCosts,
         productPerformanceMonthlyCosts,
         productTimeTrackingMonthlyCosts,
         productTimeAttendanceMonthlyCosts,
         productBenefitsAdministrationMonthlyCosts,
         AdditionalJobsCosts,
         AdditionalStorageCosts,
         TestAccountCosts,
         MonthlyTaxCosts,
         TimeClockMonthlyCost,
         payrollMonthlyTotal,
         MonthlyDynamicCosts,
         // Required inputs to perform a calculation.
         product_list,
         discount_list,
         book_id,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount,
         hasTimeClocks,
         productTimeClockDetails,
         productBenefitsAdministrationEECount,
         productPayrollEINCount,
         productPayrollEECount,
         productPayrollEECounts,
         productPayrollTotalStateCount,
         productPayrollStateCounts,
         professionalServiceDetails,
         consultingServiceDetails,
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength,
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate, // if not supplied, defaults to 1st day of month
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         allPermissions,
         user_role,
         account_status,
         pricebookDetails,
         previousCurrencyObject,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts,
         selectedTimeProduct,
         isATSOnly,
         isRecalculation,
         includeBundleDiscount,
      )
   } else {
      annualOngoingTotal.totalPrice = 0
   }
   annualOngoingTotal.totalDiscount =
      annualOngoingTotal.listPrice - annualOngoingTotal.totalPrice
   annualOngoingTotal.totalDiscountPercent =
      annualOngoingTotal.listPrice !== 0
         ? (annualOngoingTotal.totalDiscount / annualOngoingTotal.listPrice) *
           100
         : 0
   // For difference quotes, derive previous annual total from monthly previous total
   if (differenceQuote) {
      annualOngoingTotal.previousTotalPrice =
         (ongoingMonthlyTotalPrice.previousTotalPrice || 0) * 12
      annualOngoingTotal.totalPriceDifference =
         annualOngoingTotal.totalPrice - annualOngoingTotal.previousTotalPrice
   }
   // Rounds all numbers
   roundObjectPropertiesToPrecision(annualOngoingTotal, 2)

   // Calculate the total first year cost and savings
   let firstYearTotalCost = new OneTimePrice('firstYearTotalCost')

   firstYearTotalCost.listPrice =
      annualOngoingTotal.listPrice + oneTimeTotal.listPrice
   firstYearTotalCost.totalPrice =
      annualOngoingTotal.totalPrice + oneTimeTotal.totalPrice
   firstYearTotalCost.totalDiscount =
      annualOngoingTotal.totalDiscount + oneTimeTotal.totalDiscount
   firstYearTotalCost.totalDiscountPercent =
      firstYearTotalCost.listPrice !== 0
         ? (firstYearTotalCost.totalDiscount / firstYearTotalCost.listPrice) *
           100
         : 0
   roundObjectPropertiesToPrecision(firstYearTotalCost, 2)
   selectedProductCosts['firstYearTotalCost'] = firstYearTotalCost

   /****************************************************/
   // Final Values and Functions Applied to All Values //
   /****************************************************/

   // Compiles all calculated values above into a single object
   let calculatedValues = {
      hrMonthlyTotal,
      payrollMonthlyTotal,
      payrollAnnualTotal,
      monthlyTotal,
      annualTotal,
      hrImplementationCosts,
      payrollImplementationTotalCosts,
      payrollImplementationCosts,
      oneTimeTotal,
      totalDueAtActivation,
      productCoreMonthlyCosts,
      productPerformanceMonthlyCosts,
      productTimeTrackingMonthlyCosts,
      productTimeAttendanceMonthlyCosts,
      TimeClockMonthlyCost,
      TimeClockCosts,
      productBenefitsAdministrationMonthlyCosts,
      productPayrollMonthlyTotalCosts,
      productPayrollMonthlyStateTotalCosts,
      productPayrollMonthlyCosts,
      productPayrollMonthlyStateCosts,
      benefitsAdministrationImplementationCosts,
      AdditionalStorageCosts,
      AdditionalJobsCosts,
      HistoricalDataImportCosts,
      MarketplaceListingCosts,
      SponsorshipCosts,
      PaidServiceCosts,
      totalRelatedEntityDiscountAmount,
      totalNonprofitDiscountAmount,
      totalImplementationCosts,
      totalPrepaymentDiscountAmount,
      selectedProductsString,
      TestAccountCosts,
      proratedActivationDetails,
      DueAtActivation,
      resellerCustomerQuoteValues,
      AccountBalanceCosts,
      MonthlyTaxCosts,
      productEINIntegrationsTotalCosts,
      productEINIntegrationsCosts,
      monthlyActivationPrice,
      ConsultingServicesCosts,
      ProfessionalServicesCosts,
      selectedProductCosts,
      alwaysUSDMonthlyTotal,
      alwaysUSDOneTimeTotal,
      annualOngoingTotal,
      isATSOnly,
   }
   // Run the setUSDValues function on each object in the calculatedValues object. Does so recursively for nested objects in arrays.
   Object.values(calculatedValues).forEach((value) =>
      setUSDValuesRecursively(
         value,
         currencyObject?.Rate,
         pricebookDetails?.agentExchangeRate,
         product_list,
      ),
   )
   roundObjectPropertiesToPrecision(calculatedValues, 2)
   // Caches the calculated values for future use.
   quoteCache.set(cacheKey, calculatedValues)
   // Returns the calculated values.

   return calculatedValues
}

/******************************/
// Functions for Calulcations //
/******************************/

// This function rounds all number properties of an object to x 'precision' of decimals.
function roundObjectPropertiesToPrecision(obj, precision) {
   if (typeof obj !== 'object' || obj === null) {
      return obj
   }

   if (Array.isArray(obj)) {
      return obj.map((item) =>
         roundObjectPropertiesToPrecision(item, precision),
      )
   }

   for (var prop in obj) {
      if (typeof obj[prop] === 'number') {
         obj[prop] = parseFloat(obj[prop].toFixed(precision))
      } else if (typeof obj[prop] === 'object' && obj[prop] !== null) {
         obj[prop] = roundObjectPropertiesToPrecision(obj[prop], precision)
      }
   }
   return obj
}

// Uses finance's formula to determine volume cost pepm based on ee count
function volumeDiscountedPEPM(
   productItem,
   productEECount,
   productPEPMBaseCost,
   productMinimumCharge = 0,
   productMinEECount = 1,
   limitVolumeDiscount = false,
) {
   // returns 0 if product count is 0 or below
   if (productEECount <= 0) return 0

   let totalPrice

   // 2023 Essentials v2.2 formula for calculating total price PEPM * EE count at given volume
   if (productItem['VolumeCurve'] == 'V2.2 Essentials') {
      totalPrice = Math.max(
         1.6722 * productEECount,
         productMinimumCharge,
         Math.min(
            ((18 * productEECount ** 0.768) / 8.25) * productPEPMBaseCost,
            productPEPMBaseCost * productEECount,
         ),
      )
   }
   // 2023 Advantage v2.2 formula for calculating total price PEPM * EE count at given volume
   else if (productItem['VolumeCurve'] == 'V2.2 Advantage') {
      totalPrice = Math.max(
         2.8667 * productEECount,
         productMinimumCharge,
         Math.min(
            ((18 * productEECount ** 0.768) / 8.25) * productPEPMBaseCost,
            productPEPMBaseCost * productEECount,
         ),
      )
   }
   // 2023 Time Tracking v2.2 formula for calculating total price PEPM * EE count at given volume
   else if (productItem['VolumeCurve'] == 'V2.2 Time Tracking') {
      totalPrice = Math.max(
         0.9556 * productEECount,
         productMinimumCharge,
         Math.min(
            ((18 * productEECount ** 0.768) / 8.25) * productPEPMBaseCost,
            productPEPMBaseCost * productEECount,
         ),
      )
   }
   // 2023 PAYROLL SPECIFIC formula for calculating total price PEPM * EE count at given volume
   else if (productItem['VolumeCurve'] == 'V3 Payroll') {
      totalPrice = Math.max(
         3.9398,
         productMinimumCharge / productEECount,
         Math.min(
            ((18 * productEECount ** 0.768) / (productEECount * 8.25)) *
               productPEPMBaseCost,
            productPEPMBaseCost,
         ) * productEECount,
      )
   }
   // V3 formula for calculating total price PEPM * EE count at given volume
   else if (productItem['VolumeCurve'] == 'V3') {
      // formula for calculating total price PEPM * EE count at given volume (2023 v3 and 2024 Core/Pro)
      totalPrice = Math.max(
         productMinimumCharge / productEECount,
         Math.min(
            ((15.1 * productEECount ** 0.84) / (productEECount * 9.0)) *
               productPEPMBaseCost,
            productPEPMBaseCost,
         ) * productEECount,
      )
   }
   // 2024 formula for calculating total price PEPM * EE count at given volume
   else if (productItem['VolumeCurve'] == '2024') {
      // formula for calculating total price PEPM * EE count at given volume (2023 v3 and 2024 Core/Pro)
      const volumeDiscountedPEPM = Math.min(
         ((15.1 * productEECount ** 0.84) / (productEECount * 9.0)) *
            productPEPMBaseCost,
         productPEPMBaseCost,
      )
      // 50% volume discount cap: only applied if LimitVolumeDiscount permission is set
      const finalPEPM = limitVolumeDiscount ? Math.max(volumeDiscountedPEPM, productPEPMBaseCost * 0.5) : volumeDiscountedPEPM
      totalPrice = Math.max(
         productMinimumCharge / productEECount,
         finalPEPM * productEECount,
      )
   }
   else if (productItem['VolumeCurve'] == '2026') {
      // formula for calculating total price PEPM * EE count at given volume (2026 Core/Pro/Time Tracking)
      const volumeDiscountFactor = Math.min((15.1 * (productEECount ** 0.84)) / (productEECount * 9.0), 1)
      const volumeDiscountedPEPM = volumeDiscountFactor * productPEPMBaseCost
      // 50% volume discount cap: only applied if LimitVolumeDiscount permission is set
      const finalPEPM = limitVolumeDiscount ? Math.max(volumeDiscountedPEPM, productPEPMBaseCost * 0.5) : volumeDiscountedPEPM
      totalPrice = Math.max(
         productMinimumCharge / productEECount,
         finalPEPM
            * productEECount
            * Math.min(1, Math.max(0, productEECount - productMinEECount))
            + productMinimumCharge
            * (1 - Math.min(1, Math.max(0, productEECount - productMinEECount)))
      )
   }
   // Default formula (2024) for calculating total price PEPM * EE count at given volume
   else {
      // formula for calculating total price PEPM * EE count at given volume (2023 v3 and 2024 Core/Pro)
      totalPrice = Math.max(
         productMinimumCharge / productEECount,
         Math.min(
            ((15.1 * productEECount ** 0.84) / (productEECount * 9.0)) *
               productPEPMBaseCost,
            productPEPMBaseCost,
         ) * productEECount,
      )
   }

   // price PEPM at volume
   let productDiscountedPEPMCost = totalPrice / productEECount

   return productDiscountedPEPMCost
}

// Function takes in a EE count and base PEPM pricing, returns JSON object with data to display
//
export function calculateMonthlyProductCosts(
   productEECount,
   productPEPMBaseCost,
   productDiscountedPEPMCost,
   productMinimumCharge = 0,
) {
   let productCosts = new MonthlyPrice()
   // if value greater then 0, calculate costs, else return 0 for all values
   if (productEECount <= 0) {
      return productCosts
   }

   // set product cost properties
   ;((productCosts.productEECount = parseInt(productEECount)),
      (productCosts.listPrice = Math.max(
         productMinimumCharge,
         productEECount * productPEPMBaseCost,
      )))
   productCosts.listPEPMCost = productPEPMBaseCost

   ;((productCosts.volumeDiscountedPEPMPercent =
      (Math.max(
         (productEECount * productDiscountedPEPMCost <= productMinimumCharge ? 0 : productEECount * productPEPMBaseCost) -
            productEECount * productDiscountedPEPMCost,
         0,
      ) /
         Math.max(productMinimumCharge, productEECount * productPEPMBaseCost)) *
      100),
      (productCosts.discountedPEPMCost = Math.max(
         productMinimumCharge / productEECount,
         productDiscountedPEPMCost,
      )),
      (productCosts.pdfDisplayDiscountedPEPMCost = productCosts.discountedPEPMCost));
   productCosts.totalPEPMDiscount = Math.max(
      productPEPMBaseCost -
         Math.max(
            productMinimumCharge / productEECount,
            productDiscountedPEPMCost,
         ),
      0,
   )
   productCosts.totalDiscountPercent =
      (Math.max(
         productEECount * productPEPMBaseCost -
            productEECount * productDiscountedPEPMCost,
         0,
      ) /
         Math.max(productMinimumCharge, productEECount * productPEPMBaseCost)) *
      100
   productCosts.totalDiscount = Math.max(
      productEECount * productPEPMBaseCost -
         productEECount * productDiscountedPEPMCost,
      0,
   )
   productCosts.totalPrice = Math.max(
      productMinimumCharge,
      productEECount * productDiscountedPEPMCost,
   )

   // set pdfDisplayTotal as totalPrice
   productCosts.pdfDisplayTotal = productCosts.totalPrice
   // set pdfDisplayTotalDiscountPercent as totalDiscountPercent
   productCosts.pdfDisplayTotalDiscountPercent = productCosts.totalDiscountPercent
   // set pdfDisplayTotalDiscount as totalDiscount
   productCosts.pdfDisplayTotalDiscount = productCosts.totalDiscount

   // returns object with display data
   return productCosts
}

// Function takes in core product EE count, and 3 implementation charges. Returns the ProductCode of the imp to be used.
//
function determineHRImplementationType(
   impType,
   productCoreEECount,
   productQuickstartImplementation,
   productNormalImplementation,
   productPremierImplementation,
) {
   // Checks if core ee count is between the quickstart low and normal imp low. This means quickstart is required. Returns quickstart.
   if (impType === 'selfSetup') {
      return 'selfSetup'
   } else if (
      productCoreEECount > productQuickstartImplementation.EmpLow &&
      productCoreEECount < productNormalImplementation.EmpLow
   ) {
      return productQuickstartImplementation.ProductCode
   }
   // Checks if core ee count is between the normal imp low and quickstart high allowance, and quickstart is selected. Returns quickstart.
   else if (
      productCoreEECount >= productNormalImplementation.EmpLow &&
      productCoreEECount <= productQuickstartImplementation.EmpHigh &&
      impType == 'quickstart'
   ) {
      return productQuickstartImplementation.ProductCode
   }
   // Checks if core ee count is between the normal imp low and quickstart high allowance, and quickstart is NOT selected. Returns normal imp
   else if (
      productCoreEECount > productNormalImplementation.EmpLow &&
      productCoreEECount < productQuickstartImplementation.EmpHigh &&
      impType != 'quickstart'
   ) {
      return productNormalImplementation.ProductCode
   }
   // Checks if core ee count is between the normal imp low and high allowance. Returns normal imp.
   else if (
      productCoreEECount > productNormalImplementation.EmpLow &&
      productCoreEECount < productNormalImplementation.EmpHigh
   ) {
      return productNormalImplementation.ProductCode
   } else if (
      productCoreEECount >= productPremierImplementation.EmpLow &&
      impType == 'premier'
   ) {
      return productPremierImplementation.ProductCode
   } else return productNormalImplementation.ProductCode
}

// Function takes in a EE count and base PEPM pricing, and base product cost, returns JSON object with data to display
//
function calculateHRImplementationCost(
   product_list,
   productEECount,
   impProductCode,
   impType,
   coreCountryCount,
) {
   let productCosts = new OneTimePrice()

   // If the impProductCode does not exist in the product list return 0 for all values
   if (product_list[impProductCode]?.NotInPricebook) {
      return productCosts
   }

   // if value greater then 0, calculate costs, else return 0 for all values
   if (productEECount <= 0) {
      return productCosts
   }
   // if value greater then 0, calculate costs, else return 0 for all values
   if (impType == 'selfSetup') {
      productCosts.productCode = 'selfSetup'
      return productCosts
   }

   // Exception for pricebook 100, 2023 pricing. This has 2 different per employee pricing for implementation. Use the 'EmpLarge' variation for per employee price
   if (
      product_list[impProductCode]['BookId'] == 100 &&
      productEECount > product_list[impProductCode]['EmpHigh']
   ) {
      impProductCode = `${impProductCode}Large`
   }

   // Determines base pricing, banded or formula based.
   let basePrice = getBasePrice(product_list, impProductCode, productEECount)

   // Sets total pricing and EE count
   productCosts.listPrice =
      productEECount * product_list[impProductCode]['EmpListPrice'] + basePrice
   productCosts.totalPrice =
      productEECount * product_list[impProductCode]['EmpListPrice'] + basePrice
   productCosts.pdfDisplayTotal = productCosts.totalPrice
   productCosts.productEECount = productEECount

   // Gets extra country cost, if applicable in selected pricing band
   productCosts.extraCountryCost = 0
   productCosts.totalPriceWithoutExtraCountryCost = productCosts.totalPrice
   if (
      getCurrentProductBand(product_list, impProductCode, productEECount)[
         'CountryMultiplier'
      ] != null
   ) {
      let extraCountryCost =
         Math.max(coreCountryCount - 1, 0) *
         getCurrentProductBand(product_list, impProductCode, productEECount)[
            'CountryMultiplier'
         ]
      productCosts.extraCountryCost = extraCountryCost
      productCosts.totalPriceWithoutExtraCountryCost = productCosts.totalPrice
      productCosts.listPrice += extraCountryCost
      productCosts.totalPrice += extraCountryCost
   }

   return productCosts
}

// Function takes in a EE count and base PEPM pricing, and base product cost, returns JSON object with data to display
//
function calculatePayrollImplementationCost(
   product_list,
   payrollImpType,
   productEECount,
   productPEPMBaseCost,
   productBaseCost,
   isMultiEIN,
   accountSnowflake = {},
   payrollEECounts,
   einPosition,
) {
   let productCosts = new OneTimePrice()

   // Check if payroll implementation is not in pricebook, return 0 for all values
   const impType =
      payrollImpType === 'foundations' ? 'payrollImpFoundations' : 'payrollImp' //
   if (product_list[impType]?.NotInPricebook) {
      return productCosts
   }

   const impAdditionalEIN =
      payrollImpType === 'foundations'
         ? 'payrollImpFoundationsAdditionalEIN'
         : 'payrollImpAdditionalEIN'
   if (product_list[impAdditionalEIN]?.NotInPricebook) {
      return productCosts
   }

   // if value greater then 0, calculate costs, else return 0 for all values
   if (productEECount <= 0 || payrollImpType == 'none') {
      return productCosts
   }

   // checks if multi ein
   //if not multi ein
   if (!isMultiEIN) {
      productCosts.listPrice =
         productEECount * productPEPMBaseCost + productBaseCost
      productCosts.totalPrice =
         productEECount * productPEPMBaseCost + productBaseCost
      productCosts.pdfDisplayTotal = productCosts.totalPrice
      productCosts.productEECount = productEECount
      return productCosts
   }
   // If quote is an additional quote for multiple EINs
   // else if (additionalEINQuote) {
   //   let totalPrice = 500
   //   productCosts.listPrice = (productEECount * productPEPMBaseCost) + productBaseCost
   //   productCosts.totalDiscount = Math.max(((productEECount * productPEPMBaseCost) + productBaseCost) - totalPrice, 0)
   //   productCosts.totalPrice = totalPrice
   //   productCosts.productEECount = productEECount
   //   return productCosts;
   // }
   // if multi ein
   else {
      let totalPrice
      // Removes 0 values from payroll pricing array
      let payrollEECountsArray = payrollEECounts.filter((i) => i !== 0)
      // Gets highest ee count among all payroll EINs
      let highestEECount = Math.max.apply(null, payrollEECountsArray)
      // Gets occurences of the highest ee count in the array
      let highestEEOccurences = payrollEECountsArray.filter(
         (x) => x == highestEECount,
      ).length

      // EIN REPLACEMENT LOGIC: Check if all existing EINs have 0 employees
      const existingEINCount = accountSnowflake?.EINs__c?.[0]?.length || 0
      const existingEINsWithEmployees =
         accountSnowflake?.EINs__c?.[4]?.filter((count) => count > 0)
            ?.length || 0
      const isEINReplacement =
         existingEINCount > 0 && existingEINsWithEmployees === 0

      // if EIN with largest EE count, set full implementation price for payroll. If 2 or more eins have the same largest ee count, the first gets full imp, the rest set to $500
      if (
         (Math.max.apply(null, payrollEECountsArray) >= productEECount ||
            highestEEOccurences == payrollEECountsArray.length) &&
         payrollEECountsArray.length > 1
      ) {
         if (
            einPosition != payrollEECountsArray.indexOf(highestEECount) ||
            (accountSnowflake?.EINs__c?.[0]?.length > 0 && !isEINReplacement)
         ) {
            totalPrice =
               payrollImpType === 'foundations'
                  ? product_list['payrollImpFoundationsAdditionalEIN'].BasePrice
                  : product_list['payrollImpAdditionalEIN'].BasePrice
         } else
            totalPrice = productEECount * productPEPMBaseCost + productBaseCost
      }
      // if EIN is not largest EE count, set payroll imp price to 500
      else totalPrice = productEECount * productPEPMBaseCost + productBaseCost

      productCosts.listPrice =
         productEECount * productPEPMBaseCost + productBaseCost
      productCosts.totalDiscount = Math.max(
         productEECount * productPEPMBaseCost + productBaseCost - totalPrice,
         0,
      )
      productCosts.totalPrice = totalPrice
      productCosts.pdfDisplayTotal = totalPrice
      productCosts.productEECount = productEECount
      return productCosts
   }
}

// Function takes in a EE count and base PEPM pricing, and base product cost, returns JSON object with data to display
//
function calculateBenefitsAdministrationImplementationCost(
   product_list,
   benAdminImpType,
   productEECount,
   productPEPMBaseCost,
   productBaseCost,
) {
   let productCosts = new OneTimePrice('benAdminImp')

   // if benAdminImpType is none, return 0 for all values
   if (product_list['benAdminImp']?.NotInPricebook) {
      return productCosts
   }

   // if value greater then 0, calculate costs, else return 0 for all values
   if (productEECount <= 0 || benAdminImpType == 'none') {
      return productCosts
   }

   productCosts.listPrice =
      productEECount * productPEPMBaseCost + productBaseCost
   productCosts.totalPrice =
      productEECount * productPEPMBaseCost + productBaseCost
   productCosts.pdfDisplayTotal = productCosts.totalPrice
   productCosts.productEECount = productEECount
   return productCosts
}

function calculateProductBundleDiscount(
   productCoreEECount,
   productBenefitsAdministrationEECount,
   productPayrollEECount,
   bundleDiscountPercent,
   productCode,
   user_role,
   featurePermissions,
) {
   // Check if Payroll is the only product selected
   if (
      productCoreEECount === 0 &&
      productBenefitsAdministrationEECount === 0 &&
      productPayrollEECount > 0
   ) {
      if (productCode == 'payroll') {
         return bundleDiscountPercent * 0.01
      } else {
         return 0 // No discount for other products as they are not selected
      }
   }

   if (
      featurePermissions['BundleDiscountNoRequirements']?.includes(user_role) ??
      false
   ) {
      if (productCode === 'benAdmin' || productCode === 'payroll') {
         return bundleDiscountPercent * 0.01
      }
   }

   // Existing logic for bundle discount when multiple products are selected
   if (
      productCoreEECount > 0 &&
      productBenefitsAdministrationEECount > 0 &&
      productPayrollEECount > 0
   ) {
      if (productCode == 'payroll' || productCode == 'benAdmin') {
         return bundleDiscountPercent * 0.01
      } else {
         let minDiscount = 0
         let maxDiscount = bundleDiscountPercent * 0.01
         return Math.min(
            Math.max(
               (productPayrollEECount / productCoreEECount) * 0.15,
               minDiscount,
            ),
            maxDiscount,
         )
      }
   }

   // Return 0 if none of the conditions are met
   return 0
}

/**
 * Returns true if the discount is active AND, when it belongs to a DiscountFamily,
 * is the family-selected discount. Prevents multiple members of the same family
 * from stacking when more than one has a true flag in discountStates.
 */
export function isDiscountActive(discountCode, discountDef, discountStates) {
   if (!(discountCode in discountStates) || !discountStates[discountCode]) {
      return false
   }
   const family = discountDef?.DiscountFamily
   if (family) {
      return discountStates[family] === discountDef.DiscountCode
   }
   return true
}

// Function takes in a product list, discount list, productCode, and monthlyProduct (this is the variable for the product), calculates a discount and lists it on the product variable.
//
export function applyMonthlyDiscounts(
   discount_list,
   discountStates,
   productCode,
   monthlyProduct,
   productCoreEECount,
   productBenefitsAdministrationEECount,
   productPayrollEECount,
   bundleDiscountPercent,
   promotionalDiscounts,
   currencyObject,
   user_role,
   allPermissions,
) {
   // Checks if monthly product ee is count is greater than 0. If not, applies no discounts.
   if (
      monthlyProduct.productCode !== 'monthlyTotal' &&
      monthlyProduct.productEECount <= 0
   )
      return
   // If volume discount exists, add it to the appliedDiscount array.
   if (
      monthlyProduct.productCode !== 'monthlyTotal' &&
      monthlyProduct.volumeDiscountedPEPMPercent > 0
   ) {
      let volumeDiscount = {
         BookId: 200,
         DiscId: 0,
         ApplyTo: '',
         Priority: 0,
         ApplyToPDFDisplayedValue: true, // Set to true to apply discount to pdfDisplayTotal
         BillingFreq: 'monthly',
         DiscountCode: 'volume',
         DiscountName: 'Volume',
         DiscountType: 'percent',
         RoleRequired: null,
         ProductFamily: null,
         DiscountAmount: monthlyProduct.volumeDiscountedPEPMPercent,
         actualEffect: {
            previousTotalPrice: monthlyProduct.listPrice,
            newTotalPrice: monthlyProduct.totalPrice,
            previousDiscountedPEPM: monthlyProduct.listPEPMCost,
            newDiscountedPEPM:
               monthlyProduct.listPEPMCost -
               monthlyProduct.listPEPMCost *
                  (monthlyProduct.volumeDiscountedPEPMPercent / 100),
            changeInDollars:
               monthlyProduct.listPrice - monthlyProduct.totalPrice,
            changeInPercent: monthlyProduct.volumeDiscountedPEPMPercent,
            productCode: productCode,
         },
      }

      monthlyProduct.appliedDiscounts.push(volumeDiscount)
   }

   // Loops through every discount in list
   for (const discount in discount_list) {
      // Checks if discount is monthly
      if (discount_list[discount]['BillingFreq'] == 'monthly') {
         // Checks if given product code is included in 'ApplyTo' list of the discount. This means that the discount is applicable to the product.
         if (
            'ApplyTo' in discount_list[discount] &&
            discount_list[discount]['ApplyTo'] != null &&
            discount_list[discount]['ApplyTo']?.split(',').includes(productCode)
         ) {
            // Check if discount is enabled in discountStates (includes promotional discounts like prepayment discounts)
            if (isDiscountActive(discount, discount_list[discount], discountStates)) {
               // Creates discount log object
               let appliedDiscount = structuredClone(discount_list[discount])

               // Calculates discount as a percent
               let discountAsPercent = 0

               // Checks if bundle discount, if so calculates correct percentages. If not bundle, pulls percentage from nodejs.
               if (
                  discount_list[discount]['DiscountCode'] == 'bundleDiscount'
               ) {
                  let bundlePercent = calculateProductBundleDiscount(
                     productCoreEECount,
                     productBenefitsAdministrationEECount,
                     productPayrollEECount,
                     bundleDiscountPercent,
                     productCode,
                     user_role,
                     allPermissions,
                  )
                  discountAsPercent = bundlePercent
                  monthlyProduct.bundleDiscountedPEPMPercent =
                     bundlePercent * 100
                  appliedDiscount.DiscountAmount = bundlePercent * 100
               }
               // If discount is a percent discount, calculates discount as a percent
               else if (discount_list[discount]['DiscountType'] == 'percent') {
                  const enteredPctValue =
                     'numberDiscountValues' in promotionalDiscounts &&
                     promotionalDiscounts['numberDiscountValues'][discount] > 0
                        ? promotionalDiscounts['numberDiscountValues'][discount]
                        : null
                  discountAsPercent = enteredPctValue != null
                     ? 0.01 * enteredPctValue
                     : 0.01 * discount_list[discount]['DiscountAmount']
                  if (enteredPctValue != null)
                     // Number-input values can arrive as strings; store as a
                     // number so downstream consumers (e.g. getDiscountAmountByCode)
                     // don't string-concatenate DiscountAmount.
                     appliedDiscount.DiscountAmount = Number(enteredPctValue)
               }
               // If discount is a fixed discount, calculates discount as a percent
               else {
                  let discountDollars =
                     'numberDiscountValues' in promotionalDiscounts &&
                     promotionalDiscounts['numberDiscountValues'][discount] > 0
                        ? promotionalDiscounts['numberDiscountValues'][discount]
                        : discount_list[discount]['DiscountAmount']
                  discountAsPercent =
                     ((monthlyProduct.totalPrice -
                        discountDollars -
                        monthlyProduct.totalPrice) /
                        monthlyProduct.totalPrice) *
                     -1
               }
               // Appends 'actualEffect' property to discount log object
               appliedDiscount.actualEffect = {
                  previousTotalPrice: monthlyProduct.totalPrice,
                  newTotalPrice:
                     monthlyProduct.totalPrice -
                     monthlyProduct.totalPrice * discountAsPercent,
                  previousDiscountedPEPM: monthlyProduct.discountedPEPMCost,
                  newDiscountedPEPM:
                     productCode == 'monthlyTotal'
                        ? monthlyProduct.discountedPEPMCost -
                          monthlyProduct.discountedPEPMCost * discountAsPercent
                        : (monthlyProduct.totalPrice -
                             monthlyProduct.totalPrice * discountAsPercent) /
                          monthlyProduct.productEECount,
                  changeInDollars:
                     monthlyProduct.totalPrice * discountAsPercent,
                  changeInPercent: discountAsPercent,
                  productCode: productCode,
               }
               // For write-in (Number-input) commissionable discounts, derive
               // CommissionDiscountAmount from the entered % and the row's
               // CommissionFreePercent. Upfront rows use CommissionFreePercent=5
               // (first 5% comp-free); MtM rows use 0 (every % impacts comp).
               // Legacy Checkbox rows leave CommissionFreePercent NULL and
               // continue to use their static CommissionDiscountAmount.
               if (
                  appliedDiscount.InputType === 'Number' &&
                  appliedDiscount.AppliesToCommission &&
                  appliedDiscount.CommissionFreePercent != null
               ) {
                  const enteredPct =
                     promotionalDiscounts?.numberDiscountValues?.[discount] ?? 0
                  const commissionablePct = Math.max(
                     0,
                     enteredPct -
                        Number(appliedDiscount.CommissionFreePercent),
                  )
                  appliedDiscount.CommissionDiscountAmount =
                     100 - commissionablePct
               }
               // Pushes applied discount log to appliedDiscounts property of monthly product.
               monthlyProduct.appliedDiscounts.push(appliedDiscount)

               // Updates monthly product total values with new values.
               monthlyProduct.discountedPEPMCost =
                  appliedDiscount.actualEffect.newDiscountedPEPM
               monthlyProduct.totalPEPMDiscount =
                  monthlyProduct.listPEPMCost -
                  appliedDiscount.actualEffect.newDiscountedPEPM
               monthlyProduct.totalDiscountPercent =
                  (Math.max(
                     monthlyProduct.listPrice -
                        (monthlyProduct.totalPrice -
                           monthlyProduct.totalPrice * discountAsPercent),
                     0,
                  ) /
                     Math.max(monthlyProduct.listPrice)) *
                  100
               monthlyProduct.totalDiscount =
                  monthlyProduct.listPrice -
                  appliedDiscount.actualEffect.newTotalPrice
               monthlyProduct.totalPrice =
                  appliedDiscount.actualEffect.newTotalPrice

               // Apply discount to pdfDisplayTotal if ApplyToPDFDisplayedValue is true
               if (discount_list[discount]['ApplyToPDFDisplayedValue']) {
                  // Calcuate pdf Discounted PEPM Cost
                  monthlyProduct.pdfDisplayDiscountedPEPMCost = appliedDiscount.actualEffect.newDiscountedPEPM

                     // Calculate PDF Display Total Discount Percent
                  monthlyProduct.pdfDisplayTotalDiscountPercent =
                     (Math.max(
                        monthlyProduct.listPrice -
                           (monthlyProduct.pdfDisplayTotal -
                              monthlyProduct.pdfDisplayTotal * discountAsPercent),
                        0,
                     ) /
                        Math.max(monthlyProduct.listPrice)) *
                     100
                  // Calculate PDF Total Discount
                  monthlyProduct.pdfDisplayTotalDiscount = monthlyProduct.listPrice - appliedDiscount.actualEffect.newTotalPrice
                  // Calculate Pdf Display
                  monthlyProduct.pdfDisplayTotal = monthlyProduct.pdfDisplayTotal - (monthlyProduct.pdfDisplayTotal * discountAsPercent)
               }
            }
         }
      }
   }
}


 /**
 * Function takes in a monthlyPrice object, discount_list, and optional flags for specific discounts.
 * Calculates MRR total by applying volume discount and all applicable discounts from monthlyPrice.appliedDiscounts
 *
 * @param {MonthlyPrice} monthlyPrice - The MonthlyPrice object to derive MRR from.
 * @param {boolean} noMMRCalculation - Optional flag to bypass calculation when true.
 * @param {Object} accountSnowflake - Previous products and quantities from Salesforce
 * @param {String} productCode - product code for given product
 *
 * @returns {number} The final commissionable MRR total.
 */
export function calculateMRRTotal(monthlyPrice, noMMRCalculation, accountSnowflake, productCode, differenceQuote) {
   if (noMMRCalculation) return 0

   let MRRTotal = monthlyPrice.listPrice

   // Applies Volume Discount to MRR Total
   if (monthlyPrice.volumeDiscountedPEPMPercent > 0)
      MRRTotal =
         MRRTotal * ((100 - monthlyPrice.volumeDiscountedPEPMPercent) / 100)

   // Apply discounts from monthlyPrice.appliedDiscounts where appliesToCommission is true
   if (
      monthlyPrice.appliedDiscounts &&
      Array.isArray(monthlyPrice.appliedDiscounts)
   ) {
      // Find all appliedDiscount objects that have appliesToCommission set to true
      const commissionsDiscounts = monthlyPrice.appliedDiscounts.filter(
         (discount) => discount && discount.AppliesToCommission == true,
      )

      // Apply each applicable discount
      for (const discount of commissionsDiscounts) {
         if ('DiscountType' in discount) {
            // CommissionDiscountAmount is always a percentage representing the commissionable portion
            // If CommissionDiscountAmount is set, use it directly as the percentage to apply
            // Otherwise, fall back to DiscountAmount for percent discounts
            if (
               discount.CommissionDiscountAmount !== null &&
               discount.CommissionDiscountAmount !== undefined
            ) {
               // CommissionDiscountAmount is the percentage of MRR that is commissionable
               // Apply it as: MRRTotal * (CommissionDiscountAmount / 100)
               MRRTotal = MRRTotal * (discount.CommissionDiscountAmount / 100)
            } else {
               // Fall back to original discount amount for percent discounts only
               const discountType = discount.DiscountType
               let discountAmount = discount.DiscountAmount

               // If there is an actual effect recorded (from dynamic discounts), use that percentage
               if (
                  discount.actualEffect &&
                  typeof discount.actualEffect.changeInPercent === 'number'
               ) {
                  discountAmount = discount.actualEffect.changeInPercent * 100
               }

               if (
                  discountType === 'percent' &&
                  discountAmount !== null &&
                  discountAmount !== undefined
               ) {
                  MRRTotal = MRRTotal * ((100 - discountAmount) / 100)
               } else if (
                  discountType === 'fixed' &&
                  discountAmount !== null &&
                  discountAmount !== undefined
               ) {
                  MRRTotal = MRRTotal - discountAmount
               }
            }
         }
      }
   }

   if (accountSnowflake && hasAccountSnowflakeQtyItem(productCode) && differenceQuote !== false) {
      // Cutoffs
      const MinCountCutoff = 25; // 25 Employees
      const MinPercentCutoff = 10; // 10%


      const prevCount = getAccountSnowflakeQtyItem(accountSnowflake, productCode);

      if (prevCount === 0) {
         return MRRTotal
      }

      const diffEECount = (monthlyPrice?.productEECount ?? 0) - (prevCount ?? 0);
      const percentDiff = (((monthlyPrice?.productEECount ?? 0) / (prevCount ?? 1)) - 1) * 100;

      if (diffEECount >= MinCountCutoff && percentDiff >= MinPercentCutoff) {
         return MRRTotal
      } else {
         return 0
      }
   }

   return MRRTotal
}

// Function takes in a discount_list and discountStates, return new total.
//
export function applyOneTimeDiscounts(
   discount_list,
   discountStates,
   productCode,
   oneTimeTotal,
   isMultiEIN,
   promotionalDiscounts,
   currencyObject,
) {
   // Checks if monthly product ee is count is greater than 0. If not, applies no discounts.
   if (oneTimeTotal.totalPrice <= 0) return

   // TODO: Remove after DD revamp. Special logic for benAdminImp product: check if hrDealdesk should be skipped
   let skipHrDealdesk = false
   if (productCode === 'benAdminImp') {
      // Find hrDealdesk discount if it exists and is enabled
      const hrDealdeskDiscount = discount_list['hrDealdesk']
      if (
         hrDealdeskDiscount &&
         hrDealdeskDiscount['BillingFreq'] === 'once' &&
         hrDealdeskDiscount['ApplyTo'] &&
         hrDealdeskDiscount['ApplyTo']?.split(',').includes(productCode) &&
         discountStates['hrDealdesk'] === true
      ) {
         const hrDealdeskAmount = hrDealdeskDiscount['DiscountAmount']

         // Check all other discounts that apply to benAdminImp
         for (const discount in discount_list) {
            if (
               discount !== 'hrDealdesk' &&
               discount_list[discount]['BillingFreq'] === 'once' &&
               discount_list[discount]['DiscountType'] === 'percent' &&
               discount_list[discount]['ApplyTo'] &&
               discount_list[discount]['ApplyTo']?.split(',').includes(productCode) &&
               isDiscountActive(discount, discount_list[discount], discountStates)
            ) {
               const otherDiscountAmount =
                  discount_list[discount]['DiscountAmount']

               // If any other percent discount has higher or equal amount, skip hrDealdesk
               if (otherDiscountAmount >= hrDealdeskAmount) {
                  skipHrDealdesk = true
                  break
               }
            }
         }
      }
   }

   // Loops through every discount in list
   for (const discount in discount_list) {
      // Checks if discount is monthly
      if (discount_list[discount]['BillingFreq'] == 'once') {
         if (
            'ApplyTo' in discount_list[discount] &&
            discount_list[discount]['ApplyTo'] != null &&
            discount_list[discount]['ApplyTo']?.split(',').includes(productCode)
         ) {
            if (isDiscountActive(discount, discount_list[discount], discountStates)) {
               // TODO: Remove after DD revamp. Skip hrDealdesk for benAdminImp if a higher percent discount exists
               if (
                  productCode === 'benAdminImp' &&
                  discount === 'hrDealdesk' &&
                  skipHrDealdesk
               ) {
                  continue
               }

               // Creates discount log object
               let appliedDiscount = structuredClone(discount_list[discount])

               // Pushes applied discount log to appliedDiscounts property of monthly product.

               let totalChangeFixed,
                  totalChangePercent,
                  totalChangeDollars = 0
               let minDiscount = 0
               let maxDiscount = oneTimeTotal.listPrice
               let previousTotalPrice = 0
               let actualChangeDollars,
                  actualChangePercent = 0

               // Calculate the discount if a fixed dollar amount off
               if (discount_list[discount]['DiscountType'] == 'fixed') {
                  previousTotalPrice = oneTimeTotal.totalPrice
                  totalChangeFixed = Number(
                     'numberDiscountValues' in promotionalDiscounts &&
                     promotionalDiscounts['numberDiscountValues'][discount] > 0
                        ? promotionalDiscounts['numberDiscountValues'][discount]
                        : discount_list[discount]['DiscountAmount'],
                  )
                  appliedDiscount.DiscountAmount = totalChangeFixed
                  // Appends 'actualEffect' property to discount log object
                  oneTimeTotal.totalDiscount = Math.min(
                     Math.max(
                        oneTimeTotal.totalDiscount + totalChangeFixed,
                        minDiscount,
                     ),
                     maxDiscount,
                  )
                  oneTimeTotal.totalPrice = Math.min(
                     Math.max(
                        oneTimeTotal.totalPrice - totalChangeFixed,
                        minDiscount,
                     ),
                     maxDiscount,
                  )
                  actualChangeDollars =
                     previousTotalPrice - oneTimeTotal.totalPrice
                  appliedDiscount.actualEffect = {
                     previousTotalPrice: previousTotalPrice,
                     newTotalPrice: oneTimeTotal.totalPrice,
                     changeInDollars: actualChangeDollars,
                     changeInPercent:
                        (actualChangeDollars / previousTotalPrice) * 100,
                     productCode: productCode,
                  }
               }
               // Calculate discount if a percentage off.
               if (discount_list[discount]['DiscountType'] == 'percent') {
                  previousTotalPrice = oneTimeTotal.totalPrice
                  let discountAmount = Number(
                     'numberDiscountValues' in promotionalDiscounts &&
                     promotionalDiscounts['numberDiscountValues'][discount] > 0
                        ? promotionalDiscounts['numberDiscountValues'][discount]
                        : discount_list[discount]['DiscountAmount'],
                  )
                  // For Ben Admin Implementation, limit QuickStart discount to 25%
                  if (
                     productCode === 'benAdminImp' &&
                     discount === 'quickstartDiscount'
                  )
                     discountAmount = Math.min(discountAmount, 25)
                  // Keep DiscountAmount on the applied discount in sync with the resolved value
                  // so PDF column headers (buildTopDiscountCodes) display the entered % not the pricebook default
                  appliedDiscount.DiscountAmount = discountAmount
                  totalChangeDollars =
                     discountAmount * 0.01 * oneTimeTotal.totalPrice
                  oneTimeTotal.totalDiscount = Math.min(
                     Math.max(
                        oneTimeTotal.totalDiscount + totalChangeDollars,
                        minDiscount,
                     ),
                     maxDiscount,
                  )
                  oneTimeTotal.totalPrice = Math.min(
                     Math.max(
                        oneTimeTotal.totalPrice - totalChangeDollars,
                        minDiscount,
                     ),
                     maxDiscount,
                  )
                  actualChangePercent =
                     (oneTimeTotal.totalPrice / previousTotalPrice) * 100
                  actualChangeDollars =
                     previousTotalPrice - oneTimeTotal.totalPrice
                  // Appends 'actualEffect' property to discount log object
                  appliedDiscount.actualEffect = {
                     previousTotalPrice: previousTotalPrice,
                     newTotalPrice: oneTimeTotal.totalPrice,
                     changeInDollars: actualChangeDollars,
                     changeInPercent: actualChangePercent,
                  }
               }
               if (
                  discount_list[discount]['DiscountType'] == 'flatrate' &&
                  oneTimeTotal.totalPrice > 0 &&
                  productCode == 'payrollImp'
               ) {
                  previousTotalPrice = oneTimeTotal.totalPrice
                  const flatrateTarget = Number(
                     'numberDiscountValues' in promotionalDiscounts &&
                     promotionalDiscounts['numberDiscountValues'][discount] > 0
                        ? promotionalDiscounts['numberDiscountValues'][discount]
                        : discount_list[discount]['DiscountAmount'],
                  )
                  appliedDiscount.DiscountAmount = flatrateTarget
                  totalChangeFixed = oneTimeTotal.totalPrice - flatrateTarget
                  oneTimeTotal.totalDiscount = Math.min(
                     Math.max(
                        oneTimeTotal.totalDiscount + totalChangeFixed,
                        minDiscount,
                     ),
                     maxDiscount,
                  )
                  oneTimeTotal.totalPrice = flatrateTarget
                  // Appends 'actualEffect' property to discount log object
                  appliedDiscount.actualEffect = {
                     previousTotalPrice: previousTotalPrice,
                     newTotalPrice: oneTimeTotal.totalPrice,
                     changeInDollars:
                        previousTotalPrice - oneTimeTotal.totalPrice,
                     changeInPercent:
                        ((previousTotalPrice - oneTimeTotal.totalPrice) /
                           previousTotalPrice) *
                        100,
                  }
               }

               // Calculate discount in percentage changed
               oneTimeTotal.totalDiscountPercent = Math.min(
                  Math.max(
                     (oneTimeTotal.totalDiscount / oneTimeTotal.listPrice) *
                        100,
                     0,
                  ),
                  100,
               )
               // Pushes applied discount log to appliedDiscounts property of monthly product.
               oneTimeTotal.appliedDiscounts.push(appliedDiscount)

               // Apply discount to pdfDisplayTotal if ApplyToPDFDisplayedValue is true
               if (discount_list[discount]['ApplyToPDFDisplayedValue']) {
                  oneTimeTotal.pdfDisplayTotal = oneTimeTotal.pdfDisplayTotal - (appliedDiscount.actualEffect?.changeInDollars || 0)
               }
            }
         }
      }
   }

   // If total price ends up less than 0, return 0 instead.
   oneTimeTotal.totalPrice = Math.max(oneTimeTotal.totalPrice, 0)
   if ('totalPriceWithoutExtraCountryCost' in oneTimeTotal)
      oneTimeTotal.totalPriceWithoutExtraCountryCost =
         oneTimeTotal.totalPrice - oneTimeTotal.extraCountryCost
}

// Calculates and returns the total amount (in dollars) a particular discountCode is discounting across all Monthly Products
function getDiscountTotal(monthlyCosts, discount_list, discountCode) {
   let totalDiscount = 0
   //Makes sure the discount is present in the current pricebook and it has products it would be applied to
   if (
      discountCode in discount_list &&
      'ApplyTo' in discount_list[discountCode] &&
      discount_list[discountCode]['ApplyTo'] != null
   )
      //Loop through each productCode the discount would apply to
      for (const productCode of discount_list[discountCode]['ApplyTo'].split(
         ',',
      )) {
         //check if the productCode is in this quote and at least one discount is applied to it
         if (
            productCode in monthlyCosts &&
            'appliedDiscounts' in monthlyCosts[productCode] &&
            monthlyCosts[productCode].appliedDiscounts.length >= 1
         ) {
            let monthlyCost = monthlyCosts[productCode]
            //Add the changeInDollars from the product for the provided discountCode to the running total
            totalDiscount += monthlyCost.appliedDiscounts.find(
               ({ DiscountCode }) => DiscountCode === discountCode,
            )
               ? monthlyCost.appliedDiscounts.find(
                    ({ DiscountCode }) => DiscountCode === discountCode,
                 )['actualEffect']['changeInDollars']
               : 0
         } else {
            continue
         }
      }
   return totalDiscount
}

// Determines if pricing is Base + per EE, or banded. Returns base price for model to be used.
// If the base price of a product is 0, tries banded pricing.
export function getBasePrice(product_list, productCode, EECount) {
   // If ben admin imp product base price is > 0, sets the base price
   if (product_list[productCode]['BasePrice'] > 0)
      return product_list[productCode]['BasePrice']
   // Else looks for a banded price
   else
      return getProductBandBasePrice(
         product_list['productBands'],
         productCode,
         EECount,
      )
}

// Takes in an array of product band objects (productBandsArray), productCode and EECount.
// Returns first base price within the band objects EmpHigh and EmpLow.
// If EmpLow matches and EmpHigh is null, assumes it has none and returns that object.
// Example array of product band objects:
// [{
//       "Id": 1,
//       "Book": 300,
//       "EmpLow": 0,
//       "EmpHigh": 39,
//       "BasePrice": 250,
//       "CreatedAt": "2024-01-11 13:35:50+00:00",
//       "UpdatedAt": "2024-01-11 14:02:23+00:00",
//       "ProductCode": "benAdminImp",
//       "ProductBandID": 10
//   },
//   {
//       "Id": 2,
//       "Book": 300,
//       "EmpLow": 40,
//       "EmpHigh": 75,
//       "BasePrice": 300,
//       "CreatedAt": "2024-01-11 13:42:43+00:00",
//       "UpdatedAt": "2024-01-11 14:01:11+00:00",
//       "ProductCode": "benAdminImp",
//       "ProductBandID": 11
//   }]
function getProductBandBasePrice(productBandsArray, productCode, EECount) {
   // Loops through all productsBands in array
   for (let productBand in productBandsArray) {
      // If ProductCode matches band
      if (productBandsArray[productBand]['ProductCode'] == productCode) {
         // If EE count falls within band range
         if (
            EECount >= productBandsArray[productBand]['EmpLow'] &&
            (EECount <= productBandsArray[productBand]['EmpHigh'] ||
               productBandsArray[productBand]['EmpHigh'] == null)
         ) {
            return productBandsArray[productBand]['BasePrice']
         }
      }
   }
   // Returns 0 if band not found
   return 0
}

// Takes the activationDate, nextInvoiceDate, and number of months prepaid and each MonthlyPrice object and determines the list price
function calculateMonthlyListPrice(
   product_list,
   activationDate,
   nextInvoiceDate,
   discountMonths,
   productCoreMonthlyCosts,
   productPerformanceMonthlyCosts,
   productTimeTrackingMonthlyCosts,
   productTimeAttendanceMonthlyCosts,
   productBenefitsAdministrationMonthlyCosts,
   AdditionalJobCosts,
   AdditionalStorageCosts,
   TestAccountCosts,
   MonthlyTaxCosts,
   TimeClockMonthlyCost,
   MonthlyDynamicCosts,
   differenceQuote = false,
   account_status = null,
) {
   let prorated = isProrated(activationDate, nextInvoiceDate, differenceQuote, account_status)
   // Handle non-numeric discountMonths (e.g., empty string from deprecated discountPrepaymentCode)
   // Default to 1 month if not a valid number
   const validDiscountMonths = typeof discountMonths === 'number' && discountMonths > 0 ? discountMonths : 1
   let monthsAfterProration = validDiscountMonths
   let listPrice = 0
   if (prorated) {
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         productCoreMonthlyCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         productPerformanceMonthlyCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         productTimeTrackingMonthlyCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         productTimeAttendanceMonthlyCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         productBenefitsAdministrationMonthlyCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         AdditionalJobCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         AdditionalStorageCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         TestAccountCosts.listPrice,
      )
      listPrice += getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         MonthlyTaxCosts.listPrice,
      )
      monthsAfterProration = validDiscountMonths > 1 ? validDiscountMonths : 0
   }
   listPrice += productCoreMonthlyCosts.listPrice * monthsAfterProration
   listPrice += productPerformanceMonthlyCosts.listPrice * monthsAfterProration
   listPrice += productTimeTrackingMonthlyCosts.listPrice * monthsAfterProration
   listPrice += productTimeAttendanceMonthlyCosts.listPrice * monthsAfterProration
   listPrice +=
      productBenefitsAdministrationMonthlyCosts.listPrice * monthsAfterProration
   listPrice += AdditionalJobCosts.listPrice * monthsAfterProration
   listPrice += AdditionalStorageCosts.listPrice * monthsAfterProration
   listPrice += TestAccountCosts.listPrice * monthsAfterProration
   listPrice += MonthlyTaxCosts.listPrice * monthsAfterProration
   //Time clocks aren't prorated, so the full amount counts for the first month even if prorated
   listPrice += TimeClockMonthlyCost.listPrice * validDiscountMonths
   for (const productCost of MonthlyDynamicCosts) {
      const product = product_list[productCost.productCode]
      if (product?.IncludeInActivation) {
         listPrice += productCost.listPrice
      }
   }
   return listPrice
}

// Returns an object of details concerning a prorated price. Takes in an activation date and total price.
// Divides total cost by amount of days in billing cycle, then sums the per day cost + remaining days in the month inclusive of activation date.
// The billing cycle is inclusive on the date of last billing, until the night before the next billing date.
// For example, if the billing date for a company is the 20th of every month, each billing cycle runs from the 20th-19th of the next month.
// Proration only applies to existing accounts (Subscriber) OR when differenceQuote is true.
// If account_status is NOT 'Subscriber' (null, other values = existing account) and differenceQuote is false, no proration.
export function getProrationDetails(activationDate, nextInvoiceDate, differenceQuote = false, account_status = null) {
   // Only existing accounts (Subscribers) get proration
   // Any other account_status (null, other strings) = new account = no proration
   const isNewAccount = account_status !== 'Subscriber'

   // Date of activation of products.
   // All date math below uses the UTC getters/setters so that calendar dates parsed from
   // 'YYYY-MM-DD' strings (which Date() always parses as UTC midnight) aren't shifted by a
   // day when the host machine's local timezone is behind UTC.
   let dateOfActivation = new Date(activationDate)
   dateOfActivation.setUTCDate(dateOfActivation.getUTCDate())

   // Date of next invoice. Takes into account if the activation date is after the next invoice date. If so, uses the next months billing date for the calculation.
   let dateOfFutureInvoice = new Date(nextInvoiceDate)
   if (dateOfActivation > dateOfFutureInvoice) {
      dateOfFutureInvoice.setUTCMonth(
         dateOfFutureInvoice.getUTCMonth() +
            Math.max(
               1,
               dateOfActivation.getUTCMonth() -
                  dateOfFutureInvoice.getUTCMonth() +
                  dateOfActivation.getUTCDate() >
                  dateOfFutureInvoice.getUTCDate()
                  ? 1
                  : 0,
            ),
      )
   }
   // Find date of last invoice. If the day of the month of billing date is greater than the last day of the (I.E someone is billed on the 30th normally, but it is February and the onth only has 28 days), then use the last day of the month instead.
   let dateOfLastInvoice = new Date(dateOfFutureInvoice)
   if (
      new Date(
         Date.UTC(
            dateOfFutureInvoice.getUTCFullYear(),
            dateOfFutureInvoice.getUTCMonth() - 1,
            dateOfFutureInvoice.getUTCDate(),
         ),
      ) >
      new Date(
         Date.UTC(
            dateOfFutureInvoice.getUTCFullYear(),
            dateOfFutureInvoice.getUTCMonth() - 1,
         ),
      )
   ) {
      dateOfLastInvoice.setUTCFullYear(dateOfFutureInvoice.getUTCFullYear())
      dateOfLastInvoice.setUTCMonth(dateOfFutureInvoice.getUTCMonth() - 1)
      dateOfLastInvoice.setUTCDate(dateOfFutureInvoice.getUTCDate())
      // If the billing date would have been past the last day of the month (IE into the next month), set the billing date to the last day of the month.
      if (dateOfLastInvoice.getUTCMonth() == dateOfFutureInvoice.getUTCMonth()) {
         dateOfLastInvoice.setUTCMonth(dateOfLastInvoice.getUTCMonth())
         dateOfLastInvoice.setUTCDate(0)
      }
   } else {
      dateOfLastInvoice.setUTCFullYear(dateOfFutureInvoice.getUTCFullYear())
      dateOfLastInvoice.setUTCMonth(dateOfFutureInvoice.getUTCMonth() - 1)
      dateOfLastInvoice.setUTCDate(dateOfFutureInvoice.getUTCDate())
   }

   // Get first day of billing cycle
   let firstDay = dateOfLastInvoice
   // Get last day of the billing cycle
   let lastDay = dateOfFutureInvoice
   // Get total days in month
   let totalDaysInMonth = Math.round(
      (lastDay - firstDay) / (1000 * 60 * 60 * 24),
   )
   // Get days to be paid for, given specified activation date. This is activation date + every remaining day of the month
   let daysToPayAfterActivation =
      (lastDay - dateOfActivation) / (1000 * 60 * 60 * 24)
   
   // Determine if proration should be applied
   // Only apply proration for existing accounts (Subscriber) or difference quotes
   const shouldProrate = (!isNewAccount || differenceQuote) &&
      daysToPayAfterActivation / totalDaysInMonth != 1 &&
      daysToPayAfterActivation / totalDaysInMonth != 0
   
   return {
      prorated: shouldProrate,
      // Get days to be paid for, given specified activation date. This is activation date + every remaining day of the month
      daysToPayAfterActivation: shouldProrate ? daysToPayAfterActivation : 0,
      // Get total days in month
      totalDaysInMonth: totalDaysInMonth,
      // Ratio of days paid of the month
      daysPaidInMonth: `(${shouldProrate ? daysToPayAfterActivation.toFixed(0) : 0}/${totalDaysInMonth.toFixed(0)})`,
   }
}

/**
 * @deprecated Use prepayLength parameter directly instead.
 * This function is kept for backward compatibility only.
 * NEW SYSTEM: prepay_length is stored as an INT on the opportunity table,
 * decoupled from discount codes. Use the prepayLength parameter in calculateQuote().
 * Returns the number of months for a specific prepayment discount code.
 */
export function discountPrepaymentCodeToMonths(discountPrepaymentCode) {
   // Legacy mapping - kept for backward compatibility
   const discountPrepaymentCodeToMonths = {
      prepaid1_MO: 3,
      prepaid2_MO: 4,
      prepaid3_MO: 6,
      prepaid4_MO: 9,
      prepaid1: 12,
      prepaid2: 24,
      prepaid3: 36,
   }
   return discountPrepaymentCode &&
      discountPrepaymentCode in discountPrepaymentCodeToMonths
      ? discountPrepaymentCodeToMonths[discountPrepaymentCode]
      : 1
}

// When a discountReseller is available, this function returns some calculated values, but adds back in the resller discount to the price.
// For example, if a reseller wants to create a quote for 26 employees on the advantage plan, with a non profit discount it would look like this:
// 26 employees on advantage costs $234/month ($9.00 * 26 employees)
// After a 15% nonprofit discount, the total would then come to $198.90/month ($7.65 * 26 employees). This is the price the customer price.
// To get the reseller price, the customer price of $198.90 then has an additional % off. With a 35% reseller discount, the total comes to $129.29/month. This is the reseller price.
function calculateResellerQuoteValues(
   discountReseller,
   productCoreMonthlyCosts,
   productPerformanceMonthlyCosts,
   productBenefitsAdministrationMonthlyCosts,
   productTimeTrackingMonthlyCosts,
   monthlyTotal,
   annualTotal,
   discountMonths,
) {
   if (discountReseller <= 0)
      return {
         corePEPM: 0,
         coreTotalPrice: 0,
         performancePEPM: 0,
         performanceTotalPrice: 0,
         benAdminPEPM: 0,
         benAdminTotalPrice: 0,
         timeTrackingPEPM: 0,
         timeTrackingTotalPrice: 0,
         totalMonthly: 0,
         prepayDiscountAmount: 0,
         nonProfitDiscountAmount: 0,
         annualPrepayTotal: 0,
      }

   let corePEPM = 0,
      performancePEPM = 0,
      benAdminPEPM = 0,
      timeTrackingPEPM = 0,
      totalMonthly = monthlyTotal.totalPrice
   let coreTotalPrice = 0,
      performanceTotalPrice = 0,
      benAdminTotalPrice = 0,
      timeTrackingTotalPrice = 0
   let prepayDiscountAmount = 0,
      nonProfitDiscountAmount = 0,
      annualPrepayTotal = annualTotal.totalPrice

   // PEPM of each product without the reseller discount
   corePEPM = getPropertyOfActualEffectOnProduct(
      productCoreMonthlyCosts,
      'reseller',
      'previousDiscountedPEPM',
   )
   coreTotalPrice = getPropertyOfActualEffectOnProduct(
      productCoreMonthlyCosts,
      'reseller',
      'previousTotalPrice',
   )
   performancePEPM = getPropertyOfActualEffectOnProduct(
      productPerformanceMonthlyCosts,
      'reseller',
      'previousDiscountedPEPM',
   )
   performanceTotalPrice = getPropertyOfActualEffectOnProduct(
      productPerformanceMonthlyCosts,
      'reseller',
      'previousTotalPrice',
   )
   benAdminPEPM = getPropertyOfActualEffectOnProduct(
      productBenefitsAdministrationMonthlyCosts,
      'reseller',
      'previousDiscountedPEPM',
   )
   benAdminTotalPrice = getPropertyOfActualEffectOnProduct(
      productBenefitsAdministrationMonthlyCosts,
      'reseller',
      'previousTotalPrice',
   )
   timeTrackingPEPM = getPropertyOfActualEffectOnProduct(
      productTimeTrackingMonthlyCosts,
      'reseller',
      'previousDiscountedPEPM',
   )
   timeTrackingTotalPrice = getPropertyOfActualEffectOnProduct(
      productTimeTrackingMonthlyCosts,
      'reseller',
      'previousTotalPrice',
   )

   // Total PEPM, removing reseller discounts
   totalMonthly += getPropertyOfActualEffectOnProduct(
      productCoreMonthlyCosts,
      'reseller',
      'changeInDollars',
   )
   totalMonthly += getPropertyOfActualEffectOnProduct(
      productPerformanceMonthlyCosts,
      'reseller',
      'changeInDollars',
   )
   totalMonthly += getPropertyOfActualEffectOnProduct(
      productBenefitsAdministrationMonthlyCosts,
      'reseller',
      'changeInDollars',
   )
   totalMonthly += getPropertyOfActualEffectOnProduct(
      productTimeTrackingMonthlyCosts,
      'reseller',
      'changeInDollars',
   )

   // Total dollars saved from prepayment discounts. Gets all discounts with DiscountFamily = 'Prepayment Discount' and sums their changeInDollars.

   prepayDiscountAmount += getPrepaymentDiscountAmount(productCoreMonthlyCosts) * discountMonths
   prepayDiscountAmount += getPrepaymentDiscountAmount(productPerformanceMonthlyCosts) * discountMonths
   prepayDiscountAmount += getPrepaymentDiscountAmount(productBenefitsAdministrationMonthlyCosts) * discountMonths
   prepayDiscountAmount += getPrepaymentDiscountAmount(productTimeTrackingMonthlyCosts) * discountMonths

   // Total dollars saved from nonProfit discount. Gets all nonProfit discount 'changeInDollars' and sums them up.
   nonProfitDiscountAmount += getPropertyOfActualEffectOnProduct(
      productCoreMonthlyCosts,
      'nonProfit',
      'changeInDollars',
   )
   nonProfitDiscountAmount += getPropertyOfActualEffectOnProduct(
      productPerformanceMonthlyCosts,
      'nonProfit',
      'changeInDollars',
   )
   nonProfitDiscountAmount += getPropertyOfActualEffectOnProduct(
      productBenefitsAdministrationMonthlyCosts,
      'nonProfit',
      'changeInDollars',
   )
   nonProfitDiscountAmount += getPropertyOfActualEffectOnProduct(
      productTimeTrackingMonthlyCosts,
      'nonProfit',
      'changeInDollars',
   )

   // Annual total without reseller discount Based on the Monthly total without Reseller discount, per accounting, so that we use the actual monthly rounded invoice numbers.
   // We have to use toFixed since that's how we're rounding it on the frontend, and this needs to match the monthly amount we show * # of months
   annualPrepayTotal = totalMonthly.toFixed(2) * discountMonths

   // Returns all reseller values
   return {
      corePEPM: corePEPM,
      coreTotalPrice: coreTotalPrice,
      performancePEPM: performancePEPM,
      performanceTotalPrice: performanceTotalPrice,
      benAdminPEPM: benAdminPEPM,
      benAdminTotalPrice: benAdminTotalPrice,
      timeTrackingPEPM: timeTrackingPEPM,
      timeTrackingTotalPrice: timeTrackingTotalPrice,
      totalMonthly: totalMonthly,
      prepayDiscountAmount: prepayDiscountAmount,
      nonProfitDiscountAmount: nonProfitDiscountAmount,
      annualPrepayTotal: annualPrepayTotal,
   }
} //

// Get a property from the 'appliedDiscounts' array of a MonthlyPrice() or OneTimePrice().
export function getPropertyOfActualEffectOnProduct(
   monthlyProduct,
   discountCode,
   actualEffectPropertyName,
) {
   for (let discount in monthlyProduct.appliedDiscounts) {
      if (
         monthlyProduct.appliedDiscounts[discount]['DiscountCode'] ==
         discountCode
      ) {
         return parseFloat(
            monthlyProduct.appliedDiscounts[discount]['actualEffect'][
               actualEffectPropertyName
            ],
         )
      }
   }
   return 0
}

// Get a properties from the 'appliedDiscounts' array of a MonthlyPrice() or OneTimePrice(), if the discount is found in the discountCodes array.
function getPropertiesOfActualEffectOnProduct(
   monthlyProduct,
   discountCodes,
   actualEffectPropertyName,
) {
   let actualEffects = { actualEffectsArray: [] }
   // Loop through codes in discountCodes
   for (let code in discountCodes) {
      // Loop through applied discounts
      for (let discount in monthlyProduct.appliedDiscounts) {
         // If the discount code is found in applied discounts, add it to the 'actualEffect' object as a keyed object, and to the actualEffectsArray property of the actualEffects object
         if (
            monthlyProduct.appliedDiscounts[discount]['DiscountCode'] ==
            discountCodes[code]
         ) {
            actualEffects[discountCodes[code]] = {
               [actualEffectPropertyName]: parseFloat(
                  monthlyProduct.appliedDiscounts[discount]['actualEffect'][
                     actualEffectPropertyName
                  ],
               ),
            }
            // Array of all actual effects
            actualEffects['actualEffectsArray'].push(
               parseFloat(
                  monthlyProduct.appliedDiscounts[discount]['actualEffect'][
                     actualEffectPropertyName
                  ],
               ),
            )
         }
      }
   }
   // Return all found discounts
   return actualEffects
}

function calculateDifference(
   monthlyProduct,
   productCode,
   product_list,
   accountSnowflake,
   differenceQuote,
   currencyObject,
   previousCurrencyObject,
) {
   // If not a difference quote, do not calculate differences
   if (differenceQuote == false) return
   // If no data about the account's mrr numbers are present, do not calculate differences
   if (
      !accountSnowflake ||
      accountSnowflake === 'null' ||
      Object.keys(accountSnowflake).length === 0
   ) {
      console.debug('account snowflake is null')
      return
   }

   // If is NOT a payroll customer AND accountSnowflake has CMI (current month invoice) values, use them.
   // Else, use LMI (last month invoice) values.
   let commissionableMRRFieldsRaw, expectedMRRFieldsRaw
   if (
      (!accountSnowflake.Billed_Payroll__c ||
         accountSnowflake.Billed_Payroll__c == 0) &&
      (accountSnowflake.CMI_Core_MRR__c ||
         accountSnowflake.CMI_Pro_MRR__c ||
         accountSnowflake.CMI_Elite_MRR__c)
   ) {
      commissionableMRRFieldsRaw =
         product_list[productCode]?.MostRecentCommissionableMRRFields
      expectedMRRFieldsRaw =
         product_list[productCode]?.MostRecentExpectedMRRFields
   } else {
      commissionableMRRFieldsRaw =
         product_list[productCode]?.PreviousCommissionableMRRFields
      expectedMRRFieldsRaw =
         product_list[productCode]?.PreviousExpectedMRRFields
   }

   if (
      !(productCode in product_list) ||
      !commissionableMRRFieldsRaw ||
      !commissionableMRRFieldsRaw
   ) {
      monthlyProduct.previousTotalPrice = 0
      monthlyProduct.totalPriceDifference = monthlyProduct.totalPrice
      monthlyProduct.pdfDisplayDifferenceTotal = monthlyProduct.totalPriceDifference
      return
   }
   // Gets final MRR a rep is commissioned on (MRRTotal). This is difference between old and new commissionable MRR
   let commissionableMRRFields = commissionableMRRFieldsRaw.split(',')
   // Sets list price MRR Total
   monthlyProduct.listMRRTotal = monthlyProduct.MRRTotal
   for (let mrrField in commissionableMRRFields) {
      if (commissionableMRRFields[mrrField] in accountSnowflake) {
         // Sets total attributed MRR for the product (list - previous)
         monthlyProduct.MRRTotal -=
            (accountSnowflake[commissionableMRRFields[mrrField]] *
               (currencyObject?.Rate ?? 1)) /
            (previousCurrencyObject?.Rate ?? 1)
         // Sets previous MRR Total for later reference
         monthlyProduct.previousMRRTotal +=
            (accountSnowflake[commissionableMRRFields[mrrField]] *
               (currencyObject?.Rate ?? 1)) /
            (previousCurrencyObject?.Rate ?? 1)
      }
   }
   // PAYROLL PRODUCT DIFFERENCE QUOTE COMMISSION LOGIC:
   // For payroll products (productCode === 'payroll'), if there is existing commissionable MRR,
   // set MRRTotal to $0. This prevents agents from earning commission on headcount increases
   // for existing payroll customers.
   //
   // FUTURE ENHANCEMENT: When implementing pricebook migration detection,
   // check if customer is moving from old to new pricing (e.g., 2024 -> 2026 Payroll).
   // In migration scenarios, DO calculate commission on both:
   //   1. Package price increases due to new pricing
   //   2. Headcount changes
   // Add pricebook version comparison logic here when ready.
   // Example: if (isPayrollPricebookMigration(accountSnowflake, product_list)) { allow commission }
   if (productCode === 'payroll' && monthlyProduct.previousMRRTotal > 0) {
      // Existing payroll customer: No commission on headcount increases
      monthlyProduct.MRRTotal = 0
   } else {
      // Prevents commissionable MRR from being negative and docking the rep.
      monthlyProduct.MRRTotal = Math.max(monthlyProduct.MRRTotal, 0)
   }
   // Gets final MRR differences the customer will see (totalPriceDifference)
   let expectedMRRFields = expectedMRRFieldsRaw.split(',')
   for (let mrrField in expectedMRRFields) {
      if (expectedMRRFields[mrrField] in accountSnowflake)
         monthlyProduct.previousTotalPrice +=
            accountSnowflake[expectedMRRFields[mrrField]]
   }
   monthlyProduct.previousTotalPriceUnconverted =
      monthlyProduct.previousTotalPrice
   //This conversion works because if we divide the previous price by it's currency rate to USD, then multiply by the rate of the new currency to USD, it effectively can convert any two currencies.
   monthlyProduct.previousTotalPrice =
      (monthlyProduct.previousTotalPrice * (currencyObject?.Rate ?? 1)) /
      (previousCurrencyObject?.Rate ?? 1)
   monthlyProduct.totalPriceDifference =
      monthlyProduct.totalPrice - monthlyProduct.previousTotalPrice

   monthlyProduct.pdfDisplayDifferenceTotal = monthlyProduct.pdfDisplayTotal - monthlyProduct.previousTotalPrice
}

// Takes in a product_list with productBands and a product code. Finds the first product band with no EmpHigh
export function getHighestProductBand(product_list, productCode) {
   if (
      'productBands' in product_list &&
      product_list.productBands !== undefined
   ) {
      let bands = product_list.productBands
      // If product code matches and EmpHigh is null (open ended on the top)
      for (let band in bands)
         if (
            productCode == bands[band]['ProductCode'] &&
            isNaN(parseInt(bands[band]['EmpHigh']))
         ) {
            return bands[band]
         }
      return bands[0]
   }
}

// Takes in a product_list with productBands and a product code. Finds the first product band with no EmpHigh
export function getCurrentProductBand(
   product_list,
   productCode,
   productEECount,
) {
   if (
      'productBands' in product_list &&
      product_list.productBands !== undefined
   ) {
      let bands = product_list.productBands
      // If product code matches and has an EmpLow
      for (let band in bands)
         if (
            productCode == bands[band]['ProductCode'] &&
            !isNaN(parseInt(bands[band]['EmpLow']))
         )
            if (
               productEECount >= parseInt(bands[band]['EmpLow']) &&
               (isNaN(parseInt(bands[band]['EmpHigh'])) ||
                  productEECount <= parseInt(bands[band]['EmpHigh']))
            )
               // If productEECount is between EmpLow and EmpHigh
               return bands[band]
      return bands[0]
   }
}

function discountHasFootnoteDisclaimer(discount) {
   const hasValidDisclaimer =
      discount?.Disclaimer &&
      typeof discount.Disclaimer === 'string' &&
      discount.Disclaimer.trim() !== ''
   const showDisclaimerOn = discount?.ShowDisclaimerOn
   const hasValidShowOn =
      showDisclaimerOn &&
      (Array.isArray(showDisclaimerOn)
         ? showDisclaimerOn.length > 0
         : typeof showDisclaimerOn === 'string' && showDisclaimerOn.trim() !== '')
   return hasValidDisclaimer && hasValidShowOn
}

// Takes in an appliedDiscounts array of objects, sums changeInDollars of each discount based on productCode
function summarizeDiscounts(
   appliedDiscounts,
   discount_list,
   selectedProductCosts,
   product_list,
) {
   let tempArr = {}
   for (let discount in appliedDiscounts) {
      // Handle if discount has no effect. Return empty object.
      if (!appliedDiscounts[discount].hasOwnProperty('actualEffect')) continue

      const applied = appliedDiscounts[discount]
      // If no change in dollars, skip unless the discount still has a PDF footnote disclaimer
      // (e.g. Deal Desk % discount stacked after a 100% promo on the same product).
      if (
         applied['actualEffect']['changeInDollars'] == 0 &&
         !discountHasFootnoteDisclaimer(applied)
      )
         continue

      // Key by DiscountCode to keep discounts with the same name but different codes separate
      const discountKey = appliedDiscounts[discount]['DiscountCode']

      // If discount in tempArr add changeInDollars to its value
      if (discountKey in tempArr) {
         // Preserve the existing accumulated total and merge with new discount properties
         const existingTotal = tempArr[discountKey]['total']
         tempArr[discountKey] = {
            ...tempArr[discountKey],
            ...appliedDiscounts[discount],
            DiscountName: substituteDiscountNameVariable(
               appliedDiscounts[discount]['DiscountCode'],
               discount_list,
               selectedProductCosts,
               product_list,
            ),
            total:
               existingTotal +
               appliedDiscounts[discount]['actualEffect']['changeInDollars'],
         }
      }
      // If discount not in tempArr, add it
      else {
         tempArr[discountKey] = {
            ...appliedDiscounts[discount],
            DiscountName: substituteDiscountNameVariable(
               appliedDiscounts[discount]['DiscountCode'],
               discount_list,
               selectedProductCosts,
               product_list,
            ),
            total: appliedDiscounts[discount]['actualEffect'][
               'changeInDollars'
            ],
         }
      }
   }

   // Convert discount object in array of objects
   tempArr = Object.keys(tempArr).map((key) => tempArr[key])
   // Sort objects by priority, smallest being applied first
   tempArr.sort((a, b) => a.priority - b.priority)
   return tempArr
}

// Takes in a MonthlyPrice or OneTimePrice object and a currencyExchangeRate. Sets USD values on the object based on the exchange rate.
export function setUSDValuesOnPriceObject(
   priceObject,
   currentExchangeRate = 1,
   pricebookExchangeRate = 1,
) {
   let excludedFromCurrencyConversion = false
   // Defaults current exchange rate to 1 if not provided
   currentExchangeRate = currentExchangeRate || 1
   // Defaults pricebook exchange rate to 1 if not provided
   pricebookExchangeRate = pricebookExchangeRate || 1

   // If priceObject is not a MonthlyPrice or OneTimePrice object, return
   if (
      !priceObject ||
      (!(priceObject instanceof MonthlyPrice) &&
         !(priceObject instanceof OneTimePrice))
   )
      return
   // If priceObject is a payroll product or an always-USD product, mark as not to be converted with exchange rate
   if (
      priceObject.productCode == 'payrollMonthlyTotal' ||
      priceObject.productCode == 'payroll' ||
      /^payroll\d+$/.test(priceObject.productCode) ||
      isAlwaysUSD(priceObject.productCode)
   )
      excludedFromCurrencyConversion = true

   // If not excluded from conversion set USD values on priceObject based on exchange rates, else just set USD values
   priceObject.listPriceUSD =
      priceObject.listPrice && !excludedFromCurrencyConversion
         ? priceObject?.listPrice / currentExchangeRate
         : priceObject?.listPrice
   priceObject.totalPriceUSD =
      priceObject.totalPrice && !excludedFromCurrencyConversion
         ? priceObject?.totalPrice / currentExchangeRate
         : priceObject?.totalPrice
   priceObject.totalDiscountUSD =
      priceObject.totalDiscount && !excludedFromCurrencyConversion
         ? priceObject?.totalDiscount / currentExchangeRate
         : priceObject?.totalDiscount
   if (priceObject?.MRRTotal)
      priceObject.MRRTotalUSD =
         priceObject.MRRTotal && !excludedFromCurrencyConversion
            ? priceObject?.MRRTotal / pricebookExchangeRate
            : priceObject?.MRRTotal
   return
}

// Run the setUSDValues function on each object in the calculatedValues object. Does so recursively for nested objects in arrays.
function setUSDValuesRecursively(
   value,
   currentExchangeRate,
   pricebookExchangeRate,
   product_list,
) {
   if (Array.isArray(value)) {
      value.forEach((item) =>
         setUSDValuesRecursively(
            item,
            currentExchangeRate,
            pricebookExchangeRate,
            product_list,
         ),
      )
   } else if (typeof value === 'object' && value !== null) {
      // Don't set these here, they are already manually set by calculateDynamicProducts.
      // Exception: timeAttendance is marked Dynamic in the pricebook but is calculated
      // as a first-class time product in calculateQuote (calculateDynamicProducts skips
      // it), so its USD values must be set here instead.
      const isDynamic = product_list[value.productCode]?.Dynamic ?? false
      const isTimeAttendance = value.productCode === 'timeAttendance'
      if (!isDynamic || isTimeAttendance) {
         setUSDValuesOnPriceObject(
            value,
            currentExchangeRate,
            pricebookExchangeRate,
         )
      }
      Object.values(value).forEach((item) =>
         setUSDValuesRecursively(
            item,
            currentExchangeRate,
            pricebookExchangeRate,
            product_list,
         ),
      )
   }
}

export function substituteDiscountNameVariable(
   discountCode,
   discount_list,
   selectedProductCosts,
   product_list,
) {
   let discountName = discount_list[discountCode]?.DiscountName
   if (discountName?.includes('$PRODUCTNAME')) {
      for (const product of discount_list[discountCode]?.ApplyTo?.split(',') ||
         []) {
         let priceObject = selectedProductCosts[product]
         if (
            priceObject &&
            findObjectByKeyValue(
               priceObject?.appliedDiscounts,
               'DiscountCode',
               discountCode,
            )
         ) {
            return discountName.replace(
               '$PRODUCTNAME',
               product_list[product]?.ProductName,
            )
         }
      }
   } else {
      if (discountCode === 'volume') return 'Volume'
      return discountName
   }
}

export function getPriceObjectFromProductCode(calculatedValues, productCode) {
   for (const price in calculatedValues) {
      if (calculatedValues[price].productCode === productCode) {
         return calculatedValues[price]
      }
   }
   return null
}

/**
 * Recalculates quote values while excluding specified discount codes.
 *
 * This function recalculates the quote totals by ignoring the discount codes provided
 * in the discountCodesToIgnore array and then recomputing the pricing using the remaining
 * discounts and product configurations.
 *
 * @param {Set<string>} discountCodesToIgnore - Set of discount codes to ignore.
 * @param {Object<string, Product>} product_list - Object mapping product codes to product details and pricing configuration.
 * @param {Object<string, Discount>} discount_list - Object mapping discount codes to discount configurations.
 * @param {number|string} book_id - Identifier for the pricebook.
 * @param {string} quoteType - Type of quote (e.g., "monthly", "onetime").
 * @param {string} coreProductSelected - Key representing the selected core product.
 * @param {number} productCoreEECount - Employee count for the core product.
 * @param {number} productPerformanceEECount - Employee count for the performance product.
 * @param {number} productTimeTrackingEECount - Employee count for the time tracking product.
 * @param {boolean} hasTimeClocks - Flag indicating if time clocks are being used.
 * @param {Object} productTimeClockDetails - Object containing time clock configuration details.
 * @param {number} productBenefitsAdministrationEECount - Employee count for benefits administration.
 * @param {number} productPayrollEINCount - Count of payroll EINs.
 * @param {number} productPayrollEECount - Employee count for payroll.
 * @param {number} productPayrollEECounts - (Additional) payroll employee counts.
 * @param {number} productPayrollTotalStateCount - Total count for payroll state calculations.
 * @param {Object} productPayrollStateCounts - Object containing payroll state count details.
 * @param {Object} professionalServiceDetails - Object containing professional service details.
 * @param {Object} consultingServiceDetails - Object containing consulting service details.
 * @param {string} impType - HR implementation type.
 * @param {string} benAdminImpType - Benefits administration implementation type.
 * @param {string} payrollImpType - Payroll implementation type.
 * @param {boolean} discountBundle - Flag indicating if the bundle discount should be applied.
 * @param {boolean} discountNonProfit - Flag indicating if the nonprofit discount should be applied.
 * @param {number} prepayLength - Prepayment length in months (1 = month-to-month).
 * @param {boolean} discountMultiEntity - Flag for multi-entity discount.
 * @param {boolean} discountHRMonthlyPromotion - Flag for HR monthly promotion discount.
 * @param {boolean} discountHRDealDeskMonthly - Flag for HR Deal Desk monthly discount.
 * @param {boolean} discountReseller - Flag indicating if the reseller discount should be applied.
 * @param {boolean} discountQuickstartTraining - Flag for the quickstart training discount.
 * @param {boolean} discountImpPromotion - Flag for implementation promotion discount.
 * @param {boolean} discountDealDeskHRImp - Flag for Deal Desk HR implementation discount.
 * @param {boolean} discountDealDeskPayrollImp - Flag for Deal Desk payroll implementation discount.
 * @param {boolean} discountSecondEIN - Flag for the second EIN discount.
 * @param {boolean} discountGroupImp - Flag for group implementation discount.
 * @param {number} discountDealDeskHRImpRate - Numeric rate for Deal Desk HR implementation discount.
 * @param {number} discountDealDeskQuickstartImpRate - Numeric rate for Deal Desk quickstart implementation discount.
 * @param {number} discountDealDeskPayrollImpRate - Numeric rate for Deal Desk payroll implementation discount.
 * @param {number} productAdditionalStorageCount - Count for additional storage products.
 * @param {number} productAdditionalJobsCount - Count for additional job posting products.
 * @param {boolean} productHistoricalDataImport - Flag indicating if historical data import is selected.
 * @param {boolean} productMarketplaceListing - Flag indicating if marketplace listing is selected.
 * @param {boolean} productSponsorship - Flag indicating if sponsorship is selected.
 * @param {number} productPaidServiceAmount - Amount for paid service products.
 * @param {boolean} productTestAccount - Flag indicating if test account is selected.
 * @param {string} activationDate - Activation date (ISO string) for the quote.
 * @param {boolean} differenceQuote - Flag indicating if the quote should be calculated as a difference quote.
 * @param {Object} accountSnowflake - Object containing customer account data including MRR/invoice details.
 * @param {number} coreCountryCount - Number of countries applicable for core product pricing.
 * @param {Object} promotionalDiscounts - Object containing promotional discount values.
 * @param {string} nextInvoiceDate - Next invoice date (ISO string).
 * @param {Object} currencyObject - Object providing currency conversion details (e.g., exchange Rate).
 * @param {number} accountBalance - Account balance value.
 * @param {number} monthlyTax - Monthly tax amount.
 * @param {EINIntegrationSelection[][]} productEINIntegrations - Flag indicating if EIN integrations are selected.
 * @param {Object} allPermissions - Object mapping available user permissions.
 * @param {string} user_role - User role of the person generating the quote.
 * @param {string} account_status - Status of the customer account.
 * @param {Object} pricebookDetails - Object containing pricebook details, including any relevant exchange rates.
 * @param {Object} previousCurrencyObject - Object containing data about the previous currency.
 * @param {Object} productPayrollEINImpTypes - Object containing payroll EIN implementation types.
 * @param {boolean} prepaymentActivationUseDifference - specifies if the due at activation total should use the difference value for the entire term.
 * @param {Map<string, Product>} dynamicProducts - Map of dynamic product details.
 * @param {string} [selectedTimeProduct='timeTracking'] - The selected time product ('timeTracking' or 'timeAttendance').
 * @param {boolean} [isATSOnly=false] - Flag indicating the quote is an ATS-only quote. Forwarded to calculateQuote.
 * @param {boolean} [isRecalculation=false] - Flag indicating this is a recalculation context; the inner calculateQuote call always overrides this to true.
 * @returns {Object} Recalculated quote values with the specified discounts ignored.
 */
export function recalculateWithoutDiscount(
   //Discount Code(s) to ignore in the quote
   discountCodesToIgnore,
   // Required inputs to perform a calculation.
   product_list,
   discount_list,
   book_id,
   quoteType,
   coreProductSelected,
   productCoreEECount,
   productPerformanceEECount,
   productTimeTrackingEECount,
   hasTimeClocks,
   productTimeClockDetails,
   productBenefitsAdministrationEECount,
   productPayrollEINCount,
   productPayrollEECount,
   productPayrollEECounts,
   productPayrollTotalStateCount,
   productPayrollStateCounts,
   professionalServiceDetails,
   consultingServiceDetails,
   impType,
   benAdminImpType,
   payrollImpType,
   discountBundle,
   discountNonProfit,
   prepayLength,
   discountMultiEntity,
   discountHRMonthlyPromotion,
   discountHRDealDeskMonthly,
   discountReseller,
   discountQuickstartTraining,
   discountImpPromotion,
   discountDealDeskHRImp,
   discountDealDeskPayrollImp,
   discountSecondEIN,
   discountGroupImp,
   discountDealDeskHRImpRate,
   discountDealDeskQuickstartImpRate,
   discountDealDeskPayrollImpRate,
   productAdditionalStorageCount,
   productAdditionalJobsCount,
   productHistoricalDataImport,
   productMarketplaceListing,
   productSponsorship,
   productPaidServiceAmount,
   productTestAccount,
   activationDate, // if not supplied, defaults to 1st day of month
   differenceQuote,
   accountSnowflake,
   coreCountryCount,
   promotionalDiscounts,
   nextInvoiceDate,
   currencyObject,
   accountBalance,
   monthlyTax,
   productEINIntegrations,
   allPermissions,
   user_role,
   account_status,
   pricebookDetails,
   previousCurrencyObject,
   productPayrollEINImpTypes,
   prepaymentActivationUseDifference,
   dynamicProducts,
   selectedTimeProduct = 'timeTracking',
   isATSOnly = false,
   isRecalculation = false,
) {
   const filteredPromotionalDiscounts = Object.fromEntries(
      Object.entries(promotionalDiscounts).filter(
         ([key]) => !discountCodesToIgnore.has(key),
      ),
   )

   return calculateQuote(
      product_list,
      discount_list,
      book_id,
      quoteType,
      coreProductSelected,
      productCoreEECount,
      productPerformanceEECount,
      productTimeTrackingEECount,
      hasTimeClocks,
      productTimeClockDetails,
      productBenefitsAdministrationEECount,
      productPayrollEINCount,
      productPayrollEECount,
      productPayrollEECounts,
      productPayrollTotalStateCount,
      productPayrollStateCounts,
      professionalServiceDetails,
      consultingServiceDetails,
      impType,
      benAdminImpType,
      payrollImpType,
      discountCodesToIgnore.has('bundleDiscount') ? false : discountBundle,
      discountCodesToIgnore.has('discountNonProfit')
         ? false
         : discountNonProfit,
      prepayLength, // prepayLength is always passed through - prepayment discounts are now handled as promotional discounts
      discountCodesToIgnore.has('relatedEntity') ? false : discountMultiEntity,
      discountCodesToIgnore.has('hrMonthlyPromotion')
         ? false
         : discountHRMonthlyPromotion,
      discountHRDealDeskMonthly,
      discountCodesToIgnore.has('reseller') ? false : discountReseller,
      discountCodesToIgnore.has('quickstartDiscount')
         ? false
         : discountQuickstartTraining,
      discountCodesToIgnore.has('impPromotion') ? false : discountImpPromotion,
      discountCodesToIgnore.has('hrDealdesk') ? false : discountDealDeskHRImp,
      discountCodesToIgnore.has('payDealdesk')
         ? false
         : discountDealDeskPayrollImp,
      discountCodesToIgnore.has('secondEINDiscount')
         ? false
         : discountSecondEIN,
      discountCodesToIgnore.has('groupChildDiscount')
         ? false
         : discountGroupImp,
      discountDealDeskHRImpRate,
      discountDealDeskQuickstartImpRate,
      discountDealDeskPayrollImpRate,
      productAdditionalStorageCount,
      productAdditionalJobsCount,
      productHistoricalDataImport,
      productMarketplaceListing,
      productSponsorship,
      productPaidServiceAmount,
      productTestAccount,
      activationDate,
      differenceQuote,
      accountSnowflake,
      coreCountryCount,
      filteredPromotionalDiscounts,
      nextInvoiceDate,
      currencyObject,
      accountBalance,
      monthlyTax,
      productEINIntegrations,
      allPermissions,
      user_role,
      account_status,
      pricebookDetails,
      previousCurrencyObject,
      productPayrollEINImpTypes,
      prepaymentActivationUseDifference,
      dynamicProducts,
      selectedTimeProduct,
      isATSOnly,
      true, // isRecalculation
   )
}

export function productCodeToCalculatedValuesPriceObject(
   calculatedValues,
   productCode,
) {
   // First check if the product code exists directly in selectedProductCosts
   if (
      calculatedValues?.selectedProductCosts &&
      calculatedValues?.selectedProductCosts[productCode]
   ) {
      return calculatedValues.selectedProductCosts[productCode]
   }
   for (const price in calculatedValues) {
      if (Array.isArray(calculatedValues[price])) {
         for (const element of calculatedValues[price]) {
            if (element?.productCode === productCode) {
               return element
            }
         }
      } else if (calculatedValues[price]?.productCode === productCode) {
         return calculatedValues[price]
      }
   }
   return null
}

// Helper function to get prepayment discount amounts from a monthly cost object
export function getPrepaymentDiscountAmount(monthlyCosts) {
   if (!monthlyCosts?.appliedDiscounts) return 0
   return monthlyCosts.appliedDiscounts
      .filter((d) => d?.DiscountFamily === 'Prepayment Discount')
      .reduce((sum, d) => sum + (d?.actualEffect?.changeInDollars || 0), 0)
}
